import {
  Box,
  Button,
  CircularProgress,
  Grid,
  TextField,
  Tooltip,
} from "@material-ui/core";
import moment from "moment";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";
import VesselContractForm from "../../../components/Calculator/AddCalculatorFleet/createVesselContractModel";
import ExpenseBreakdownModal from "../../../components/Calculator/ExpenseBreakdownModal";
import CustomAddModal from "../../../components/Common/CustomAddModal";
import CustomShowModal from "../../../components/Common/CustomShowModal";
import {
  initialContractFields,
  initialVesselContractFields,
} from "../Calculator/Contracts";
import CreateCargoContractForm from "../Orders/createCargoContractModel";
import "./../../../styles/custom.scss";
import {
  calculateOperation,
  checkTripEligibility,
  endTrip,
  getOperationById,
} from "./action";
import CargoOperationTable from "./CargoOperationTable";
import FualSupplyTable from "./FualSupplyTable";
import HireExpenseCalculations, {
  parseOffHireDate,
} from "./HireExpenseCalculations";
import NextTripCargoSelectionModal from "./NextTripCargoSelectionModal";
import {
  extractEstimatePortList,
  formatToLT,
  mergeCargoWithEstimate,
} from "./operationUtils";
import TripEndWarningModal, {
  normalizeTripEligibilityResponse,
} from "./TripEndWarningModal";

import _, { debounce } from "lodash";
import { v4 as uuidv4 } from "uuid";
import { VesselVesselContract } from "../..";
import CustomTabs from "../../../common/Tabs";
import CustomTooltip from "../../../common/Tooltip";
import AddOperationsModal from "../../../components/Calculator/AddOperationsModal";
import {
  isSingleOperationTaken,
} from "../../../constants";
import { toastError, toastSuccess, toastWarning } from "../../../utils";
import { getPostedInvoiceByUserId } from "../Accounting/actions";
import {
  portsByChar,
  updateCargoContract,
  updateVesselContract,
} from "../Calculator/actions";
import CargoViewModel from "../Calculator/Contracts/CargoViewModel";
import VesselViewModel from "../Calculator/Contracts/VesselViewModel";
import { addDownloadPdf } from "../Dashboard/actions";
import {
  createElectricLaytimePdf,
  createElectricSofPdf,
} from "../Electric/action";
import AdditionalRevenueModel from "./AdditionalRevenueModel";
import DateChangeModal from "./DateChangeModal";
import ExpensesInvoiceModel from "./ExpensesInvoiceModel";
import FinalInvoiceModel from "./FinalInvoiceModel";
import FuelSupplyReqModel from "./FuelSupplyReqModel";
import InsightsTable from "./InsightsTable";
import IssueInvoiceModel from "./IssueInvoiceModel";
import LaytimeSettlementModel from "./LaytimeSettlementModel";
import LaytimeStatement from "./LaytimeStatement";
import OtherExpensesModal from "./OtherExpensesModal";
import PricingTable from "./PricingTable";
import SeaTable from "./SeaTable";
import StatementOfFactsModel from "./StatementofFactsModel";
import SustainabilityModel from "./sustainabilityModel";
import VesselFuelExpenseTable from "./VesselVesselFuelExpensesTable";

// Itinerary dates arrive in several shapes (picker, DB, cascade); strict multi-format avoids junk years.
const OPS_DATE_FORMATS = [
  "YYYY-MM-DD HH:mm:ss.S",
  "YYYY-MM-DD HH:mm:ss.SSS",
  "YYYY-MM-DD HH:mm:ss",
  "YYYY-MM-DD HH:mm",
  "DD/MM/YYYY HH:mm",
  "DD/MM/YYYY HH:mm:ss",
  "DD-MM-YYYY HH:mm",
  moment.ISO_8601,
];
const parseOpsDate = (value) => moment.utc(value, OPS_DATE_FORMATS, true);

const revenueFields = [
  { label: "Gross Freight", key: "grossFreight" },
  { label: "Charterer's Adcom", key: "charterersAdcom" },
  { label: "Brokerage", key: "brokerage" },
  { label: "Taxes, Others, etc", key: "taxesOthers" },
  { label: "Laytime (Demurrage/Despatch)", key: "laytime" },
  { label: "Additional", key: "additional", link: true },
  { label: "Net Revenue", key: "netFreightRevenue" },
];

const expensesFields = [
  { label: "Gross Hire Amount", key: "grossHireAmount" },
  { label: "Adcom", key: "adCom" },
  { label: "Brokerage Exp", key: "brokerageExp" },
  { label: "Deduction", key: "deduction" },
  { label: "Total Net Hire Expense", key: "totalNetHireExpense" },
  { label: "CVE", key: "cve" },
  { label: "Port Expenses", key: "portExpenses" },
  { label: "Other Exp", key: "otherExp", link: true },
  { label: "HSFO (Main Fuel Expenses)", key: "MainFuelExpHSFO" },
  { label: "LSFO (ECA Fuel Expense)", key: "ECAFuelExpLSFO" },
  { label: "MGO (Light Fuel Expense)", key: "lightFuelExpMGO" },
  { label: "Total Fuel Expenses", key: "totalFuelExpenses" },
  { label: "Fuel Hand-Over Profit Total", key: "fuelHandOverProfitTotal" },
  { label: "Total Trip Exp", key: "totalTripExp" },
];

const financialFields = [...revenueFields, ...expensesFields];

const profitFileds = [
  { label: "Net Profit", key: "netProfit" },
  { label: "TCE Net", key: "tceNet" },
  { label: "Total Trip Duration", key: "totalTripDuration" },
  { label: "Off-hire Duration ", key: "OffhireDuration" },
  { label: "Hire Duration", key: "hireDuration" },
  { label: "Ballast Passage", key: "ballastPassage" },
  { label: "Laden Passage", key: "ladenPassage" },
  { label: "Port Stay Total", key: "portStayTotal" },
  { label: "HSFO (Main Fuel) Qty", key: "hsfoQty" },
  { label: "LSFO Qty", key: "lsfoQty" },
  { label: "MGO Qty", key: "mgoQty" },
];

// Vessel-vessel estimates save no detailed_output; the estimate result lives on the fleet
// (FuelHandOverProfitCount, same numbers the estimate tab's result panel shows).
const extractVesselEstimate = (fleet) => {
  const r = fleet?.FuelHandOverProfitCount || {};
  const fmtAll = (map) =>
    Object.fromEntries(
      Object.entries(map).map(([k, v]) => [
        k,
        // zero / empty / NaN all render as a dash
        !Number.isFinite(Number(v)) || Number(v) === 0
          ? "-"
          : Number(v).toFixed(3),
      ]),
    );
  return {
    // Revenue + Profit rows
    in: fmtAll({
      grossFreight: r.TCOGrossHire,
      charterersAdcom: r.TCOHireCommission,
      additional: r.miscRevenue,
      netFreightRevenue: r.totalRevenue,
      grossHireAmount: r.TCOGrossHire,
      adCom: r.TCOHireCommission,
      fuelHandOverProfitTotal: r.FuelHandOverProfit,
      netProfit: r.profit,
      tceNet: r.profitPerDay,
      totalTripDuration: r.voyDuration,
      ballastPassage: fleet?.ballastSeaDuration,
      ladenPassage: fleet?.ladenSeaDuration,
      portStayTotal: fleet?.portStayTotal,
      hsfoQty: r.hsQty,
      lsfoQty: r.lsQty,
      mgoQty: r.mgoQty,
    }),
    // Expenses rows
    out: fmtAll({
      totalNetHireExpense: r.hireExpenses,
      portExpenses: r.portExpenses,
      otherExp: r.others,
      MainFuelExpHSFO: fleet?.fuelHsExpense,
      ECAFuelExpLSFO: fleet?.fuelLsExpense,
      lightFuelExpMGO: fleet?.fuelMgoExpense,
      totalFuelExpenses: r.fuelExpenses,
      totalTripExp: r.totalExpenses,
    }),
  };
};

const columns = [
  {
    key: "operation",
    label: "Operation",
    width: "40px",
    editable: false,
    bg: "#ffefdc",
  },
  {
    key: "port",
    label: "Port",
    width: "40px",
    editable: false,
    bg: "#fff0ec",
  },
  {
    key: "cargoTx",
    label: "CargoTxInputManual",
    width: "40px",
    editable: true,
  },
  {
    key: "cargoDescription",
    label: "Cargo Description",
    width: "50px",
    editable: true,
  },
  {
    key: "billOfLadingDate",
    label: "Bill of Lading Date",
    width: "40px",
    editable: true,
    type: "datetime",
  },
  { key: "blNumber", label: "B/L Number", width: "30px", editable: true },
  { key: "remarks", label: "Remarks", width: "70px", editable: true },
  {
    key: "statementOfFacts",
    label: "Statement of Facts",
    width: "40px",
    editable: true,
    isBtn: true,
  },
  {
    key: "laytimes",
    label: "Laytimes (Hours)",
    width: "110px",
    editable: true,
    isBtn: true,
  },
  { key: "remarks2", label: "Remarks", width: "40px", editable: true },
  { key: "portExp", label: "Port Exp", width: "30px", editable: true },
  {
    key: "sulfur",
    label: "Port Emission",
    width: "30px",
    type: "selection",
    options: ["Normal", "ECA"],
    editable: true,
  },
  {
    key: "loadline",
    label: "Loadline",
    width: "30px",
    type: "selection",
    options: [
      "S-Summer",
      "TF-Tropical",
      "F-FW",
      "T-Tropical",
      "W-Winter",
      "WNA-Winter",
    ],
    editable: true,
  },
  {
    key: "draftRestriction",
    label: "Draft Restriction",
    width: "25px",
    editable: true,
  },
  {
    key: "waterDensity",
    label: "Water Density",
    width: "25px",
    editable: true,
  },
];

const insightsColumns = [
  {
    key: "operation",
    label: "Operation",
    tooltip: "Operation",
    width: "105px",
    editable: false,
    bg: "#ffefdc",
  },
  {
    key: "port",
    label: "Port",
    tooltip: "Port",
    width: "105px",
    editable: false,
    bg: "#fff0ec",
  },
];

const seaTableColumns = [
  {
    key: "operation",
    label: "Operation",
    tooltip: "Operation",
    width: "105px",
    editable: false,
    bg: "#ffefdc",
  },
  {
    key: "port",
    label: "Port",
    tooltip: "Port",
    width: "105px",
    editable: false,
    bg: "#fff0ec",
  },
  {
    key: "distNormal",
    label: "Dist (HSFO)",
    tooltip: "Distance Normal (HSFO)",
    width: "20px",
    editable: true,
    innerField: true,
  },
  {
    key: "distEca",
    label: "Dist (LSFO)",
    tooltip: "Distance ECA (LSFO)",
    width: "20px",
    editable: true,
    innerField: true,
  },
  {
    key: "observedDistanceNormal",
    label: "Obs Dist (Norm)",
    tooltip: "Observed Distance (Normal)",
    width: "20px",
    editable: true,
  },
  {
    key: "totalTimeTaken",
    label: "Tot Time",
    tooltip: "Total Time Taken",
    width: "20px",
    editable: true,
  },
  {
    key: "weatherDistance",
    label: "GW Dist",
    tooltip: "Good Weather Distance",
    width: "20px",
    editable: true,
  },
  {
    key: "weatherTimeDuration",
    label: "GW Time",
    tooltip: "Good Weather Time Duration (Days)",
    editable: true,
    width: "20px",
  },
  {
    key: "WeatherSpeed",
    label: "GW Spd",
    tooltip: "Good Weather Speed",
    width: "20px",
    editable: true,
  },
  {
    key: "badWeatherDistance",
    label: "BW Dist",
    tooltip: "Bad Weather Distance",
    width: "20px",
    editable: true,
  },
  {
    key: "badWeatherTimeDuration",
    label: "BW Time",
    tooltip: "Bad Weather Time Duration (Days)",
    editable: true,
    width: "20px",
  },
  {
    key: "badWeatherSpeed",
    label: "BW Spd",
    tooltip: "Bad Weather Speed",
    width: "20px",
    editable: true,
  },
  {
    key: "observedDistanceECA",
    label: "Obs Dist (ECA)",
    tooltip: "Observed Distance (ECA)",
    width: "20px",
    editable: true,
  },
  {
    key: "totalTimeTakenECA",
    label: "Tot Time (ECA)",
    tooltip: "Total Time Taken (ECA)",
    width: "20px",
    editable: true,
  },
  {
    key: "goodWeatherDistanceECA",
    label: "GW Dist (ECA)",
    tooltip: "Good Weather Distance (ECA)",
    width: "20px",
    editable: true,
  },
  {
    key: "goodWeatherTimeDurationECA",
    label: "GW Time (ECA)",
    tooltip: "Good Weather Time Duration (Days, ECA)",
    width: "20px",
    editable: true,
  },
  {
    key: "goodWeatherSpeedECA",
    label: "GW Spd (ECA)",
    tooltip: "Good Weather Speed (ECA)",
    width: "20px",
    editable: true,
  },
  {
    key: "badWeatherDistanceECA",
    label: "BW Dist (ECA)",
    tooltip: "Bad Weather Distance (ECA)",
    width: "20px",
    editable: true,
  },
  {
    key: "badWeatherTimeDurationECA",
    label: "BW Time (ECA)",
    tooltip: "Bad Weather Time Duration (Days, ECA)",
    width: "20px",
    editable: true,
  },
  {
    key: "badWeatherSpeedECA",
    label: "BW Spd (ECA)",
    tooltip: "Bad Weather Speed (ECA)",
    width: "20px",
    editable: true,
  },
];

const pricingColumns = [
  {
    key: "operation",
    label: "Operation",
    tooltip: "Operation",
    width: "105px",
    editable: false,
    bg: "#ffefdc",
  },
  {
    key: "port",
    label: "Port",
    tooltip: "Port",
    width: "105px",
    editable: false,
    bg: "#fff0ec",
  },
  {
    key: "name",
    label: "Cargo",
    tooltip: "Cargo",
    width: "30px",
    editable: false,
    outerValue: true,
  },
  {
    key: "stowFactor",
    label: "Stow F.",
    tooltip: "Stow Factor",
    width: "30px",
    editable: true,
  },
  {
    key: "min",
    label: "Min",
    tooltip: "Minimum",
    width: "30px",
    editable: true,
  },
  {
    key: "max",
    label: "Max",
    tooltip: "Maximum",
    width: "30px",
    editable: true,
  },
  {
    key: "ratePerDay",
    label: "Rate MT/d",
    tooltip: "Rate MT/day",
    width: "30px",
    editable: true,
  },
  {
    key: "terms",
    label: "Terms",
    tooltip: "Terms",
    width: "30px",
    editable: true,
  },
  {
    key: "turnTime",
    label: "Turn Time",
    tooltip: "Turn Time (days)",
    width: "30px",
    editable: true,
  },
  {
    key: "demurrage",
    label: "Demurrage",
    tooltip: "Demurrage Per Day",
    width: "30px",
    editable: true,
  },
  {
    key: "despatch",
    label: "Despatch",
    tooltip: "Despatch Per Day",
    width: "30px",
    editable: true,
  },
  {
    key: "freight",
    label: "Freight",
    tooltip: "Freight",
    width: "30px",
    editable: true,
  },
  {
    key: "lumpsumFreight",
    label: "Lumpsum",
    tooltip: "Lumpsum Freight",
    width: "30px",
    editable: true,
  },
  {
    key: "adcom",
    label: "Adcom",
    tooltip: "Adcom",
    width: "30px",
    editable: true,
  },
  {
    key: "other",
    label: "Other(%)",
    tooltip: "Other (%), Tax, ILOW, etc.",
    width: "30px",
    editable: true,
  },
  {
    key: "brokerage",
    label: "Brokerage",
    tooltip: "Brokerage",
    width: "30px",
    editable: true,
  },
  {
    key: "additionalRate",
    label: "Additional Rate",
    tooltip: "Additional Rate",
    width: "30px",
    editable: true,
  },
  {
    key: "lumpsumTypeRevenue",
    label: "LTR",
    tooltip: "Lumpsum Type Revenue",
    width: "30px",
    editable: true,
  },
  {
    key: "grossRevenue",
    label: "Gross Rev",
    tooltip: "Gross Revenue",
    width: "30px",
    editable: true,
  },
  {
    key: "adcomAmt",
    label: "Adcom Amt",
    tooltip: "Adcom Amount",
    width: "30px",
    editable: true,
  },
  {
    key: "otherAmt",
    label: "Other Amt",
    tooltip: "Other (%) Amount",
    width: "30px",
    editable: true,
  },
  {
    key: "brokerageAmt",
    label: "Broker Amt",
    tooltip: "Brokerage Amount",
    width: "30px",
    editable: true,
  },
  {
    key: "netRevenue",
    label: "Net Rev",
    tooltip: "Net Revenue",
    width: "30px",
    editable: true,
  },
  {
    key: "counterparty",
    label: "Ctrpty",
    tooltip: "Counterparty",
    width: "30px",
    editable: true,
  },
  {
    key: "laytime",
    label: "Laytime",
    tooltip: "Laytime Amount (Demurrage/Despatch)",
    width: "30px",
    editable: true,
  },
];

const expenseVesselFields = [
  { label: "Vessel Name", key: "vesselName", type: "text", disabled: true },
  { label: "Vessel ID", key: "vesselId", type: "text", disabled: true },
  { label: "Voyage Number", key: "voyageNumber", type: "text", disabled: true },
  { label: "CP Date (Expense)", key: "cpDate", type: "date", disabled: true },
  {
    label: "Counterparty",
    key: "counterpartyExpense",
    type: "text",
    disabled: true,
  },
  { label: "Ops Type", key: "contractType", type: "text", disabled: true },
];

const shipmentsFileds = [
  { label: "Ops ID", key: "opsId", type: "text", disabled: true },
  { label: "Ops Id (user)", key: "opsIdUser", type: "text", disabled: false },
  {
    label: "Commenced (Z)",
    key: "commencedZ",
    type: "datetime-local",
    disabled: true,
  },
  { label: "Commence Port", key: "commencePort", type: "text", disabled: true },
  {
    label: "First Load Port",
    key: "firstLoadPort",
    type: "text",
    disabled: true,
  },
  {
    label: "Last Disch Port",
    key: "lastDischPort",
    type: "text",
    disabled: true,
  },
  {
    label: "Completed (Z)",
    key: "completedZ",
    type: "datetime-local",
    disabled: true,
  },
  { label: "No. of Days", key: "noOfDays", type: "text", disabled: true },
  { label: "Total Revenue", key: "totalRevenue", type: "text", disabled: true },
  {
    label: "Total Expenditure",
    key: "totalExpenditure",
    type: "text",
    disabled: true,
  },
  { label: "Total Profit", key: "totalProfit", type: "text", disabled: true },
  {
    label: "Daily Returns (TCE)",
    key: "dailyReturnsTCE",
    type: "text",
    disabled: true,
  },
  { label: "Ops-PIC", key: "opsPIC", type: "text", disabled: true },
  {
    label: "Chartering-PIC",
    key: "charteringPIC",
    type: "text",
    disabled: true,
  },
  { label: "Vessel Type", key: "vesselType", type: "text", disabled: true },
  { label: "Status", key: "status", type: "text", disabled: true },
  {
    label: "Closure Date/Time",
    key: "closureDateTime",
    type: "datetime-local",
    disabled: true,
  },
  {
    label: "Cargo Description",
    key: "cargoDescription",
    type: "text",
    disabled: true,
  },
];

const revenueCargoFields = [
  {
    label: "CP Date (Revenue)",
    key: "cpDate",
    type: "date",
    disabled: true,
  },
  {
    label: "Counterparty",
    key: "counterpartyRevenue",
    type: "text",
    disabled: true,
  },
  {
    label: "Contract ID (user)",
    key: "contractIdUser",
    type: "text",
    disabled: true,
  },
  { label: "PO No.", key: "poNo", type: "text", disabled: false },
  {
    label: "Shipper / Vendor",
    key: "shipperVendor",
    type: "text",
    disabled: false,
  },
  { label: "Shipper Code", key: "shipperCode", type: "text", disabled: false },
  { label: "User Ref", key: "userRef", type: "text", disabled: false },
];

const initialsustainabilityData = {
  voyageNumber: "",
  vesselName: "",
  vesselCpDate: "",
  cargoPartyId: "",
  cargoCpDate: "",
  fuelRows: [
    {
      type: "HFO",
      description: "Main Engine",
      selectedFuel: "Heavy Fuel Oil (HFO)",
    },
    {
      type: "ECA",
      description: "Auxiliary Engine",
      selectedFuel: "Diesel/Gas Oil",
    },
    { type: "MGO", description: "Boiler", selectedFuel: "Diesel/Gas Oil" },
  ],
  carbonRows: [],
  preVoyagePort: "",
  postVoyagePort: "",
  agreedEtsCo2: "",
  agreedEtsCost: "",
};

const VesselVesselCalc = (props) => {
  const { loader, setLoader } = props;
  const { id = "" } = useParams();
  const isReadOnly = window.location.pathname.includes("/ops-calc/") || false;
  const [openSustainabilityModel, setOpenSustainabilityModel] = useState(false);
  const [cargoOperationTableData, setCargoOperationTableData] = useState({
    cargoList: [],
  });
  const [sustainabilityData, setSustainabilityData] = useState(
    initialsustainabilityData,
  );
  const [uniqIds, setUniqIds] = useState({});
  const [selectedCargoIndex, setSelectedCargoIndex] = useState(0);
  const [isIssueInvoiceModelOpen, setIsIssueInvoiceModelOpen] = useState(false);
  const [nextTripModalOpen, setNextTripModalOpen] = useState(false);
  const [tripEndWarningOpen, setTripEndWarningOpen] = useState(false);
  const [tripEndWarningReason, setTripEndWarningReason] = useState("");
  const [tripEndWarningMode, setTripEndWarningMode] = useState("blocked");

  const [subModelData, setSubModelData] = useState({
    margins: {
      dwf: 0,
      consumptionMargin: 0,
      speedMargin: 0,
      speedReduction: 0,
    },
    revenue: {
      misc: [
        { id: 1, label: "Misc 1", amount: 0 },
        { id: 2, label: "Misc 2", amount: 0 },
        { id: 3, label: "Misc 3", amount: 0 },
      ],
    },
    revenueOut: { misc: [] },
    expensesIn: {
      cvePerDay: 0,
      insurancePerDay: 0,
      hireSurvey: 0,
      canalExpenses: 0,
      additionalPremium: 0,
      ilohc: 0,
      manoevringExp: 0,
      miscExp: [
        { id: 1, label: "Misc Exp-1", amount: 0 },
        { id: 2, label: "Misc Exp-2", amount: 0 },
      ],
    },
    expensesOut: {
      cvePerDay: 0,
      insurancePerDay: 0,
      hireSurvey: 0,
      canalExpenses: 0,
      additionalPremium: 0,
      ilohc: 0,
      manoevringExp: 0,
      miscExp: [
        { id: 1, label: "Misc Exp-1", amount: 0 },
        { id: 2, label: "Misc Exp-2", amount: 0 },
      ],
    },
    fuelExpenseData: {
      hsfoShortfall: 0,
      lsfoShortfall: 0,
      mgoShortfall: 0,
      hsfoExcess: 0,
      lsfoExcess: 0,
      mgoExcess: 0,
      reserveInternalQtyHsfo: 0,
      reserveExternalQtyHsfo: 0,
      reserveInternalQtyLsfo: 0,
      redeliveryShortagePriceQtyHsfo: 0,
      settlementShortagePriceQtyHsfo: 0,
      withIntoleranceShortageCostHsfo: 0,
      outSidetoleranceShortageCostHsfo: 0,
      redeliveryExcessPriceQtyHsfo: 0,
      settlementExcessPriceQtyHsfo: 0,
      withIntoleranceExcessRevenueHsfo: 0,
      outSidetoleranceExcessRevenueHsfo: 0,
      redeliveryShortagePriceQtyLsfo: 0,
      settlementShortagePriceQtyLsfo: 0,
      withIntoleranceShortageCostLsfo: 0,
      outSidetoleranceShortageCostLsfo: 0,
      redeliveryExcessPriceQtyLsfo: 0,
      settlementExcessPriceQtyLsfo: 0,
      withIntoleranceExcessRevenueLsfo: 0,
      outSidetoleranceExcessRevenueLsfo: 0,
      costExcessLsfo: 0,
      reserveInternalQtyMgo: 0,
      reserveInternalPriceHsfo: 0,
      redeliveryShortagePriceQtyMgo: 0,
      settlementShortagePriceQtyMgo: 0,
      withIntoleranceShortageCostMgo: 0,
      outSidetoleranceShortageCostMgo: 0,
      redeliveryExcessPriceQtyMgo: 0,
      settlementExcessPriceQtyMgo: 0,
      withIntoleranceExcessRevenueMgo: 0,
      outSidetoleranceExcessRevenueMgo: 0,
      costExcessMgo: 0,
      reserveExternalQtyMgo: 0,
      reserveInternalCostHsfo: 0,
      reserveInternalCostLsfo: 0,
      reserveInternalCostMgo: 0,
      tripSupplyQtyHsfo: 0,
      tripSupplyCostHsfo: 0,
      averagePricePMTHsfo: 0,
      voyageConsumptionQtyHsfo: 0,
      appliedQuantityHsfo: 0,
      selectedAppliedCostHsfo: 0,
      appliedCostLIFOHsfo: 0,
      appliedCostFIFOHsfo: 0,
      appliedCostAverageHsfo: 0,
      expenditureforShortageHsfo: 0,
      voyagePricePMTHsfo: 0,
      excessCostHsfo: 0,
      revenueExcessHsfo: 0,
      shortageQtyHsfo: 0,
      excessQtyHsfo: 0,
      voyageFuelExpenditureHsfo: 0,
      fuelSettlementProfitHsfo: 0,
      qtyBalanceOnboardHsfo: 0,
      deliveryPricesHsfo: 0,
      tripSupplyQtyLsfo: 0,
      tripSupplyCostLsfo: 0,
      averagePricePMTLsfo: 0,
      voyageConsumptionQtyLsfo: 0,
      appliedQuantityLsfo: 0,
      selectedAppliedCostLsfo: 0,
      appliedCostLIFOLsfo: 0,
      appliedCostFIFOLsfo: 0,
      appliedCostAverageLsfo: 0,
      expenditureforShortageLsfo: 0,
      voyagePricePMTLsfo: 0,
      excessCostLsfo: 0,
      revenueExcessLsfo: 0,
      shortageQtyLsfo: 0,
      excessQtyLsfo: 0,
      voyageFuelExpenditureLsfo: 0,
      fuelSettlementProfitLsfo: 0,
      qtyBalanceOnboardLsfo: 0,
      deliveryPricesLsfo: 0,
      tripSupplyQtyMgo: 0,
      tripSupplyCostMgo: 0,
      averagePricePMTMgo: 0,
      voyageConsumptionQtyMgo: 0,
      appliedQuantityMgo: 0,
      selectedAppliedCostMgo: 0,
      appliedCostLIFOMgo: 0,
      appliedCostFIFOMgo: 0,
      appliedCostAverageMgo: 0,
      expenditureforShortageMgo: 0,
      voyagePricePMTMgo: 0,
      excessCostMgo: 0,
      revenueExcessMgo: 0,
      shortageQtyMgo: 0,
      excessQtyMgo: 0,
      voyageFuelExpenditureMgo: 0,
      fuelSettlementProfitMgo: 0,
      qtyBalanceOnboardMgo: 0,
      deliveryPricesMgo: 0,
      fuelMethodHsfo: "LIFO",
      fuelMethodLsfo: "LIFO",
      fuelMethodMgo: "LIFO",
      hsfoDeliveryPrice: 0,
      lsfoDeliveryPrice: 0,
      mgoDeliveryPrice: 0,
    },
  });
  const isSaved = useRef(false);
  const fuelExpenseDataRef = useRef(null);
  const [warning, setWarning] = useState({});
  const isChanged = useRef(false);
  const [values, setValues] = useState({
    Revenue: {},
    RevenueOut: {},
    Revised: {},
    RevisedOut: {},
    Actual: {},
    ActualOut: {},
    Settled: [],
    SettledOut: [],
  });
  const unchangedValues = useRef(null);
  const [selectedSettledAmt, setSelectedSettledAmt] = useState(null);

  const [offHireDeductionsIn, setOffHireDeductionsIn] = useState([
    {
      id: 1,
      from: "21-04-2025 03:56",
      fromTz: 5.5,
      to: "21-04-2025 18:32",
      toTz: 5,
      duration: 0,
      offHireDeducation: 75,
      netDaysDedn: 0,
      remarks: "",
    },
  ]);

  const [fuelDeductionsIn, setFuelDeductionsIn] = useState([
    {
      id: 1,
      parentId: 1,
      from: "21-04-2025 03:56",
      fromTz: 5.5,
      to: "21-04-2025 18:32",
      toTz: 5,
      fuelGrade: "Main",
      quantity: 0,
      price: 0,
      fuelDeducation: 0,
      amount: 0,
      remarks: "",
    },
  ]);

  const [miscDeductionsIn, setMiscDeductionsIn] = useState([
    { id: 1, remarks: "", amount: 0 },
  ]);
  const [offHireDeductionsOut, setOffHireDeductionsOut] = useState([
    {
      id: 1,
      from: "21-04-2025 03:56",
      fromTz: 5.5,
      to: "21-04-2025 18:32",
      toTz: 5,
      duration: 0,
      offHireDeducation: 75,
      netDaysDedn: 0,
      remarks: "",
    },
  ]);

  const [fuelDeductionsOut, setFuelDeductionsOut] = useState([
    {
      id: 1,
      parentId: 1,
      from: "21-04-2025 03:56",
      fromTz: 5.5,
      to: "21-04-2025 18:32",
      toTz: 5,
      fuelGrade: "Main",
      quantity: 0,
      price: 0,
      fuelDeducation: 0,
      amount: 0,
      remarks: "",
    },
  ]);

  const [miscDeductionsOut, setMiscDeductionsOut] = useState([
    { id: 1, remarks: "", amount: 0 },
  ]);

  const mockData = {
    vesselName: "",
    vesselId: "",
    voyageNumber: "",
    cpDate: "",
    counterpartyExpense: "",
    opsType: "",
    opsId: "",
    opsIdUser: "",
    commencedZ: "",
    commencePort: "",
    firstLoadPort: "",
    lastDischPort: "",
    completedZ: "",
    noOfDays: "",
    totalRevenue: "",
    totalExpenditure: "",
    totalProfit: "",
    dailyReturnsTCE: "",
    opsPIC: "",
    charteringPIC: "",
    vesselType: "",
    status: "",
    closureDateTime: "",
    cargoDescription: "",
    cpDateRevenue: "",
    counterpartyRevenue: "",
    cargoContractId: "",
    shipmentNumber: "",
    contractIdUser: "",
    shipmentNumberUser: "",
    poNo: "",
    shipperVendor: "",
    shipperCode: "",
    userRef: [{ id: 1, label: "User Ref-1", value: "" }],
  };
  const [vesselCargoDetailsTableData, setVesselCargoDetailsTableData] =
    useState(mockData);

  const [cargoContractDetails, setCargoContractDetails] = useState(
    initialContractFields,
  );
  const [vesselContractDetails, setVesselContractDetails] = useState(
    initialVesselContractFields,
  );
  const [selectedOperationIndex, setSelectedOperationIndex] = useState(0);
  const fuelSettlementList = [
    { label: "Redelivery", value: "redelivery" },
    { label: "Relay", value: "relay" },
  ];
  const operationStatusList = [
    { label: "Planned", value: "planned" },
    { label: "In Progress", value: "in-progress" },
    { label: "Completed", value: "completed" },
    { label: "Closed", value: "closed" },
  ];
  const [pendingDateChange, setPendingDateChange] = useState(null);
  const addQueryModalRef = useRef();
  const vesselCargoContainerRef = useRef(null);

  const [cargoInvoices, setCargoInvoices] = useState([]);

  const [vesselAttachments, setVesselAttachments] = useState([]);
  const [prevAttachments, setPrevAttachments] = useState({
    attachments: [],
    textContract: undefined,
  });
  const [calculatedExpenses, setCalculatedExpenses] = useState({});
  const [vesselTextContract, setVesselTextContract] = useState();
  const [cargoAttachments, setCargoAttachments] = useState();
  const [cargoTextContract, setCargoTextContract] = useState();
  const [splitHireModal, setSplitHireModal] = useState();
  const [activeTab, setActiveTab] = useState(0);
  const [activeCargoModelTab, setActiveCargoModelTab] = useState(0);
  const [fuelSettlementMode, setFuelSettlementMode] = useState("redelivery");
  const [operationStatus, setOperationStatus] = useState("planned");
  const [rawContractData, setRawContractData] = useState({});

  const [expenseInvoicesIn, setExpenseInvoicesIn] = useState([]);
  const [expenseInvoicesOut, setExpenseInvoicesOut] = useState([]);
  const [usedCounterpartiesIn, setUsedCounterpartiesIn] = useState({});
  const [usedCounterpartiesOut, setUsedCounterpartiesOut] = useState({});

  const [editVesselContractModelOpen, setEditVesselContractModelOpen] =
    useState({
      isOpen: false,
      key: "vesselContractIn",
    });
  const [openFuelExpenseModel, setOpenFuelExpenseModel] = useState(false);
  const [openAddOperation, setOpenAddOperation] = useState({
    isOpen: false,
    index: null,
  });
  const [openExpensesInvoiceModel, setOpenExpensesInvoiceModel] = useState({
    isOpen: false,
    vessel: "In",
  });
  const [freightMode, setFreightMode] = useState("Single Freight");
  const [openAdditionalRevenueModel, setOpenAdditionalRevenueModel] =
    useState(null);
  const [isLaytimesModelOpen, setIsLaytimesModelOpen] = useState(false);
  const [isLaytimeSettlementsModelOpen, setIsLaytimeSettlementsModelOpen] =
    useState(false);
  const [isFinalInvoiceModelOpen, setIsFinalInvoiceModelOpen] = useState(false);
  const [isFuelSupplyRequestModelOpen, setIsFuelSupplyRequestModelOpen] =
    useState(false);
  const [isStatementOfFactsModelOpen, setIsStatementOfFactsModelOpen] =
    useState(false);
  const [openOtherExpModel, setOpenOtherExpModel] = useState({
    isOpen: false,
    key: "",
  });
  const [expenseModalField, setExpenseModalField] = useState(null);
  const [nextOperationId, setNextOperationId] = useState(null);
  const [optimumSpeedModelOpen, setOptimumSpeedModelOpen] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [editCargoContractModelOpen, setEditCargoContractModelOpen] =
    useState(false);
  const [showDateChangeModal, setShowDateChangeModal] = useState(false);
  const transitionTimeoutRef = useRef(null);
  const [portOptions, setPortOptions] = useState([]);
  const [portLoading, setPortLoading] = useState(false);
  const [initialAddOperation, setInitialAddOperation] = useState({
    operation: "Bunkering",
    port: "",
    portExp: 0,
    extraDays: 0,
    lat: 0,
    lng: 0,
  });
  const reCalculate = useRef(false);
  const [selectedPorts, setSelectedPorts] = useState([]);
  const [vesselActiveTab, setVesselActiveTab] = React.useState(0);
  useEffect(() => {
    setSustainabilityData((prev) => ({
      ...prev,
      vesselName: vesselContractDetails?.vesselDetails?.vesselName || "",
      vesselCpDate: vesselContractDetails?.cpDate || "",
      cargoCpDate: cargoContractDetails?.cpDate || "",
      voyageNumber: uniqIds?.voyageNumber || "",
    }));
  }, [uniqIds, vesselContractDetails, cargoContractDetails]);
  const [selectedQuery, setSelectedQuery] = useState({});
  const [selectedPortsCommence, setSelectedPortsCommence] = useState([]);
  const [finalState, setFinalState] = useState({
    groups: [
      { id: "group-1", name: "Port A", operations: [], totalStats: {} },
      { id: "group-2", name: "Port B", operations: [], totalStats: {} },
    ],
    ungroupedOperations: [],
    allOperations: [],
    globalStats: {},
  });
  const [IsLoading, setIsLoading] = useState(false);
  const [pdfData, setPdfData] = useState([]);
  const [mainTab, setMainTab] = useState(2);
  const [isRevised, setIsRevised] = useState(false);
  const vesselRef = useRef(null);

  const handleUpdateVesselDetails = () => {
    vesselRef.current?.submit();
  };

  const totalOffHireDurationIn = useMemo(
    () =>
      offHireDeductionsIn?.reduce(
        (sum, item) => sum + (Number(item.netDaysDedn) || 0),
        0,
      ),
    [offHireDeductionsIn],
  );
  const totalFuelDeductionsIn = useMemo(
    () =>
      fuelDeductionsIn?.reduce(
        (sum, item) => sum + (Number(item.amount) || 0),
        0,
      ),
    [fuelDeductionsIn],
  );
  const hirePerDayDeductionIn = useMemo(
    () =>
      totalOffHireDurationIn *
      Number(vesselContractDetails?.vesselDetails?.hirePerDay) || 0,
    [totalOffHireDurationIn, vesselContractDetails],
  );
  const cveDeductionIn = useMemo(
    () =>
      totalOffHireDurationIn * Number(subModelData?.expensesIn?.cvePerDay) || 0,
    [totalOffHireDurationIn, subModelData],
  );

  const totalMiscDeductionsIn = useMemo(
    () =>
      miscDeductionsIn?.reduce(
        (sum, item) => sum + (Number(item.amount) || 0),
        0,
      ),
    [miscDeductionsIn],
  );
  const totalDeductionsIn = useMemo(
    () =>
      totalFuelDeductionsIn +
      totalMiscDeductionsIn +
      hirePerDayDeductionIn +
      cveDeductionIn,
    [
      offHireDeductionsIn,
      totalFuelDeductionsIn,
      totalMiscDeductionsIn,
      hirePerDayDeductionIn,
      cveDeductionIn,
    ],
  );

  const totalOffHireDurationOut = useMemo(
    () =>
      offHireDeductionsOut?.reduce(
        (sum, item) => sum + (Number(item.netDaysDedn) || 0),
        0,
      ),
    [offHireDeductionsOut],
  );
  const totalFuelDeductionsOut = useMemo(
    () =>
      fuelDeductionsOut?.reduce(
        (sum, item) => sum + (Number(item.amount) || 0),
        0,
      ),
    [fuelDeductionsOut],
  );
  const totalMiscDeductionsOut = useMemo(
    () =>
      miscDeductionsOut?.reduce(
        (sum, item) => sum + (Number(item.amount) || 0),
        0,
      ),
    [miscDeductionsOut],
  );

  const hirePerDayDeductionOut = useMemo(
    () =>
      totalOffHireDurationOut *
      Number(
        vesselContractDetails?.vesselOutContract?.vesselDetails?.hirePerDay,
      ) || 0,
    [totalOffHireDurationOut, vesselContractDetails],
  );
  const cveDeductionOut = useMemo(
    () =>
      totalOffHireDurationOut * Number(subModelData?.expensesOut?.cvePerDay) ||
      0,
    [totalOffHireDurationOut, subModelData],
  );

  const totalDeductionsOut = useMemo(
    () =>
      totalFuelDeductionsOut +
      totalMiscDeductionsOut +
      cveDeductionOut +
      hirePerDayDeductionOut,
    [
      offHireDeductionsOut,
      totalFuelDeductionsOut,
      totalMiscDeductionsOut,
      cveDeductionOut,
      hirePerDayDeductionOut,
    ],
  );

  const totalMiscExpenses =
    Number(subModelData?.expensesIn?.hireSurvey || 0) +
    Number(subModelData?.expensesIn?.canalExpenses || 0) +
    Number(subModelData?.expensesIn?.additionalPremium || 0) +
    Number(subModelData?.expensesIn?.ilohc || 0) +
    Number(subModelData?.expensesIn?.manoevringExp || 0) +
    (subModelData?.expensesIn?.miscExp?.reduce(
      (sum, item) => sum + Number(item.amount || 0),
      0,
    ) || 0);

  const totalMiscExpensesOut =
    Number(subModelData?.expensesOut?.hireSurvey || 0) +
    Number(subModelData?.expensesOut?.canalExpenses || 0) +
    Number(subModelData?.expensesOut?.additionalPremium || 0) +
    Number(subModelData?.expensesOut?.ilohc || 0) +
    Number(subModelData?.expensesOut?.manoevringExp || 0) +
    (subModelData?.expensesOut?.miscExp?.reduce(
      (sum, item) => sum + Number(item.amount || 0),
      0,
    ) || 0);

  const additionalRevenueIn = useMemo(
    () =>
      (subModelData?.revenue?.misc || []).reduce(
        (sum, item) => sum + Number(item.amount || 0),
        0,
      ),
    [subModelData?.revenue?.misc],
  );
  const additionalRevenueOut = useMemo(
    () =>
      (subModelData?.revenueOut?.misc || []).reduce(
        (sum, item) => sum + Number(item.amount || 0),
        0,
      ),
    [subModelData?.revenueOut?.misc],
  );
  const handleMainTabChange = (event, newValue) => {
    setMainTab(newValue);
  };

  const setIntialCargoInvoiceDetails = (cargoContract) => {
    const cargoData = cargoContract?.cargoDetails || cargoContract;
    const cargoList = cargoData?.cargoList || [];

    const initialCargoInvoiceDetails = cargoList?.reduce((acc, item) => {
      if (item?.operation?.toLowerCase() === "loading") {
        const defaultInvoiceDetail = {
          _id: uuidv4(),
          cargoContractId: cargoContract?.cargoContractId,
          label: cargoData?.name || "",
          section: "",
          cargoName: cargoData?.name || "",
          cargoQty: 0,
          unit: "",
          rate: 0,
          amount: 0,
          payReceive: "",
          counterParty: "",
          description: "",
          hsn: "",
          isDefault: false,
          isCargo: true,
        };

        let invoiceDetails = [];

        if (item?.invoiceDetails?.length > 0) {
          invoiceDetails = item.invoiceDetails
            .filter((detail) => Number(detail.amount) > 0)
            .map((detail) => ({
              ...defaultInvoiceDetail,
              ...detail,
              _id: detail._id || uuidv4(),
              cargoId: item._id,
              isCargo: true,
              amount: Number(detail.amount) || defaultInvoiceDetail.amount,
              label: detail.label || defaultInvoiceDetail.label,
              cargoContractId: cargoContract?.cargoContractId,
            }));
        }

        if (invoiceDetails.length > 0) {
          acc.push({
            cargoId: item._id,
            port: item.port,
            operation: item.operation,
            cargoName: cargoData?.name || "",
            totalAmt: invoiceDetails?.reduce(
              (sum, d) => sum + (Number(d.amount) || 0),
              0,
            ),
            invoiceDetails,
          });
        }
      }

      return acc;
    }, []);

    if (initialCargoInvoiceDetails.length > 0) {
      setCargoInvoices(initialCargoInvoiceDetails);
    }
  };

  const recalculateOffHireRows = (rows = []) =>
    rows.map((item) => {
      const from = parseOffHireDate(item.from);
      const to = parseOffHireDate(item.to);
      const percentage = Math.min(
        100,
        Math.max(0, Number(item.offHireDeducation) || 0),
      );
      const duration =
        from.isValid() && to.isValid()
          ? Math.max(
            0,
            to
              .clone()
              .subtract(Number(item.toTz) || 0, "hours")
              .diff(
                from.clone().subtract(Number(item.fromTz) || 0, "hours"),
                "days",
                true,
              ),
          )
          : 0;
      return {
        ...item,
        from: from.isValid() ? from.format("DD-MM-YYYY HH:mm") : "",
        to: to.isValid() ? to.format("DD-MM-YYYY HH:mm") : "",
        offHireDeducation: percentage,
        duration,
        netDaysDedn: Number(((duration * percentage) / 100).toFixed(6)),
      };
    });

  const fetchOperation = async () => {
    try {
      const { data } = await getOperationById({ id });

      setIsRevised(data?.isRevised || false);
      setNextOperationId(
        data?.nextOperationId ||
        data?.operationCalculation?.nextOperationId ||
        null,
      );

      const {
        actualCalculation = {},
        calculatedExpenses = {},
        fuelSettlementMode = "redelivery",
        sustainabilityData = {},
        operationStatus = "planned",
        offHire = {},
        subModelCalculation = {},
        vesselCargoDetailsTableData = {},
      } = data?.operationCalculation || {};

      const actualVesselContract =
        data?.vesselContractDetails?.actualVesselContract || {};
      const pad = (num, size) => {
        let s = num.toString();
        while (s.length < size) s = "0" + s;
        return s;
      };

      const transformIds = (id, type, ctx = {}) => {
        switch (type) {
          case "opsId":
            return `Ops-${pad(id, 6)}`;
          case "cargoPrefixId":
            return `C${pad(id, 6)}`;
          case "vesselPrefixId":
            return `V${pad(id, 6)}`;
          case "shipmentId":
            return `${ctx.cargoId}S${pad(id, 3)}`;
          case "voyageTripId":
            return `${ctx.vesselId}T${pad(id, 3)}`;
          default:
            return id?.toString?.() || "";
        }
      };

      const {
        cargoPrefixId = 1,
        opsId = 1,
        shipmentId = 1,
        voyageTripId = 1,
        vesselPrefixId = 1,
      } = data || {};

      const transformedIds = {};
      transformedIds.cargoId = transformIds(cargoPrefixId, "cargoPrefixId");
      transformedIds.opsId = transformIds(opsId, "opsId");
      transformedIds.vesselId = transformIds(vesselPrefixId, "vesselPrefixId");
      transformedIds.shipmentId = transformIds(
        shipmentId,
        "shipmentId",
        transformedIds,
      );
      transformedIds.voyageNumber = transformIds(
        voyageTripId,
        "voyageTripId",
        transformedIds,
      );

      setUniqIds(transformedIds);

      /* -------------------- BASIC STATE -------------------- */

      setVesselCargoDetailsTableData(
        (prev) => ({ ...prev, ...vesselCargoDetailsTableData }) || {},
      );

      setVesselContractDetails((prev) => {
        const savedContract = data?.vesselContractDetails || {};
        const actualContract = savedContract.actualVesselContract || {};
        const savedOut = savedContract.vesselOutContract || {};
        const actualOut = actualContract.vesselOutContract || {};
        const vesselInDetails = {
          ...(savedContract.vesselDetails || {}),
          ...(actualContract.vesselDetails || {}),
        };
        // Vessel-Out is a copy of Vessel-In; older contracts were saved with an empty
        // vesselOutContract, so fill blanks from Vessel-In (+ its vesselOut overrides).
        const { vesselOut: inVesselOut, clientQuery: _cq, ...inBase } =
          vesselInDetails;
        const filled = (obj = {}) =>
          Object.fromEntries(
            Object.entries(obj).filter(([, v]) => v !== undefined && v !== ""),
          );
        return {
          ...prev,
          ...savedContract,
          ...actualContract,
          vesselDetails: vesselInDetails,
          vesselOutContract: {
            ...savedOut,
            ...actualOut,
            vesselDetails: {
              ...inBase,
              ...filled(inVesselOut),
              ...filled(savedOut.vesselDetails),
              ...filled(actualOut.vesselDetails),
            },
          },
          vesselContractId: data?.vesselContractId,
        };
      });

      const mergeById = (prev = [], next = []) => {
        const map = new Map(prev.map((item) => [item.id, item]));
        next.forEach((item) => {
          map.set(item.id, { ...map.get(item.id), ...item });
        });
        return Array.from(map.values());
      };

      setOffHireDeductionsIn(
        recalculateOffHireRows(offHire?.offHireDeductionsIn || []),
      );

      setFuelDeductionsIn((prev) => mergeById(prev, offHire?.fuelDeductionsIn));

      setMiscDeductionsIn((prev) => mergeById(prev, offHire?.miscDeductionsIn));

      setOffHireDeductionsOut(
        recalculateOffHireRows(offHire?.offHireDeductionsOut || []),
      );

      setFuelDeductionsOut((prev) =>
        mergeById(prev, offHire?.fuelDeductionsOut),
      );

      setMiscDeductionsOut((prev) =>
        mergeById(prev, offHire?.miscDeductionsOut),
      );

      const savedVessel = data?.vesselContractDetails || {};
      const actualVessel = savedVessel.actualVesselContract || {};
      const estimateExpensesIn = {
        ...(savedVessel.vesselDetails?.clientQuery || {}),
        ...(actualVessel.vesselDetails?.clientQuery || {}),
      };
      const estimateExpensesOut = {
        ...(savedVessel.vesselOutContract?.vesselDetails?.clientQuery || {}),
        ...(actualVessel.vesselOutContract?.vesselDetails?.clientQuery || {}),
      };
      const expenseFieldDefs = [
        { key: "cvePerDay", fbKey: "cve" },
        { key: "insurancePerDay", fbKey: "insurance" },
        { key: "hireSurvey", itemsKey: "hireSurveyItems" },
        { key: "canalExpenses", itemsKey: "canalExpensesItems" },
        { key: "additionalPremium", itemsKey: "additionalPremiumItems" },
        { key: "ilohc", itemsKey: "ilohcItems" },
        { key: "manoevringExp", itemsKey: "manoevringExpItems" },
      ];
      const mergeExpenseData = (saved, fallback) => {
        const merged = { ...(saved || {}) };
        const fb = fallback || {};
        expenseFieldDefs.forEach(({ key, fbKey, itemsKey }) => {
          const fallbackValue = fb[key] !== undefined ? fb[key] : fb[fbKey];
          if (!merged[key] && fallbackValue !== undefined)
            merged[key] = fallbackValue;
          if (
            itemsKey &&
            (!merged[itemsKey] || merged[itemsKey].length === 0) &&
            fb[itemsKey]
          )
            merged[itemsKey] = fb[itemsKey];
        });
        if (
          fb.dailyExpenses &&
          (!merged.dailyExpenses || merged.dailyExpenses.length === 0)
        ) {
          merged.dailyExpenses = fb.dailyExpenses;
        }
        if (!merged.cvePerDay && merged.dailyExpenses) {
          merged.cvePerDay = merged.dailyExpenses
            .filter((i) => i.label?.toLowerCase()?.includes("cve"))
            .reduce((sum, i) => sum + (Number(i.value) || 0), 0);
        }
        if (!merged.insurancePerDay && merged.dailyExpenses) {
          merged.insurancePerDay = merged.dailyExpenses
            .filter((i) => i.label?.toLowerCase()?.includes("insurance"))
            .reduce((sum, i) => sum + (Number(i.value) || 0), 0);
        }
        if (merged.dailyExpenses && merged.dailyExpenses.length > 0) {
          merged.expensesPerDay = merged.dailyExpenses.reduce(
            (sum, i) => sum + (Number(i.value) || 0),
            0,
          );
        } else {
          merged.expensesPerDay =
            (Number(merged.cvePerDay) || 0) +
            (Number(merged.insurancePerDay) || 0);
          merged.dailyExpenses = [
            { label: "CVE per Day", value: Number(merged.cvePerDay) || 0 },
            {
              label: "Insurance per Day",
              value: Number(merged.insurancePerDay) || 0,
            },
          ];
        }
        if (
          !merged.miscExp ||
          !Array.isArray(merged.miscExp) ||
          merged.miscExp.length === 0
        ) {
          merged.miscExp = fb.miscExpItems ||
            (Array.isArray(fb.miscExp) ? fb.miscExp : null) || [
              { id: 1, label: "Misc Exp-1", amount: 0 },
              { id: 2, label: "Misc Exp-2", amount: 0 },
            ];
        }
        return merged;
      };
      const mergedExpensesIn = mergeExpenseData(
        subModelCalculation?.expensesIn,
        estimateExpensesIn,
      );
      const mergedExpensesOut = mergeExpenseData(
        subModelCalculation?.expensesOut,
        estimateExpensesOut,
      );

      setSubModelData((prev) => ({
        ...prev,
        ...subModelCalculation,
        expensesIn: { ...prev.expensesIn, ...mergedExpensesIn },
        expensesOut: { ...prev.expensesOut, ...mergedExpensesOut },
      }));

      // First Estimate = snapshot taken at ops creation; Revised = the live estimate
      const firstEstimate = extractVesselEstimate(
        savedVessel.operationVesselContract?.vesselDetails?.clientQuery
          ? savedVessel.operationVesselContract.vesselDetails
          : savedVessel.vesselDetails,
      );
      const revisedEstimate = extractVesselEstimate(savedVessel.vesselDetails);

      setValues((prev) => ({
        ...prev,
        Revenue: firstEstimate.in,
        RevenueOut: firstEstimate.out,
        Revised: revisedEstimate.in,
        RevisedOut: revisedEstimate.out,
        Actual:
          {
            ...actualCalculation.vesselIn,
            cvePerDay: subModelCalculation?.expensesIn?.cvePerDay,
            ilohc: subModelCalculation?.expensesIn?.ilohc,
          } || {},
        ActualOut:
          {
            ...actualCalculation.vesselOut,
            cvePerDay: subModelCalculation?.expensesOut?.cvePerDay,
            ilohc: subModelCalculation?.expensesOut?.ilohc,
          } || {},
      }));

      unchangedValues.current = actualCalculation;

      setSustainabilityData(
        (prev) => ({ ...prev, ...sustainabilityData }) || {},
      );

      setFuelSettlementMode(fuelSettlementMode);
      setOperationStatus(operationStatus);

      setCalculatedExpenses(
        (prev) => ({ ...prev, ...calculatedExpenses }) || {},
      );

      setRawContractData(data || {});

      const safeJsonParse = (value) => {
        try {
          return JSON.parse(value);
        } catch {
          return null;
        }
      };

      const EXPENSE_KEYS = [
        "grossHireAmount",
        "adCom",
        "brokerageExp",
        "deduction",
        "totalNetHireExpense",
        "cve",
        "portExpenses",
        "otherExp",
      ];

      const buildInitialExpenseAllocations = ({
        keys,
        vesselId,
        vesselContractId,
        vesselName,
        vesselType,
        actualValues,
        expensesFields,
      }) =>
        keys.map((key) => {
          const label = expensesFields.find((f) => f.key === key)?.label;

          return {
            vesselId,
            key,
            vesselType,
            totalAmt: actualValues[key] || 0,
            vesselName,
            label,
            invoiceDetails: [
              {
                _id: uuidv4(),
                vesselContractId,
                vesselName,
                label,
                counterPartyId: "",
                name: "",
                amt: 0,
                isCargo: false,
              },
            ],
          };
        });

      const normalizeInvoicesWithVesselType = (invoices, vesselType) =>
        invoices.map((item) => ({
          ...item,
          vesselType,
          invoiceDetails: Array.isArray(item.invoiceDetails)
            ? item.invoiceDetails
            : [],
        }));

      const initInvoicesAndUsed = ({
        storedInvoices,
        fallbackInvoices,
        vesselType,
      }) => {
        const invoices =
          Array.isArray(storedInvoices) && storedInvoices.length > 0
            ? normalizeInvoicesWithVesselType(storedInvoices, vesselType)
            : fallbackInvoices;

        const used = invoices?.reduce((acc, item) => {
          acc[item.key] = new Set(
            item.invoiceDetails
              .map((detail) => detail.counterPartyId)
              .filter(Boolean),
          );
          return acc;
        }, {});

        return { invoices, used };
      };

      const vesselDetails = actualVesselContract?.vesselDetails || {};

      const vesselName =
        safeJsonParse(vesselDetails?.vessel)?.vesselLabel || "";

      const vesselId = actualVesselContract?.vesselId;
      const vesselContractId = data?.vesselContractId;

      const initialExpenseAllocationsIn = buildInitialExpenseAllocations({
        keys: EXPENSE_KEYS,
        vesselId,
        vesselContractId,
        vesselName,
        vesselType: "vesselIn",
        actualValues: values.Actual,
        expensesFields,
      });

      const initialExpenseAllocationsOut = buildInitialExpenseAllocations({
        keys: EXPENSE_KEYS,
        vesselId,
        vesselContractId,
        vesselName,
        vesselType: "vesselOut",
        actualValues: values.ActualOut,
        expensesFields,
      });

      setRawContractData(data || {});

      const { invoices: expenseInvoicesIn, used: initialUsedIn } =
        initInvoicesAndUsed({
          storedInvoices: actualVesselContract?.vesselInvoiceDetails,
          fallbackInvoices: initialExpenseAllocationsIn,
          vesselType: "vesselIn",
        });

      setUsedCounterpartiesIn(initialUsedIn);
      setExpenseInvoicesIn(expenseInvoicesIn);

      const { invoices: expenseInvoicesOut, used: initialUsedOut } =
        initInvoicesAndUsed({
          storedInvoices: actualVesselContract?.vesselInvoiceDetailsOut,
          fallbackInvoices: initialExpenseAllocationsOut,
          vesselType: "vesselOut",
        });

      setUsedCounterpartiesOut(initialUsedOut);
      setExpenseInvoicesOut(expenseInvoicesOut);

      /* -------------------- CLIENT QUERY (SINGLE SOURCE OF TRUTH) -------------------- */

      const clientQuery =
        data?.vesselContractDetails?.actualVesselContract?.vesselDetails
          ?.clientQuery || {};

      const cargoList = clientQuery?.cargoList || [];

      if (!Array.isArray(cargoList)) {
        setCargoOperationTableData([]);
        return;
      }

      const estimatePortList = extractEstimatePortList(data);
      const mergedCargoList = mergeCargoWithEstimate(
        cargoList,
        estimatePortList,
        convertToUTC,
      );

      setCargoOperationTableData({
        ...clientQuery,
        cargoList: mergedCargoList,
        FuelSupplyRequests:
          data?.vesselContractDetails?.actualVesselContract
            ?.FuelSupplyRequests || [],
      });

      setIntialCargoInvoiceDetails(clientQuery);

      /* -------------------- LAYTIME PROCESSING -------------------- */

      const initialGroups = clientQuery?.counterPartyNames || [
        { id: "group-1", name: "Port A" },
        { id: "group-2", name: "Port B" },
      ];

      const processedData = mergedCargoList
        .map((cargo) => {
          if (!cargo?.laytimeStatement) return null;

          const st = cargo.laytimeStatement;
          const op = cargo?.operation?.toLowerCase();

          if (op !== "loading" && op !== "discharging") return null;

          return {
            Operation: cargo.operation || st.norTerms || "Loading",
            Port: cargo.port || "",
            CargoDescription: st.cargoDesc || cargo.cargo || "",
            CargoQty: Number(st.cargoQty || cargo.cargoQty || 0).toFixed(3),
            RateMTday: Number(st.dischargingRate || cargo.rate || 0).toFixed(3),
            TurnTimeDays: Number(
              st.turnTimeAllowed || cargo.turnTime || 0,
            ).toFixed(3),
            BLDate: moment(
              cargo.billOfLadingDate || st.norTendered || "",
            ).format("YYYY-MM-DD HH:mm"),
            CounterParty: cargo.charterer || "",
            DemurrageRate: Number(st.demurrageRate || 0).toFixed(3),
            DespatchRate: Number(st.despatchRate || 0).toFixed(3),
            TotalTimeAllowed: Number(st.totalTimeAllowed || 0).toFixed(3),
            TotalTimeUsed: Number(st.totalTimeUsed || 0).toFixed(3),
            Difference: Number(st.difference || 0).toFixed(3),
            DemurrageDespatch: Number(st.demurrage || 0).toFixed(3),
            vesselName: cargo.vesselName || "",
            terms: cargo.terms || st.dischargingTerms || "",
            laytimeStarts: st.laytimeStarts || "",
            laytimeStops: st.laytimeStops || "",
          };
        })
        .filter(Boolean);

      const operations = processedData.map((op, index) => ({
        ...op,
        id: `op-${index}`,
        groupId: null,
        order: index,
      }));

      if (!Object.keys(clientQuery?.laytimeSettlementDetails || {}).length) {
        updateFinalState(operations, initialGroups);
      } else {
        setFinalState(clientQuery?.laytimeSettlementDetails);
      }
      return {
        success: true,
      };
    } catch (error) {
      console.error("Error fetching operation:", error);
      setCargoOperationTableData([]);
    } finally {
      props.setLoader(false);
    }
  };

  useEffect(() => {
    if (mainTab === 2) fetchOperation();
  }, [mainTab]);

  const calculateStats = (operations) => {
    return {
      totalCargoQty: operations
        ?.reduce((sum, op) => sum + (parseFloat(op.CargoQty) || 0), 0)
        .toFixed(3),
      totalRateMTday: operations
        ?.reduce((sum, op) => sum + (parseFloat(op.RateMTday) || 0), 0)
        .toFixed(3),
      totalTurnTimeDays: operations
        ?.reduce((sum, op) => sum + (parseFloat(op.TurnTimeDays) || 0), 0)
        .toFixed(3),
      totalDemurrageRate: operations
        ?.reduce((sum, op) => sum + (parseFloat(op.DemurrageRate) || 0), 0)
        .toFixed(3),
      totalDespatchRate: operations
        ?.reduce((sum, op) => sum + (parseFloat(op.DespatchRate) || 0), 0)
        .toFixed(3),
      totalTimeAllowed: operations
        ?.reduce((sum, op) => sum + (parseFloat(op.TotalTimeAllowed) || 0), 0)
        .toFixed(3),
      totalTimeUsed: operations
        ?.reduce((sum, op) => sum + (parseFloat(op.TotalTimeUsed) || 0), 0)
        .toFixed(3),
      totalDifference: operations
        ?.reduce((sum, op) => sum + (parseFloat(op.Difference) || 0), 0)
        .toFixed(3),
      totalDemurrageDespatch: operations
        ?.reduce((sum, op) => sum + (parseFloat(op.DemurrageDespatch) || 0), 0)
        .toFixed(3),
      count: operations.length,
    };
  };

  const updateFinalState = (operations, groups) => {
    const ungroupedOps = operations.filter((op) => !op.groupId);
    const groupsWithOps = groups.map((group) => {
      const groupOps = operations
        .filter((op) => op.groupId === group.id)
        .sort((a, b) => a.order - b.order);

      return {
        ...group,
        operations: groupOps,
        totalStats: calculateStats(groupOps),
      };
    });

    setFinalState({
      groups: groupsWithOps,
      ungroupedOperations: ungroupedOps,
      allOperations: operations,
      globalStats: calculateStats(operations),
    });
  };

  useEffect(() => {
    setCargoContractDetails((prev) => ({
      ...prev,
      cargoDetails: {
        ...prev.cargoDetails,
        cargoList: cargoOperationTableData?.cargoList,
      },
    }));
  }, [cargoOperationTableData]);

  useEffect(() => {
    const cargoDetails = cargoContractDetails?.cargoDetails || {};
    const cargoList = cargoDetails?.cargoList || [];
    const rawVessel = vesselContractDetails?.vesselDetails?.vessel;

    const vesselName = rawVessel ? JSON.parse(rawVessel)?.vesselName : "";
    const commencedZDate = formatToLT(cargoList[0]?.arrivalTimeZ);
    const completedZDate = formatToLT(
      cargoList[cargoList.length - 1]?.arrivalTimeZ,
    );

    const updatedData = {
      vesselName,
      vesselId: vesselContractDetails?.vesselFixtureId || "",
      voyageNumber: vesselContractDetails?.voyageNumber || "",
      cpDate: vesselContractDetails?.vesselOutContract?.cpDate
        ? moment(vesselContractDetails?.vesselOutContract?.cpDate).format(
          "YYYY-MM-DD HH:mm",
        )
        : "",

      counterpartyRevenue:
        vesselContractDetails?.counterPartyNames?.length > 0
          ? vesselContractDetails?.counterPartyNames
            ?.map((val) => val?.name)
            .join(", ") || ""
          : vesselContractDetails?.counterparty || "",
      opsType: vesselContractDetails?.opsType || "",
      opsId: vesselContractDetails?.opsId || "",
      commencedZ: commencedZDate
        ? moment(commencedZDate, "DD/MM/YYYY HH:mm", true).format(
          "DD-MMM-YYYY HH:mm",
        )
        : "",
      commencePort: cargoList[0]?.port || "",
      firstLoadPort: cargoList[1]?.port || "",
      lastDischPort: cargoList[cargoList.length - 1]?.port || "",
      completedZ: completedZDate
        ? moment(completedZDate, "DD/MM/YYYY HH:mm", true).format(
          "DD-MMM-YYYY HH:mm",
        )
        : "",
      noOfDays:
        commencedZDate && completedZDate
          ? moment(completedZDate, "DD/MM/YYYY HH:mm", true).diff(
            moment(commencedZDate, "DD/MM/YYYY HH:mm", true),
            "days",
          )
          : "",
      totalRevenue: values?.Actual?.netFreightRevenue || "",
      totalExpenditure: values?.Actual?.totalTripExp || "",
      totalProfit: values?.Actual?.netProfit || "",
      dailyReturnsTCE: values?.Actual?.tceNet || "",
      opsPIC: `${rawContractData?.assignedTo?.firstName || ""} ${rawContractData?.assignedTo?.lastName || ""
        }`.trim(),
      charteringPIC: `${rawContractData?.userId?.firstName || ""} ${rawContractData?.userId?.lastName || ""
        }`.trim(),
      vesselType: vesselContractDetails?.vesselType || "",
      status: vesselContractDetails?.status || "",
      closureDateTime: cargoContractDetails?.closureDateTime || "",
      cargoDescription: cargoDetails?.description || "",
      cpDateRevenue: cargoContractDetails?.cpDateRevenue || "",
      cargoContractId: cargoContractDetails?.cargoContractId || "",
      shipmentNumber: cargoContractDetails?.shipmentNumber || "",
      contractIdUser: cargoContractDetails?.contractIdUser || "",
    };

    setVesselCargoDetailsTableData((prev) => ({
      ...prev,
      ...updatedData,
    }));
  }, [vesselContractDetails, cargoContractDetails, values, rawContractData]);

  useEffect(() => {
    const portOperation = cargoOperationTableData?.cargoList || [];
    setSelectedPortsCommence(
      portOperation.filter((item) => item.operation === "Commence"),
    );
    setSelectedQuery(cargoOperationTableData);
    setSelectedPorts(
      portOperation.filter(
        (item) =>
          item.operation === "Loading" || item.operation === "Discharging",
      ),
    );
  }, [cargoOperationTableData]);

  const handleChange = (category, field) => (event) => {
    setValues((prev) => ({
      ...prev,
      [category]: {
        ...prev[category],
        [field]: event.target.value,
      },
    }));
  };
  const handleDateChangeForCargoOperationTable = (rowIndex, field, date) => {
    setCargoOperationTableData((prev) => {
      const newData = [...prev.cargoList];
      newData[rowIndex][field] = date
        ? moment(date).format("YYYY-MM-DD HH:mm:ss.S")
        : "";
      return {
        ...prev,
        cargoList: newData,
      };
    });
  };

  const handleDateChangeForFualSupplyTable = (rowIndex, field, date) => {
    setPendingDateChange({ rowIndex, field, date });
    setShowDateChangeModal(true);
  };

  function convertToUTC(dateTimeString, timezoneOffset) {
    const now = new Date();
    const fallbackUTC =
      now.toISOString().slice(0, 19).replace("T", " ") + ".000";

    if (!dateTimeString || typeof dateTimeString !== "string") {
      return fallbackUTC;
    }

    let normalizedDateTime = dateTimeString.trim();

    if (!normalizedDateTime.includes(" ")) {
      normalizedDateTime += " 00:00:00.000";
    } else {
      const [datePart, timePart] = normalizedDateTime.split(" ");
      const timeComponents = timePart.split(":");

      if (timeComponents.length === 2) {
        normalizedDateTime = `${datePart} ${timePart}:00.000`;
      } else if (timeComponents.length === 3) {
        const [hh, mm, ss] = timeComponents;
        if (!ss.includes(".")) {
          normalizedDateTime = `${datePart} ${hh}:${mm}:${ss}.000`;
        }
      }
    }
    let offsetHours = 0;
    let offsetMinutes = 0;

    if (timezoneOffset && typeof timezoneOffset === "string") {
      const offsetMatch = timezoneOffset.match(/^([+-]?)(\d+)(?:\.(\d+))?$/);
      if (offsetMatch) {
        const sign = offsetMatch[1] === "-" ? -1 : 1;
        offsetHours = (parseInt(offsetMatch[2]) || 0) * sign;
        offsetMinutes = (parseInt(offsetMatch[3]) || 0) * sign;
      }
    }

    let date;
    try {
      const [datePart, timePart] = normalizedDateTime.split(" ");
      const [hours, minutes, secondsWithMs] = timePart.split(":");
      const [seconds, milliseconds] = secondsWithMs.split(".").map(Number);
      let day, month, year;
      if (datePart.includes("/")) {
        [day, month, year] = datePart.split("/").map(Number);
      } else if (datePart.includes("-")) {
        [year, month, day] = datePart.split("-").map(Number);
      } else {
        throw new Error("Unknown date format");
      }

      date = new Date(
        Date.UTC(
          year || now.getUTCFullYear(),
          month ? month - 1 : now.getUTCMonth(),
          day || now.getUTCDate(),
          hours || 0,
          minutes || 0,
          seconds || 0,
          milliseconds || 0,
        ),
      );

      if (isNaN(date.getTime())) {
        date = new Date();
      }
    } catch (e) {
      date = new Date();
    }

    date.setUTCHours(date.getUTCHours() - offsetHours);
    date.setUTCMinutes(date.getUTCMinutes() - offsetMinutes);

    const pad = (num) => String(num).padStart(2, "0");

    return (
      `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(
        date.getUTCDate(),
      )} ` +
      `${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}:${pad(
        date.getUTCSeconds(),
      )}.` +
      `${String(date.getUTCMilliseconds()).padStart(3, "0")}`
    );
  }

  const handleInputChangeForCargoOperationTable = (rowIndex, field, value) => {
    isSaved.current = true;

    setCargoOperationTableData((prev) => {
      const newCargoList = [...prev.cargoList];
      const existingRow = newCargoList[rowIndex] || {};

      const updatedRow = { ...existingRow };

      if (field === "timeZone") {
        updatedRow.timeZone = value;
        updatedRow.departureTimeZ = convertToUTC(
          existingRow.departureTime,
          value,
        );
        updatedRow.arrivalTimeZ = convertToUTC(existingRow.arrivalTime, value);
      }

      const INNER_FIELD_KEY_SET = new Set(
        seaTableColumns.filter((col) => col.innerField).map((col) => col.key),
      );

      const FUEL_PRICE_FIELDS = [
        "fuelPriceType0Hsfo",
        "fuelPriceType0Lsfo",
        "fuelPriceType0Mgo",
        "fuelPriceType1Hsfo",
        "fuelPriceType1Lsfo",
        "fuelPriceType1Mgo",
        "fuelPriceType2Hsfo",
        "fuelPriceType2Lsfo",
        "fuelPriceType2Mgo",
        "fuelPriceType3Hsfo",
        "fuelPriceType3Lsfo",
        "fuelPriceType3Mgo",
        "fuelPriceType4Hsfo",
        "fuelPriceType4Lsfo",
        "fuelPriceType4Mgo",
        "bunkerSuppliedHSFO",
        "bunkerSuppliedLSFO",
        "bunkerSuppliedMGO",
        "reserve",
        "paidBy",
      ];

      const shouldGoInsideCargoDetails =
        INNER_FIELD_KEY_SET.has(field) || FUEL_PRICE_FIELDS.includes(field);

      if (shouldGoInsideCargoDetails) {
        updatedRow.cargoDetails = {
          ...(existingRow.cargoDetails || {}),
          [field]: value,
        };
      } else if (field !== "timeZone") {
        updatedRow[field] = value;
      }

      newCargoList[rowIndex] = updatedRow;

      return {
        ...prev,
        cargoList: newCargoList,
      };
    });
  };

  const handlePortChange = async (rowIndex, field, value) => {
    const updatedCargoList = [...cargoOperationTableData.cargoList];
    if (
      field === "operation" &&
      updatedCargoList[rowIndex]?.operation !== value &&
      isSingleOperationTaken(updatedCargoList, value)
    ) {
      return toastError(
        `Only one ${value} operation is allowed per contract.`,
      );
    }
    updatedCargoList[rowIndex] = {
      ...updatedCargoList[rowIndex],
      [field]: value,
    };

    const newCargoOperationTableData = {
      ...cargoOperationTableData,
      cargoList: updatedCargoList,
    };

    setCargoOperationTableData(newCargoOperationTableData);
    setIntialCargoInvoiceDetails({
      ...cargoContractDetails,
      cargoDetails: newCargoOperationTableData,
    });
  };

  const confirmDateChange = (changeType) => {
    if (!pendingDateChange) return;

    const { rowIndex, field, date } = pendingDateChange;

    if (changeType === "current") {
      updateSingleDate(rowIndex, field, date);
    } else {
      updateDateWithCascade(rowIndex, field, date);
    }

    setPendingDateChange(null);
    setShowDateChangeModal(false);
  };

  const updateSingleDate = (rowIndex, field, date) => {
    const inputFormat = "DD/MM/YYYY HH:mm";
    const storedFormat = "YYYY-MM-DD HH:mm:ss.S";

    setCargoOperationTableData((prev) => {
      const newData = [...prev?.cargoList];
      const parsedNewDate = moment(date, inputFormat, true);

      if (!parsedNewDate.isValid()) {
        toastError("Invalid date format.");
        return prev;
      }

      const timeZoneOffset = newData[rowIndex]?.timeZone || "+0";

      if (field === "arrivalTime") {
        const sameRowDepartureStr = newData[rowIndex]["departureTime"];
        if (sameRowDepartureStr) {
          const sameRowDeparture = moment(
            sameRowDepartureStr,
            storedFormat,
            true,
          );
          if (
            sameRowDeparture.isValid() &&
            !parsedNewDate.isBefore(sameRowDeparture)
          ) {
            toastError(
              "Arrival time must be before the same row's departure time.",
            );
            return prev;
          }
        }

        if (rowIndex > 0) {
          const prevDepartureStr = newData[rowIndex - 1]["departureTime"];
          if (prevDepartureStr) {
            const prevDeparture = moment(prevDepartureStr, storedFormat, true);
            if (
              prevDeparture.isValid() &&
              !parsedNewDate.isAfter(prevDeparture)
            ) {
              toastError(
                "Arrival time must be after the previous row's departure time.",
              );
              return prev;
            }
          }
        }
      }

      if (field === "departureTime") {
        const currentArrivalStr = newData[rowIndex]["arrivalTime"];
        const currentArrival = moment(currentArrivalStr, storedFormat, true);
        if (
          currentArrival.isValid() &&
          !parsedNewDate.isAfter(currentArrival)
        ) {
          toastError(
            "Departure time must be after arrival time in the same row.",
          );
          return prev;
        }
      }

      newData[rowIndex][field] = parsedNewDate.format(storedFormat);
      if (field === "arrivalTime") {
        newData[rowIndex]["arrivalTimeZ"] = convertToUTC(date, timeZoneOffset);
      } else if (field === "departureTime") {
        newData[rowIndex]["departureTimeZ"] = convertToUTC(
          date,
          timeZoneOffset,
        );
      }
      return {
        ...prev,
        cargoList: newData,
      };
    });
  };

  const updateDateWithCascade = (rowIndex, field, date) => {
    const inputFormat = "DD/MM/YYYY HH:mm";
    const storedFormat = "YYYY-MM-DD HH:mm:ss.S";
    const parseFormats = [
      storedFormat,
      "YYYY-MM-DD HH:mm:ss.SSS",
      "YYYY-MM-DD HH:mm:ss",
      inputFormat,
    ];
    setCargoOperationTableData((prev) => {
      const newData = prev?.cargoList?.map((row) => ({ ...row }));
      const newDate = moment(date, inputFormat, true);

      if (!newDate.isValid()) {
        toastError("Invalid date format.");
        return prev;
      }

      if (field === "arrivalTime" && rowIndex > 0) {
        const prevDepartureStr = newData[rowIndex - 1]["departureTime"];
        const prevDeparture = moment(prevDepartureStr, parseFormats, true);
        if (prevDeparture.isValid() && !newDate.isAfter(prevDeparture)) {
          toastError(
            "Arrival time must be after the previous row's departure time.",
          );
          return prev;
        }
      }

      if (field === "departureTime") {
        const currentArrivalStr = newData[rowIndex]["arrivalTime"];
        const currentArrival = moment(currentArrivalStr, parseFormats, true);
        if (currentArrival.isValid() && !newDate.isAfter(currentArrival)) {
          toastError(
            "Departure time must be after arrival time in the same row.",
          );
          return prev;
        }
      }

      const originalDate = moment(newData[rowIndex][field], parseFormats, true);
      let timeDiff = 0;
      if (originalDate.isValid()) {
        timeDiff = newDate.diff(originalDate, "milliseconds");
      }

      newData[rowIndex][field] = newDate.format(storedFormat);

      const timeZoneOffset =
        newData[rowIndex].timeZone || newData[rowIndex].timeZoneOffset || "+0";
      if (field === "arrivalTime") {
        newData[rowIndex]["arrivalTimeZ"] = convertToUTC(
          newData[rowIndex][field],
          timeZoneOffset,
        );
      } else if (field === "departureTime") {
        newData[rowIndex]["departureTimeZ"] = convertToUTC(
          newData[rowIndex][field],
          timeZoneOffset,
        );
      }

      if (field === "arrivalTime" && timeDiff !== 0) {
        const departureField = "departureTime";
        const departureDate = moment(
          newData[rowIndex][departureField],
          parseFormats,
          true,
        );
        if (departureDate.isValid()) {
          newData[rowIndex][departureField] = departureDate
            .add(timeDiff, "milliseconds")
            .format(storedFormat);
          newData[rowIndex]["departureTimeZ"] = convertToUTC(
            newData[rowIndex][departureField],
            timeZoneOffset,
          );
        }
      }

      if (timeDiff !== 0) {
        for (let i = rowIndex + 1; i < newData.length; i++) {
          const rowTimeZoneOffset =
            newData[i].timeZone || newData[i].timeZoneOffset || "+0";

          ["arrivalTime", "departureTime"].forEach((timeField) => {
            const currentDate = moment(
              newData[i][timeField],
              parseFormats,
              true,
            );
            if (currentDate.isValid()) {
              newData[i][timeField] = currentDate
                .add(timeDiff, "milliseconds")
                .format(storedFormat);

              if (timeField === "arrivalTime") {
                newData[i]["arrivalTimeZ"] = convertToUTC(
                  newData[i][timeField],
                  rowTimeZoneOffset,
                );
              } else if (timeField === "departureTime") {
                newData[i]["departureTimeZ"] = convertToUTC(
                  newData[i][timeField],
                  rowTimeZoneOffset,
                );
              }
            }
          });
        }
      }

      return {
        ...prev,
        cargoList: newData,
      };
    });
  };

  const handleVesselContractInputChange = (e) => {
    const { name, value } = e.target;

    if (editVesselContractModelOpen.key === "vesselContractIn") {
      setVesselContractDetails((prev) => ({
        ...prev,
        [name]: value,
      }));
    } else {
      setVesselContractDetails((prev) => ({
        ...prev,
        vesselOutContract: {
          ...(prev?.vesselOutContract || {}),
          [name]: value,
        },
      }));
    }
  };

  const removeVesselAttachment = (index) => {
    setVesselAttachments((prev) =>
      (Array.isArray(prev) ? prev : []).filter((_, i) => i !== index),
    );
  };

  const handleVesselTextContractUpload = (e) => {
    if (e.target.files[0]) {
      setVesselTextContract(e.target.files[0]);
    }
  };

  const handleVesselContractAttachmentUpload = (e) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      const validFiles = newFiles.filter(
        (file) => file.size <= 5 * 1024 * 1024,
      );

      if (validFiles.length !== newFiles.length) {
        toastError("Some files exceed the 5 MB size limit and were not added.");
      }

      setVesselAttachments((prev) => [
        ...(Array.isArray(prev) ? prev : []),
        ...validFiles,
      ]);
    }
  };

  const handleCargoContractInputChange = (e) => {
    const { name, value } = e.target;
    setCargoContractDetails({
      ...cargoContractDetails,
      [name]: value,
    });
  };

  const removeCargoAttachment = (index) => {
    setCargoAttachments((prev) =>
      (Array.isArray(prev) ? prev : []).filter((_, i) => i !== index),
    );
  };

  const handleCargoTextContractUpload = (e) => {
    if (e.target.files[0]) {
      setCargoTextContract(e.target.files[0]);
    }
  };

  const handleCargoContractAttachmentUpload = (e) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      const validFiles = newFiles.filter(
        (file) => file.size <= 5 * 1024 * 1024,
      );

      if (validFiles.length !== newFiles.length) {
        toastError("Some files exceed the 5 MB size limit and were not added.");
      }

      setCargoAttachments((prev) => [
        ...(Array.isArray(prev) ? prev : []),
        ...validFiles,
      ]);
    }
  };

  // First itinerary row (Commence) is driven by Vessel In > Vessel Details > Bunker Qty / Prices
  // "On Delivery"; the row's inputs are locked, so keep it in sync whenever those change.
  const viDet = vesselContractDetails?.vesselDetails;
  const deliveryFuel = [viDet?.bnkDelhs, viDet?.bnkDells, viDet?.bnkDelmgo]
    .map((v) => Number(v) || 0);
  const deliveryPrice = [
    viDet?.bnkPriceDeliveryhs,
    viDet?.bnkPriceDeliveryls,
    viDet?.bnkPriceDeliverymgo,
  ].map((v) => Number(v) || 0);
  const deliveryKey = [...deliveryFuel, ...deliveryPrice].join("|");
  useEffect(() => {
    if (!viDet || !cargoOperationTableData?.cargoList?.length) return;
    setCargoOperationTableData((prev) => {
      const first = prev.cargoList?.[0];
      if (!first) return prev;
      const d = first.cargoDetails || {};
      const set = {
        bunkerSuppliedHSFO: deliveryFuel[0],
        bunkerSuppliedLSFO: deliveryFuel[1],
        bunkerSuppliedMGO: deliveryFuel[2],
      };
      [0, 1, 2, 3, 4].forEach((t) => {
        set[`fuelPriceType${t}Hsfo`] = deliveryPrice[0];
        set[`fuelPriceType${t}Lsfo`] = deliveryPrice[1];
        set[`fuelPriceType${t}Mgo`] = deliveryPrice[2];
      });
      const next = { ...d, ...set };
      const row = {
        ...first,
        cargoDetails: next,
        departureHsfo: deliveryFuel[0],
        departureLsfo: deliveryFuel[1],
        departureMgo: deliveryFuel[2],
      };
      const same =
        Object.keys(set).every((k) => Number(d[k]) === set[k]) &&
        Number(first.departureHsfo) === row.departureHsfo &&
        Number(first.departureLsfo) === row.departureLsfo &&
        Number(first.departureMgo) === row.departureMgo;
      if (same) return prev;
      return { ...prev, cargoList: [row, ...prev.cargoList.slice(1)] };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deliveryKey, cargoOperationTableData?.cargoList?.length]);

  const handleTabChange = (tabIndex) => {
    if (activeTab !== tabIndex && !isAnimating) {
      setIsAnimating(true);
      setActiveTab(tabIndex);

      if (transitionTimeoutRef.current) {
        clearTimeout(transitionTimeoutRef.current);
      }

      transitionTimeoutRef.current = setTimeout(() => {
        setIsAnimating(false);
        transitionTimeoutRef.current = null;
      }, 300);
    }
  };

  const handleFuelSettlementModeChange = (event) => {
    setFuelSettlementMode(event.target.value);
  };

  const handleOperationStatusChange = (event) => {
    setOperationStatus(event.target.value);
  };

  const calculateRevenue = async (fuelConsumption, contractSide) => {
    const cargoOperation =
      (contractSide === "out"
        ? contractCargoListOut()
        : cargoOperationTableData?.cargoList) || {};
    const fuelType = cargoOperationTableData.fuelType || "TYPE0";
    const {
      bnkDelhs = 0,
      bnkDells = 0,
      bnkDelmgo = 0,
      bnkPriceDeliveryhs = 0,
      bnkPriceDeliveryls = 0,
      bnkPriceDeliverymgo = 0,
    } = (contractSide === "out"
      ? vesselContractDetails?.vesselOutContract?.vesselDetails
      : vesselContractDetails?.vesselDetails) || {};

    const finalData = [];
    const maxLength = cargoOperation.length;

    for (let i = 0; i < maxLength; i++) {
      const item = cargoOperation[i] || {};
      const operation = cargoOperation[i] || {};
      const { cargoDetails } = item || {};

      const {
        bunkerSuppliedHSFO = null,
        bunkerSuppliedLSFO = null,
        bunkerSuppliedMGO = null,
        paidBy = "Operator",
        reserve = "Reserve",
        fuelPriceType0Hsfo = null,
        fuelPriceType0Lsfo = null,
        fuelPriceType0Mgo = null,
        fuelPriceType1Hsfo = null,
        fuelPriceType1Lsfo = null,
        fuelPriceType1Mgo = null,
        fuelPriceType3Hsfo = null,
        fuelPriceType3Lsfo = null,
        fuelPriceType3Mgo = null,
        fuelPriceType4Hsfo = null,
        fuelPriceType4Lsfo = null,
        fuelPriceType4Mgo = null,
      } = cargoDetails || {};

      const freight = Number(item.freight) || 0;
      const adcomRate = Number(item.adcom) || 0;
      const otherRate = Number(item.other) || 0;
      const brokerageRate = Number(item.brokerage) || 0;
      const cargoTx = Number(operation.cargoTx) || 0;

      let hsfo, lsfo, mgo;

      switch (fuelType) {
        case "TYPE0":
          hsfo = fuelPriceType0Hsfo;
          lsfo = fuelPriceType0Lsfo;
          mgo = fuelPriceType0Mgo;
          break;
        case "TYPE1":
          hsfo = i === 0 ? bnkPriceDeliveryhs : fuelPriceType1Hsfo;
          lsfo = i === 0 ? bnkPriceDeliveryls : fuelPriceType1Lsfo;
          mgo = i === 0 ? bnkPriceDeliverymgo : fuelPriceType1Mgo;
          break;
        case "TYPE2":
          hsfo = bunkerSuppliedHSFO;
          lsfo = bunkerSuppliedLSFO;
          mgo = bunkerSuppliedMGO;
          break;
        case "TYPE3":
          hsfo = fuelPriceType3Hsfo;
          lsfo = fuelPriceType3Lsfo;
          mgo = fuelPriceType3Mgo;
          break;
        case "TYPE4":
          hsfo = fuelPriceType4Hsfo;
          lsfo = fuelPriceType4Lsfo;
          mgo = fuelPriceType4Mgo;
          break;
        default:
          hsfo = null;
          lsfo = null;
          mgo = null;
      }

      let fqaHsfo, fqaLsfo, fqaMgo, tsfHsfo, tsfLsfo, tsfMgo, fqaType;

      if (i === 0) {
        fqaHsfo =
          reserve && reserve?.toLowerCase() === "reserve" ? bnkDelhs : 0;
        fqaLsfo =
          reserve && reserve?.toLowerCase() === "reserve" ? bnkDells : 0;
        fqaMgo =
          reserve && reserve?.toLowerCase() === "reserve" ? bnkDelmgo : 0;
        fqaType = paidBy || "Internal";

        tsfHsfo =
          reserve && reserve?.toLowerCase() !== "reserve" ? bnkDelhs : 0;
        tsfLsfo =
          reserve && reserve?.toLowerCase() !== "reserve" ? bnkDells : 0;
        tsfMgo =
          reserve && reserve?.toLowerCase() !== "reserve" ? bnkDelmgo : 0;
      } else {
        fqaHsfo =
          reserve && reserve?.toLowerCase() === "reserve"
            ? bunkerSuppliedHSFO
            : 0;
        fqaLsfo =
          reserve && reserve?.toLowerCase() === "reserve"
            ? bunkerSuppliedLSFO
            : 0;
        fqaMgo =
          reserve && reserve?.toLowerCase() === "reserve"
            ? bunkerSuppliedMGO
            : 0;
        fqaType = paidBy || "Internal";

        tsfLsfo =
          reserve && reserve?.toLowerCase() !== "reserve"
            ? bunkerSuppliedLSFO
            : 0;
        tsfHsfo =
          reserve && reserve?.toLowerCase() !== "reserve"
            ? bunkerSuppliedHSFO
            : 0;
        tsfMgo =
          reserve && reserve?.toLowerCase() !== "reserve"
            ? bunkerSuppliedMGO
            : 0;
      }

      const totalCommissionRate = adcomRate + otherRate + brokerageRate;
      // Excel: (freight + additional rate) x cargo qty + lumpsum freight
      const grossRevenue =
        (freight + (Number(item.additionalRate) || 0)) * cargoTx +
        (Number(item.lumpsumFreight) || 0);

      const adcomAmt = grossRevenue * (adcomRate / 100);
      const otherAmt = grossRevenue * (otherRate / 100);
      const brokerageAmt = grossRevenue * (brokerageRate / 100);
      const netFrt = freight * (totalCommissionRate / 100);

      const netRevenue = grossRevenue - adcomAmt - otherAmt - brokerageAmt;

      finalData.push({
        freight,
        ttlComm: totalCommissionRate,
        netFrt,
        grossRevenue,
        netRevenue,
        adcomAmt,
        otherAmt,
        brokerageAmt,
        hsfo,
        lsfo,
        mgo,
        fqaHsfo,
        fqaLsfo,
        fqaMgo,
        fcaHsfo: fqaHsfo * hsfo,
        fcaLsfo: fqaLsfo * lsfo,
        fcaMgo: fqaMgo * mgo,
        fqaType,
        tsfHsfo,
        tsfLsfo,
        tsfMgo,
        lifoQtyRemainHsfo: 0,
        lifoQtyAppliedHsfo: 0,
        fifoQtyRemainHsfo: 0,
        fifoQtyAppliedHsfo: 0,
        lifoFuelCostHsfo: 0,
        fifoFuelCostHsfo: 0,
        avgFualCostHsfo: 0,
        lifoQtyRemainLsfo: 0,
        lifoQtyAppliedLsfo: 0,
        fifoQtyRemainLsfo: 0,
        fifoQtyAppliedLsfo: 0,
        lifoFuelCostLsfo: 0,
        fifoFuelCostLsfo: 0,
        avgFualCostLsfo: 0,
        lifoQtyRemainMgo: 0,
        lifoQtyAppliedMgo: 0,
        fifoQtyRemainMgo: 0,
        fifoQtyAppliedMgo: 0,
        lifoFuelCostMgo: 0,
        fifoFuelCostMgo: 0,
        avgFualCostMgo: 0,
      });
    }

    function calculateFIFO(finalData, valuesKey, fuelType, tsfField) {
      const qty = Number(fuelConsumption?.[valuesKey]) || 0;

      for (let i = 0; i < finalData.length; i++) {
        if (i === 0) {
          const firstTsf = finalData[0][tsfField] || 0;
          finalData[i][`fifoQtyRemain${fuelType}`] = Math.max(
            qty - firstTsf,
            0,
          );
          finalData[i][`fifoQtyApplied${fuelType}`] = Math.min(qty, firstTsf);
        } else {
          const previousQtyRemain =
            finalData[i - 1][`fifoQtyRemain${fuelType}`] || 0;
          const currentTsf = finalData[i][tsfField] || 0;
          finalData[i][`fifoQtyRemain${fuelType}`] = Math.max(
            previousQtyRemain - currentTsf,
            0,
          );
          finalData[i][`fifoQtyApplied${fuelType}`] = Math.min(
            previousQtyRemain,
            currentTsf,
          );
        }

        finalData[i][`fifoFuelCost${fuelType}`] =
          (finalData[i][fuelType.toLowerCase()] || 0) *
          finalData[i][`fifoQtyApplied${fuelType}`];
      }
    }

    function calculateLIFO(finalData, valuesKey, fuelType, tsfField) {
      const qty = Number(fuelConsumption?.[valuesKey]) || 0;

      for (let i = 0; i < finalData.length; i++) {
        const reverseIndex = finalData.length - 1 - i;

        if (i === 0) {
          const lastTsf = finalData[finalData.length - 1][tsfField] || 0;
          finalData[i][`lifoQtyRemain${fuelType}`] = Math.max(qty - lastTsf, 0);
          finalData[i][`lifoQtyApplied${fuelType}`] = Math.min(qty, lastTsf);
        } else {
          const previousQtyRemain =
            finalData[i - 1][`lifoQtyRemain${fuelType}`] || 0;
          const currentTsf = finalData[reverseIndex][tsfField] || 0;
          finalData[i][`lifoQtyRemain${fuelType}`] = Math.max(
            previousQtyRemain - currentTsf,
            0,
          );
          finalData[i][`lifoQtyApplied${fuelType}`] = Math.min(
            previousQtyRemain,
            currentTsf,
          );
        }

        finalData[i][`lifoFuelCost${fuelType}`] =
          (finalData[reverseIndex][fuelType.toLowerCase()] || 0) *
          finalData[i][`lifoQtyApplied${fuelType}`];
      }
    }

    function calculateAvgCost(finalData, fuelType, tsfField) {
      for (let i = 0; i < finalData.length; i++) {
        finalData[i][`avgFualCost${fuelType}`] =
          (finalData[i][tsfField] || 0) *
          (finalData[i][fuelType.toLowerCase()] || 0);
      }
    }

    calculateFIFO(finalData, "hsfoQty", "Hsfo", "tsfHsfo");
    calculateLIFO(finalData, "hsfoQty", "Hsfo", "tsfHsfo");
    calculateAvgCost(finalData, "Hsfo", "tsfHsfo");

    calculateFIFO(finalData, "lsfoQty", "Lsfo", "tsfLsfo");
    calculateLIFO(finalData, "lsfoQty", "Lsfo", "tsfLsfo");
    calculateAvgCost(finalData, "Lsfo", "tsfLsfo");

    calculateFIFO(finalData, "mgoQty", "Mgo", "tsfMgo");
    calculateLIFO(finalData, "mgoQty", "Mgo", "tsfMgo");
    calculateAvgCost(finalData, "Mgo", "tsfMgo");

    return finalData;
  };

  const contractCargoList = () => cargoOperationTableData?.cargoList || [];

  // Vessel-Out covers Delivery-out to Redelivery-in (inclusive). Vessel-In is calculated over
  // the whole list minus those legs. Older operations without both rows use the whole list.
  const vesselOutRange = () => {
    const list = contractCargoList();
    const from = list.findIndex((item) => item.operation === "Delivery-out");
    const to = list.findIndex((item) => item.operation === "Redelivery-in");
    return from !== -1 && to > from ? { from, to } : null;
  };
  const contractCargoListOut = () => {
    const range = vesselOutRange();
    return range
      ? contractCargoList().slice(range.from, range.to + 1)
      : contractCargoList();
  };

  const parseSplitHire = (splitHire) => {
    if (Array.isArray(splitHire)) return splitHire;
    if (!splitHire) return [];
    try {
      const parsed = JSON.parse(splitHire);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

  const contractHireDays = (list, redeliveryOperation) => {
    const delivery =
      list.find((item) => item.operation === "Commence") || list[0];
    const redelivery = list.find(
      (item) => item.operation === redeliveryOperation,
    );
    const deliveryTime = delivery?.departureTime || delivery?.arrivalTime;
    const redeliveryTime = redelivery?.departureTime || redelivery?.arrivalTime;
    if (!deliveryTime || !redeliveryTime) return 0;
    const timeFormats = [
      "DD/MM/YYYY HH:mm",
      "DD/MM/YYYY HH:mm:ss",
      "DD-MM-YYYY HH:mm",
      "YYYY-MM-DD HH:mm:ss.SSS",
      "YYYY-MM-DD HH:mm:ss.S",
      "YYYY-MM-DD HH:mm:ss",
      "YYYY-MM-DD HH:mm",
      moment.ISO_8601,
    ];
    const start = moment(deliveryTime, timeFormats, true).subtract(
      Number(delivery.timeZone) || 0,
      "hours",
    );
    const end = moment(redeliveryTime, timeFormats, true).subtract(
      Number(redelivery.timeZone) || 0,
      "hours",
    );
    return start.isValid() && end.isValid()
      ? Math.max(0, end.diff(start, "days", true))
      : 0;
  };

  const calculateExpenses = async () => {
    let {
      hireGbb = 0,
      hirePerDay = 0,
      splitHire = "",
      tcAdcom = 0,
      brokerage = 0,
    } = vesselContractDetails?.vesselDetails || {};
    brokerage = Number(brokerage) / 100 || 0;
    const cargoList = contractCargoList();
    const splitHireData = parseSplitHire(splitHire);
    const OffhireDuration = contractHireDays(cargoList, "Redelivery-in");
    const result = [];
    let currentDayRemain = OffhireDuration;
    const format = "YYYY-MM-DD HH:mm:ss.S";

    for (let i = 0; i < splitHireData?.length; i++) {
      const item = splitHireData[i];
      const NoDays = Math.max(0, Number(item.NoDays) || 0);
      const hireRate = Number(item.hireRate) || 0;
      const DayApplied = Math.min(currentDayRemain, NoDays);
      const DayRemain = currentDayRemain - DayApplied;
      const Hire = hireRate * DayApplied;
      currentDayRemain = DayRemain;
      result.push({
        DayRemain,
        DayApplied,
        Hire,
      });
    }

    const travelData = [];
    let totalTravelConsumption = 0;
    let totalSeaDays = 0;
    let totalHSFO = 0;
    let totalLSFO = 0;
    let totalMGO = 0;
    let totalNormalHS = 0;
    let totalEcaLS = 0;
    let totalStayHSFO = 0;
    let totalStayLSFO = 0;
    let totalStayMGO = 0;

    let totalBallastSeaday = 0;
    let totalBallastHSFO = 0;
    let totalBallastLSFO = 0;
    let totalBallastMGO = 0;
    let totalBallastStay = 0;
    let totalBallastStayHSFO = 0;
    let totalBallastStayLSFO = 0;
    let totalBallastStayMGO = 0;
    let totalBallast = 0;
    let totalLaden = 0;
    let totalLadenSeaday = 0;
    let totalLadenHSFO = 0;
    let totalLadenLSFO = 0;
    let totalLadenMGO = 0;
    let totalLadenStay = 0;
    let totalLadenStayHSFO = 0;
    let totalLadenStayLSFO = 0;
    let totalLadenStayMGO = 0;

    if (cargoList.length > 0) {
      const outRange = vesselOutRange();
      for (let j = 0; j < cargoList.length - 1; j++) {
        // legs between Delivery-out and Redelivery-in are Vessel-Out's days and consumption
        if (outRange && j >= outRange.from && j < outRange.to) continue;
        const currentItem = cargoList[j];
        const nextItem = cargoList[j + 1] || {};
        const nextCargoDetails = nextItem?.cargoDetails || {};

        const currentTimeZone = Number(currentItem?.timeZone) || 0;
        const currentDepartureTimeLT = currentItem?.departureTime;
        const nextTimeZone = Number(nextItem?.timeZone) || 0;
        const nextArrivalTimeLT = nextItem?.arrivalTime;
        const nextDepartureTimeLT = nextItem?.departureTime;

        const currentDepartureAdjusted = parseOpsDate(
          currentDepartureTimeLT,
        ).isValid()
          ? parseOpsDate(currentDepartureTimeLT).subtract(
            currentTimeZone,
            "hours",
          )
          : moment.invalid();

        const nextArrivalAdjusted = parseOpsDate(nextArrivalTimeLT).isValid()
          ? parseOpsDate(nextArrivalTimeLT).subtract(nextTimeZone, "hours")
          : moment.invalid();

        const travelDays =
          currentDepartureAdjusted.isValid() && nextArrivalAdjusted.isValid()
            ? nextArrivalAdjusted.diff(currentDepartureAdjusted, "days", true)
            : 0;

        const currentHSFO =
          (Number(currentItem?.departureHsfo) || 0) -
          (Number(nextItem?.arrivalHsfo) || 0);
        const currentLSFO =
          (Number(currentItem?.departureLsfo) || 0) -
          (Number(nextItem?.arrivalLsfo) || 0);
        const currentMGO =
          (Number(currentItem?.departureMgo) || 0) -
          (Number(nextItem?.arrivalMgo) || 0);

        const stayHSFO =
          (Number(nextCargoDetails?.bunkerSuppliedHSFO) || 0) +
          (Number(nextItem?.arrivalHsfo) || 0) -
          (Number(nextItem?.departureHsfo) || 0);

        const stayLSFO =
          (Number(nextCargoDetails?.bunkerSuppliedLSFO) || 0) +
          (Number(nextItem?.arrivalLsfo) || 0) -
          (Number(nextItem?.departureLsfo) || 0);

        const stayMGO =
          (Number(nextCargoDetails?.bunkerSuppliedMGO) || 0) +
          (Number(nextItem?.arrivalMgo) || 0) -
          (Number(nextItem?.departureMgo) || 0);

        const arrival = parseOpsDate(nextArrivalTimeLT);
        const departure = parseOpsDate(nextDepartureTimeLT);
        let totalStay = 0;

        if (arrival.isValid() && departure.isValid()) {
          totalStay = departure.diff(arrival, "days", true);
          totalStay = Math.max(Number(totalStay.toFixed(3)), 0);
        }

        let normalHS = 0;
        let ecaLS = 0;
        const cargoItem = cargoList?.[j + 1];

        if (cargoItem?.sulfur !== "ECA") {
          normalHS += totalStay;
        } else if (cargoItem?.sulfur === "ECA") {
          ecaLS += totalStay;
        }
        if (cargoItem?.lbmarker?.toLowerCase() === "ballast") {
          totalBallast += travelDays;
        } else {
          totalLaden += travelDays;
        }

        const isBallast = cargoItem?.lbmarker?.toLowerCase() === "ballast";

        if (isBallast) {
          totalBallastSeaday += travelDays;
          totalBallastHSFO += currentHSFO;
          totalBallastLSFO += currentLSFO;
          totalBallastMGO += currentMGO;
          totalBallastStay += totalStay;
          totalBallastStayHSFO += stayHSFO;
          totalBallastStayLSFO += stayLSFO;
          totalBallastStayMGO += stayMGO;
        } else {
          totalLadenSeaday += travelDays;
          totalLadenHSFO += currentHSFO;
          totalLadenLSFO += currentLSFO;
          totalLadenMGO += currentMGO;
          totalLadenStay += totalStay;
          totalLadenStayHSFO += stayHSFO;
          totalLadenStayLSFO += stayLSFO;
          totalLadenStayMGO += stayMGO;
        }

        travelData.push({
          fromIndex: j,
          toIndex: j + 1,
          port: cargoItem?.port,
          travelDays: Math.max(0, travelDays),
          currentDeparture: currentDepartureAdjusted.isValid()
            ? currentDepartureAdjusted.format(format)
            : "",
          nextArrival: nextArrivalAdjusted.isValid()
            ? nextArrivalAdjusted.format(format)
            : "",
          consumption: Number(currentItem?.consumption) || 0,
          seaDays: Number(currentItem?.seaDays) || 0,
          HSFO: currentHSFO,
          LSFO: currentLSFO,
          MGO: currentMGO,
          stayHSFO,
          stayLSFO,
          stayMGO,
          totalStay,
        });

        totalTravelConsumption += Number(currentItem?.consumption) || 0;
        totalSeaDays += Number(currentItem?.seaDays) || 0;
        totalHSFO += currentHSFO;
        totalLSFO += currentLSFO;
        totalMGO += currentMGO;
        totalNormalHS += normalHS;
        totalEcaLS += ecaLS;
        totalStayHSFO += stayHSFO;
        totalStayLSFO += stayLSFO;
        totalStayMGO += stayMGO;
      }
    }

    const fmt = (num) => Number(Number(num).toFixed(3)) || 0;

    const baseHire = fmt(result?.reduce((sum, { Hire = 0 }) => sum + Hire, 0));
    const totalGrossHire = fmt(baseHire + hirePerDay * currentDayRemain);

    const commissionRate = tcAdcom / 100;
    const vesselAdCom = fmt(-1 * totalGrossHire * commissionRate);
    const vesselAdComGbb = fmt(-1 * hireGbb * commissionRate);
    // Excel: brokerage is payable and is added to the hire expense (TC Adcom is deducted)
    const brokerageAmount = fmt(totalGrossHire * brokerage);
    const brokerageGbb = fmt(hireGbb * brokerage);

    const netHire = fmt(totalGrossHire + vesselAdCom + brokerageAmount);
    const netHireGbb = fmt(hireGbb + vesselAdComGbb + brokerageGbb);
    const totalNetHireExpense = fmt(netHire + netHireGbb - totalDeductionsIn);

    const grossHireAmount = fmt(totalGrossHire + hireGbb);
    const deduction = fmt(totalDeductionsIn);
    return {
      totalNetHireExpense: fmt(totalNetHireExpense),
      grossHireAmount: fmt(grossHireAmount),
      adCom: fmt(vesselAdCom + vesselAdComGbb),
      brokerageExp: fmt(brokerageGbb + brokerageAmount),
      deduction: fmt(deduction),
      totalEcaLS: fmt(totalEcaLS),
      totalNormalHS: fmt(totalNormalHS),
      totalHSFO: fmt(totalHSFO),
      totalLSFO: fmt(totalLSFO),
      totalMGO: fmt(totalMGO),
      travelDetails: travelData,
      totalBallast: fmt(totalBallast),
      totalLaden: fmt(totalLaden),

      totalBallastSeaday: fmt(totalBallastSeaday),
      totalBallastHSFO: fmt(totalBallastHSFO),
      totalBallastLSFO: fmt(totalBallastLSFO),
      totalBallastMGO: fmt(totalBallastMGO),
      totalBallastStay: fmt(totalBallastStay),
      totalBallastStayHSFO: fmt(totalBallastStayHSFO),
      totalBallastStayLSFO: fmt(totalBallastStayLSFO),
      totalBallastStayMGO: fmt(totalBallastStayMGO),

      totalLadenSeaday: fmt(totalLadenSeaday),
      totalLadenHSFO: fmt(totalLadenHSFO),
      totalLadenLSFO: fmt(totalLadenLSFO),
      totalLadenMGO: fmt(totalLadenMGO),
      totalLadenStay: fmt(totalLadenStay),
      totalLadenStayHSFO: fmt(totalLadenStayHSFO),
      totalLadenStayLSFO: fmt(totalLadenStayLSFO),
      totalLadenStayMGO: fmt(totalLadenStayMGO),

      totalStay: fmt(totalNormalHS + totalEcaLS || 0),
      totalTravelDays: fmt(totalBallastSeaday + totalLadenSeaday || 0),
      totalStayHSFO: fmt(totalStayHSFO),
      totalStayLSFO: fmt(totalStayLSFO),
      totalStayMGO: fmt(totalStayMGO),
    };
  };

  const calculateExpensesOut = async () => {
    const {
      hireGbb = 0,
      hirePerDay = 0,
      splitHire = "",
    } = vesselContractDetails?.vesselOutContract?.vesselDetails || {};
    let { tcAdcom = 0, brokerage = 0 } =
      vesselContractDetails?.vesselOutContract?.vesselDetails || {};
    brokerage = Number(brokerage) / 100 || 0;
    const cargoList = contractCargoListOut();
    const splitHireData = parseSplitHire(splitHire);
    // Vessel-Out hire runs Delivery-out to Redelivery-in when both rows exist; otherwise to
    // Redelivery-out, or to Redelivery-in when there is no Redelivery-out row.
    const OffhireDuration = vesselOutRange()
      ? contractHireDays(cargoList, "Redelivery-in")
      : contractHireDays(
        contractCargoList(),
        contractCargoList().some((item) => item.operation === "Redelivery-out")
          ? "Redelivery-out"
          : "Redelivery-in",
      );
    const result = [];
    let currentDayRemain = OffhireDuration;
    const format = "YYYY-MM-DD HH:mm:ss.S";

    for (let i = 0; i < splitHireData?.length; i++) {
      const item = splitHireData[i];
      const NoDays = Math.max(0, Number(item.NoDays) || 0);
      const hireRate = Number(item.hireRate) || 0;
      const DayApplied = Math.min(currentDayRemain, NoDays);
      const DayRemain = currentDayRemain - DayApplied;
      const Hire = hireRate * DayApplied;
      currentDayRemain = DayRemain;
      result.push({
        DayRemain,
        DayApplied,
        Hire,
      });
    }

    const travelData = [];
    let totalTravelConsumption = 0;
    let totalSeaDays = 0;
    let totalHSFO = 0;
    let totalLSFO = 0;
    let totalMGO = 0;
    let totalNormalHS = 0;
    let totalEcaLS = 0;
    let totalStayHSFO = 0;
    let totalStayLSFO = 0;
    let totalStayMGO = 0;

    let totalBallastSeaday = 0;
    let totalBallastHSFO = 0;
    let totalBallastLSFO = 0;
    let totalBallastMGO = 0;
    let totalBallastStay = 0;
    let totalBallastStayHSFO = 0;
    let totalBallastStayLSFO = 0;
    let totalBallastStayMGO = 0;
    let totalBallast = 0;
    let totalLaden = 0;
    let totalLadenSeaday = 0;
    let totalLadenHSFO = 0;
    let totalLadenLSFO = 0;
    let totalLadenMGO = 0;
    let totalLadenStay = 0;
    let totalLadenStayHSFO = 0;
    let totalLadenStayLSFO = 0;
    let totalLadenStayMGO = 0;

    if (cargoList.length > 0) {
      for (let j = 0; j < cargoList.length - 1; j++) {
        const currentItem = cargoList[j];
        const nextItem = cargoList[j + 1] || {};
        const nextCargoDetails = nextItem?.cargoDetails || {};

        const currentTimeZone = Number(currentItem?.timeZone) || 0;
        const currentDepartureTimeLT = currentItem?.departureTime;
        const nextTimeZone = Number(nextItem?.timeZone) || 0;
        const nextArrivalTimeLT = nextItem?.arrivalTime;
        const nextDepartureTimeLT = nextItem?.departureTime;

        const currentDepartureAdjusted = parseOpsDate(
          currentDepartureTimeLT,
        ).isValid()
          ? parseOpsDate(currentDepartureTimeLT).subtract(
            currentTimeZone,
            "hours",
          )
          : moment.invalid();

        const nextArrivalAdjusted = parseOpsDate(nextArrivalTimeLT).isValid()
          ? parseOpsDate(nextArrivalTimeLT).subtract(nextTimeZone, "hours")
          : moment.invalid();

        const travelDays =
          currentDepartureAdjusted.isValid() && nextArrivalAdjusted.isValid()
            ? nextArrivalAdjusted.diff(currentDepartureAdjusted, "days", true)
            : 0;

        const currentHSFO =
          (Number(currentItem?.departureHsfo) || 0) -
          (Number(nextItem?.arrivalHsfo) || 0);
        const currentLSFO =
          (Number(currentItem?.departureLsfo) || 0) -
          (Number(nextItem?.arrivalLsfo) || 0);
        const currentMGO =
          (Number(currentItem?.departureMgo) || 0) -
          (Number(nextItem?.arrivalMgo) || 0);

        const stayHSFO =
          (Number(nextCargoDetails?.bunkerSuppliedHSFO) || 0) +
          (Number(nextItem?.arrivalHsfo) || 0) -
          (Number(nextItem?.departureHsfo) || 0);

        const stayLSFO =
          (Number(nextCargoDetails?.bunkerSuppliedLSFO) || 0) +
          (Number(nextItem?.arrivalLsfo) || 0) -
          (Number(nextItem?.departureLsfo) || 0);

        const stayMGO =
          (Number(nextCargoDetails?.bunkerSuppliedMGO) || 0) +
          (Number(nextItem?.arrivalMgo) || 0) -
          (Number(nextItem?.departureMgo) || 0);

        const arrival = parseOpsDate(nextArrivalTimeLT);
        const departure = parseOpsDate(nextDepartureTimeLT);
        let totalStay = 0;

        if (arrival.isValid() && departure.isValid()) {
          totalStay = departure.diff(arrival, "days", true);
          totalStay = Math.max(Number(totalStay.toFixed(3)), 0);
        }

        let normalHS = 0;
        let ecaLS = 0;
        const cargoItem = cargoList?.[j + 1];

        if (cargoItem?.sulfur !== "ECA") {
          normalHS += totalStay;
        } else if (cargoItem?.sulfur === "ECA") {
          ecaLS += totalStay;
        }
        if (cargoItem?.lbmarker?.toLowerCase() === "ballast") {
          totalBallast += travelDays;
        } else {
          totalLaden += travelDays;
        }

        const isBallast = cargoItem?.lbmarker?.toLowerCase() === "ballast";

        if (isBallast) {
          totalBallastSeaday += travelDays;
          totalBallastHSFO += currentHSFO;
          totalBallastLSFO += currentLSFO;
          totalBallastMGO += currentMGO;
          totalBallastStay += totalStay;
          totalBallastStayHSFO += stayHSFO;
          totalBallastStayLSFO += stayLSFO;
          totalBallastStayMGO += stayMGO;
        } else {
          totalLadenSeaday += travelDays;
          totalLadenHSFO += currentHSFO;
          totalLadenLSFO += currentLSFO;
          totalLadenMGO += currentMGO;
          totalLadenStay += totalStay;
          totalLadenStayHSFO += stayHSFO;
          totalLadenStayLSFO += stayLSFO;
          totalLadenStayMGO += stayMGO;
        }

        travelData.push({
          fromIndex: j,
          toIndex: j + 1,
          port: cargoItem?.port,
          travelDays: Math.max(0, travelDays),
          currentDeparture: currentDepartureAdjusted.isValid()
            ? currentDepartureAdjusted.format(format)
            : "",
          nextArrival: nextArrivalAdjusted.isValid()
            ? nextArrivalAdjusted.format(format)
            : "",
          consumption: Number(currentItem?.consumption) || 0,
          seaDays: Number(currentItem?.seaDays) || 0,
          HSFO: currentHSFO,
          LSFO: currentLSFO,
          MGO: currentMGO,
          stayHSFO,
          stayLSFO,
          stayMGO,
          totalStay,
        });

        totalTravelConsumption += Number(currentItem?.consumption) || 0;
        totalSeaDays += Number(currentItem?.seaDays) || 0;
        totalHSFO += currentHSFO;
        totalLSFO += currentLSFO;
        totalMGO += currentMGO;
        totalNormalHS += normalHS;
        totalEcaLS += ecaLS;
        totalStayHSFO += stayHSFO;
        totalStayLSFO += stayLSFO;
        totalStayMGO += stayMGO;
      }
    }

    const fmt = (num) => Number(Number(num).toFixed(3)) || 0;

    const baseHire = fmt(result.reduce((sum, { Hire = 0 }) => sum + Hire, 0));
    const totalGrossHire = fmt(baseHire + hirePerDay * currentDayRemain);
    const totalGrossHireGbb = fmt(hireGbb);

    const commissionRate = tcAdcom / 100;
    const vesselAdCom = fmt(-1 * totalGrossHire * commissionRate);
    const vesselAdComGbb = fmt(-1 * hireGbb * commissionRate);
    // Excel: brokerage is payable and is added to the hire expense (TC Adcom is deducted)
    const brokerageAmount = fmt(totalGrossHire * brokerage);
    const brokerageGbb = fmt(totalGrossHireGbb * brokerage);

    const netHire = fmt(totalGrossHire + vesselAdCom + brokerageAmount);
    const netHireGbb = fmt(hireGbb + vesselAdComGbb + brokerageGbb);
    const totalNetHireExpense = fmt(netHire + netHireGbb - totalDeductionsOut);

    const grossHireAmount = fmt(totalGrossHire + hireGbb);
    const deduction = fmt(totalDeductionsOut);

    return {
      totalNetHireExpense: fmt(totalNetHireExpense),
      grossHireAmount: fmt(grossHireAmount),
      adCom: fmt(vesselAdCom + vesselAdComGbb),
      brokerageExp: fmt(brokerageGbb + brokerageAmount),
      deduction: fmt(deduction),
      totalEcaLS: fmt(totalEcaLS),
      totalNormalHS: fmt(totalNormalHS),
      totalHSFO: fmt(totalHSFO),
      totalLSFO: fmt(totalLSFO),
      totalMGO: fmt(totalMGO),
      travelDetails: travelData,
      totalBallast: fmt(totalBallast),
      totalLaden: fmt(totalLaden),

      totalBallastSeaday: fmt(totalBallastSeaday),
      totalBallastHSFO: fmt(totalBallastHSFO),
      totalBallastLSFO: fmt(totalBallastLSFO),
      totalBallastMGO: fmt(totalBallastMGO),
      totalBallastStay: fmt(totalBallastStay),
      totalBallastStayHSFO: fmt(totalBallastStayHSFO),
      totalBallastStayLSFO: fmt(totalBallastStayLSFO),
      totalBallastStayMGO: fmt(totalBallastStayMGO),

      totalLadenSeaday: fmt(totalLadenSeaday),
      totalLadenHSFO: fmt(totalLadenHSFO),
      totalLadenLSFO: fmt(totalLadenLSFO),
      totalLadenMGO: fmt(totalLadenMGO),
      totalLadenStay: fmt(totalLadenStay),
      totalLadenStayHSFO: fmt(totalLadenStayHSFO),
      totalLadenStayLSFO: fmt(totalLadenStayLSFO),
      totalLadenStayMGO: fmt(totalLadenStayMGO),

      totalStay: fmt(totalNormalHS + totalEcaLS || 0),
      totalTravelDays: fmt(totalBallastSeaday + totalLadenSeaday || 0),
      totalStayHSFO: fmt(totalStayHSFO),
      totalStayLSFO: fmt(totalStayLSFO),
      totalStayMGO: fmt(totalStayMGO),
    };
  };

  const calculateFuelExpense = async (
    calculatedExpenses,
    calculatedRevenue,
    contractSide = "out",
  ) => {
    const safeNum = (val) => Number(val) || 0;

    const fmt = (num) => {
      const n = safeNum(num);
      return Number(n.toFixed(3));
    };

    const {
      totalStayHSFO = 0,
      totalHSFO = 0,
      totalStayLSFO = 0,
      totalLSFO = 0,
      totalStayMGO = 0,
      totalMGO = 0,
    } = calculatedExpenses || {};

    const vesselOutDetails =
      vesselContractDetails?.vesselOutContract?.vesselDetails || {};
    const bnkDelhs = safeNum(vesselOutDetails.bnkDelhs);
    const bnkDells = safeNum(vesselOutDetails.bnkDells);
    const bnkDelmgo = safeNum(vesselOutDetails.bnkDelmgo);
    const vesselOutbnkPriceDeliveryhs = safeNum(
      vesselOutDetails.bnkPriceDeliveryhs,
    );
    const vesselOutbnkPriceDeliveryls = safeNum(
      vesselOutDetails.bnkPriceDeliveryls,
    );
    const vesselOutbnkPriceDeliverymgo = safeNum(
      vesselOutDetails.bnkPriceDeliverymgo,
    );
    const vesselOutbnkQtyRedeliveryhs = safeNum(
      vesselOutDetails.bnkQtyRedeliveryhs,
    );
    const vesselOutbnkQtyRedeliveryls = safeNum(
      vesselOutDetails.bnkQtyRedeliveryls,
    );
    const vesselOutbnkQtyRedeliverymgo = safeNum(
      vesselOutDetails.bnkQtyRedeliverymgo,
    );
    const vesselOutbnkRedeliveryQtyTolerancehs = safeNum(
      vesselOutDetails.bnkRedeliveryQtyTolerancehs,
    );
    const vesselOutbnkRedeliveryQtyTolerancels = safeNum(
      vesselOutDetails.bnkRedeliveryQtyTolerancels,
    );
    const vesselOutbnkRedeliveryQtyTolerancemgo = safeNum(
      vesselOutDetails.bnkRedeliveryQtyTolerancemgo,
    );
    const vesselOutbnkPriceRedeliveryhs = safeNum(
      vesselOutDetails.bnkPriceRedeliveryhs,
    );
    const vesselOutbnkPriceRedeliveryls = safeNum(
      vesselOutDetails.bnkPriceRedeliveryls,
    );
    const vesselOutbnkPriceRedeliverymgo = safeNum(
      vesselOutDetails.bnkPriceRedeliverymgo,
    );
    const vesselOutbnkPriceSettlementmgo = safeNum(
      vesselOutDetails.bnkPriceSettlementmgo,
    );
    const vesselOutbnkPriceSettlemenths = safeNum(
      vesselOutDetails.bnkPriceSettlemenths,
    );
    const vesselOutbnkPriceSettlementls = safeNum(
      vesselOutDetails.bnkPriceSettlementls,
    );

    const vesselInDetails = vesselContractDetails?.vesselDetails || {};
    const selectedVesselDetails =
      contractSide === "in" ? vesselInDetails : vesselOutDetails;
    const vesselInDelhs = safeNum(vesselInDetails.bnkDelhs);
    const vesselInDells = safeNum(vesselInDetails.bnkDells);
    const vesselInDelmgo = safeNum(vesselInDetails.bnkDelmgo);
    const bnkPriceDeliveryhs = safeNum(vesselInDetails.bnkPriceDeliveryhs);
    const bnkPriceDeliveryls = safeNum(vesselInDetails.bnkPriceDeliveryls);
    const bnkPriceDeliverymgo = safeNum(vesselInDetails.bnkPriceDeliverymgo);
    const bnkQtyRedeliveryhs = safeNum(vesselInDetails.bnkQtyRedeliveryhs);
    const bnkQtyRedeliveryls = safeNum(vesselInDetails.bnkQtyRedeliveryls);
    const bnkQtyRedeliverymgo = safeNum(vesselInDetails.bnkQtyRedeliverymgo);
    const bnkRedeliveryQtyTolerancehs = safeNum(
      vesselInDetails.bnkRedeliveryQtyTolerancehs,
    );
    const bnkRedeliveryQtyTolerancels = safeNum(
      vesselInDetails.bnkRedeliveryQtyTolerancels,
    );
    const bnkRedeliveryQtyTolerancemgo = safeNum(
      vesselInDetails.bnkRedeliveryQtyTolerancemgo,
    );
    const bnkPriceRedeliveryhs = safeNum(vesselInDetails.bnkPriceRedeliveryhs);
    const bnkPriceRedeliveryls = safeNum(vesselInDetails.bnkPriceRedeliveryls);
    const bnkPriceRedeliverymgo = safeNum(
      vesselInDetails.bnkPriceRedeliverymgo,
    );
    const bnkPriceSettlementmgo = safeNum(
      vesselInDetails.bnkPriceSettlementmgo,
    );
    const bnkPriceSettlemenths = safeNum(vesselInDetails.bnkPriceSettlemenths);
    const bnkPriceSettlementls = safeNum(vesselInDetails.bnkPriceSettlementls);

    const redeliveryIn =
      cargoOperationTableData?.cargoList?.find(
        (item) => item.operation === "Redelivery-in",
      ) || {};
    const redeliveryOut =
      cargoOperationTableData?.cargoList?.find(
        (item) => item.operation === "Redelivery-out",
      ) || {};
    // no Redelivery-in row means nothing was redelivered: show 0, not a penalty on a 0 quantity
    const hasRedeliveryIn = !!cargoOperationTableData?.cargoList?.some(
      (item) => item.operation === "Redelivery-in",
    );
    const redeliveryInMgo = safeNum(redeliveryIn.departureMgo);
    const redeliveryInLsfo = safeNum(redeliveryIn.departureLsfo);
    const redeliveryInHsfo = safeNum(redeliveryIn.departureHsfo);
    const departureMgo = safeNum(redeliveryOut.departureMgo);
    const departureLsfo = safeNum(redeliveryOut.departureLsfo);
    const departureHsfo = safeNum(redeliveryOut.departureHsfo);

    const fuelExpenseData = fuelExpenseDataRef.current || {};
    const {
      hsfoShortfall,
      lsfoShortfall,
      mgoShortfall,
      hsfoExcess,
      lsfoExcess,
      mgoExcess,
      fuelMethodHsfo = "Average",
      fuelMethodLsfo = "LIFO",
      fuelMethodMgo = "Average",
      shortagePriceHsfo,
      shortagePriceLsfo,
      shortagePriceMgo,
      fuelHandOverPriceHsfo,
      fuelHandOverPriceLsfo,
      fuelHandOverPriceMgo,
    } = fuelExpenseData;

    const fuelTypes = ["Hsfo", "Lsfo", "Mgo"];
    const fuelData = {};

    const sumProperty = (property) => {
      if (!calculatedRevenue || !Array.isArray(calculatedRevenue)) return 0;
      return calculatedRevenue.reduce(
        (sum, item) => sum + safeNum(item[property]),
        0,
      );
    };
    const tripSupplyQtyHsfo = sumProperty("tsfHsfo");
    const tripSupplyQtyLsfo = sumProperty("tsfLsfo");
    const tripSupplyQtyMgo = sumProperty("tsfMgo");

    const reserves = (calculatedRevenue || []).reduce(
      (acc, item) => {
        if (item.fqaType === "Owner") {
          acc.internal.hsfo.qty += safeNum(item.fqaHsfo);
          acc.internal.lsfo.qty += safeNum(item.fqaLsfo);
          acc.internal.mgo.qty += safeNum(item.fqaMgo);
          acc.internal.hsfo.cost += safeNum(item.fcaHsfo);
          acc.internal.lsfo.cost += safeNum(item.fcaLsfo);
          acc.internal.mgo.cost += safeNum(item.fcaMgo);
        } else {
          acc.external.hsfo += safeNum(item.fqaHsfo);
          acc.external.lsfo += safeNum(item.fqaLsfo);
          acc.external.mgo += safeNum(item.fqaMgo);
        }
        return acc;
      },
      {
        internal: {
          hsfo: {
            qty: 0,
            cost: 0,
          },
          lsfo: {
            qty: 0,
            cost: 0,
          },
          mgo: {
            qty: 0,
            cost: 0,
          },
        },
        external: {
          hsfo: 0,
          lsfo: 0,
          mgo: 0,
        },
      },
    );
    const reserveInternalQtyHsfo = reserves.internal.hsfo.qty;
    const reserveInternalQtyLsfo = reserves.internal.lsfo.qty;
    const reserveInternalQtyMgo = reserves.internal.mgo.qty;

    fuelTypes.forEach((fuel) => {
      const lower = fuel.toLowerCase();
      const subFuel = lower === "hsfo" ? "hs" : lower === "lsfo" ? "ls" : "mgo";
      const reserveInternal = reserves.internal[lower];

      fuelData[`tripSupplyQty${fuel}`] = sumProperty(`tsf${fuel}`);
      fuelData[`tripSupplyCost${fuel}`] = sumProperty(`avgFualCost${fuel}`);

      fuelData[`averagePricePMT${fuel}`] = fuelData[`tripSupplyQty${fuel}`]
        ? fuelData[`tripSupplyCost${fuel}`] / fuelData[`tripSupplyQty${fuel}`]
        : 0;

      const consumptionRaw =
        fuel === "Hsfo"
          ? safeNum(totalStayHSFO) + safeNum(totalHSFO)
          : fuel === "Lsfo"
            ? safeNum(totalStayLSFO) + safeNum(totalLSFO)
            : safeNum(totalStayMGO) + safeNum(totalMGO);

      fuelData[`voyageConsumptionQty${fuel}`] = fmt(consumptionRaw);

      fuelData[`appliedQuantity${fuel}`] = Math.min(
        fuelData[`tripSupplyQty${fuel}`],
        fuelData[`voyageConsumptionQty${fuel}`],
      );

      const lifoData = {
        remaining: sumProperty(`lifoQtyRemain${fuel}`),
        applied: sumProperty(`lifoQtyApplied${fuel}`),
        totalCost: sumProperty(`lifoFuelCost${fuel}`),
      };

      const fifoData = {
        remaining: sumProperty(`fifoQtyRemain${fuel}`),
        applied: sumProperty(`fifoQtyApplied${fuel}`),
        totalCost: sumProperty(`fifoFuelCost${fuel}`),
      };

      fuelData[`appliedCostLIFO${fuel}`] = fmt(lifoData.totalCost);
      fuelData[`appliedCostFIFO${fuel}`] = fmt(fifoData.totalCost);
      fuelData[`appliedCostAverage${fuel}`] = fmt(
        fuelData[`averagePricePMT${fuel}`] * fuelData[`appliedQuantity${fuel}`],
      );

      const method =
        fuel === "Hsfo"
          ? fuelMethodHsfo
          : fuel === "Lsfo"
            ? fuelMethodLsfo
            : fuelMethodMgo;

      fuelData[`selectedAppliedCost${fuel}`] =
        method === "LIFO"
          ? fuelData[`appliedCostLIFO${fuel}`]
          : method === "FIFO"
            ? fuelData[`appliedCostFIFO${fuel}`]
            : fuelData[`appliedCostAverage${fuel}`];

      const diff =
        fuelData[`tripSupplyQty${fuel}`] -
        fuelData[`voyageConsumptionQty${fuel}`];
      fuelData[`shortageQty${fuel}`] = fmt(diff < 0 ? Math.abs(diff) : 0);
      fuelData[`excessQty${fuel}`] = fmt(diff > 0 ? diff : 0);

      const selectedTolerance = safeNum(
        selectedVesselDetails[`bnkRedeliveryQtyTolerance${subFuel}`],
      );

      fuelData[`redeliveryShortagePriceQty${fuel}`] =
        fuelData[`shortageQty${fuel}`] < selectedTolerance
          ? fuelData[`shortageQty${fuel}`]
          : selectedTolerance;

      fuelData[`settlementShortagePriceQty${fuel}`] = fmt(
        fuelData[`shortageQty${fuel}`] -
        fuelData[`redeliveryShortagePriceQty${fuel}`],
      );

      fuelData[`withIntoleranceShortageCost${fuel}`] = fmt(
        fuelData[`redeliveryShortagePriceQty${fuel}`] *
        safeNum(selectedVesselDetails[`bnkPriceRedelivery${subFuel}`]),
      );

      fuelData[`outSidetoleranceShortageCost${fuel}`] = fmt(
        fuelData[`settlementShortagePriceQty${fuel}`] *
        safeNum(selectedVesselDetails[`bnkPriceSettlement${subFuel}`]),
      );

      fuelData[`redeliveryExcessPriceQty${fuel}`] =
        fuelData[`excessQty${fuel}`] < selectedTolerance
          ? fuelData[`excessQty${fuel}`]
          : selectedTolerance;

      const shortfallRate =
        fuel === "Hsfo"
          ? hsfoShortfall
          : fuel === "Lsfo"
            ? lsfoShortfall
            : mgoShortfall;

      fuelData[`settlementExcessPriceQty${fuel}`] = fmt(
        fuelData[`excessQty${fuel}`] -
        fuelData[`redeliveryExcessPriceQty${fuel}`],
      );

      fuelData[`withIntoleranceExcessRevenue${fuel}`] = fmt(
        fuelData[`redeliveryExcessPriceQty${fuel}`] *
        safeNum(selectedVesselDetails[`bnkPriceRedelivery${subFuel}`]),
      );

      fuelData[`outSidetoleranceExcessRevenue${fuel}`] = fmt(
        fuelData[`settlementExcessPriceQty${fuel}`] *
        safeNum(selectedVesselDetails[`bnkPriceSettlement${subFuel}`]),
      );

      fuelData[`voyageFuelExpenditure${fuel}`] =
        fuelSettlementMode === "relay"
          ? fmt(
            fuelData[`selectedAppliedCost${fuel}`] +
            (shortfallRate
              ? safeNum(shortfallRate) * fuelData[`shortageQty${fuel}`]
              : 0),
          )
          : fmt(
            fuelData[`selectedAppliedCost${fuel}`] +
            fuelData[`withIntoleranceShortageCost${fuel}`] -
            fuelData[`outSidetoleranceShortageCost${fuel}`],
          );

      fuelData[`costExcess${fuel}`] = fmt(
        fuelData[`tripSupplyCost${fuel}`] +
        reserveInternal.cost +
        fuelData[`voyageFuelExpenditure${fuel}`],
      );

      fuelData[`expenditureforShortage${fuel}`] = shortfallRate
        ? fmt(safeNum(shortfallRate) * fuelData[`shortageQty${fuel}`])
        : 0;

      fuelData[`voyagePricePMT${fuel}`] = fmt(
        fuelData[`voyageConsumptionQty${fuel}`]
          ? fuelData[`voyageFuelExpenditure${fuel}`] /
          fuelData[`voyageConsumptionQty${fuel}`]
          : 0,
      );

      fuelData[`excessCost${fuel}`] = fmt(
        fuelData[`tripSupplyQty${fuel}`] -
        fuelData[`selectedAppliedCost${fuel}`],
      );

      fuelData[`qtyBalanceOnboard${fuel}`] = fmt(
        reserveInternal.qty +
        fuelData[`tripSupplyQty${fuel}`] -
        fuelData[`voyageConsumptionQty${fuel}`],
      );

      const excessRate =
        fuel === "Hsfo" ? hsfoExcess : fuel === "Lsfo" ? lsfoExcess : mgoExcess;
      fuelData[`revenueExcess${fuel}`] = fmt(
        safeNum(excessRate) * fuelData[`qtyBalanceOnboard${fuel}`],
      );

      fuelData[`fuelSettlementProfit${fuel}`] =
        fuelSettlementMode === "relay"
          ? fmt(
            fuelData[`revenueExcess${fuel}`] - fuelData[`excessCost${fuel}`],
          )
          : fmt(
            fuelData[`withIntoleranceExcessRevenue${fuel}`] +
            fuelData[`outSidetoleranceExcessRevenue${fuel}`] -
            fuelData[`costExcess${fuel}`],
          );

      fuelData[`deliveryPrices${fuel}`] =
        fuel === "Hsfo"
          ? hsfoShortfall
          : fuel === "Lsfo"
            ? lsfoShortfall
            : mgoShortfall;
    });

    const calcShortage = (actual, contractQty, tolerance) =>
      actual < contractQty - tolerance ? contractQty - tolerance - actual : 0;

    const calcValueOnRedel = (
      actual,
      contractQty,
      tolerance,
      redelPrice,
      settlementPrice,
    ) => {
      const maxAcceptable = contractQty + tolerance;
      return (
        Math.min(actual, maxAcceptable) * redelPrice +
        Math.max(actual - maxAcceptable, 0) * settlementPrice
      );
    };

    const valueOnRedelHsfo = calcValueOnRedel(
      departureHsfo,
      vesselOutbnkQtyRedeliveryhs,
      vesselOutbnkRedeliveryQtyTolerancehs,
      vesselOutbnkPriceRedeliveryhs,
      vesselOutbnkPriceSettlemenths,
    );
    const valueOnRedelLsfo = calcValueOnRedel(
      departureLsfo,
      vesselOutbnkQtyRedeliveryls,
      vesselOutbnkRedeliveryQtyTolerancels,
      vesselOutbnkPriceRedeliveryls,
      vesselOutbnkPriceSettlementls,
    );
    const valueOnRedelMgo = calcValueOnRedel(
      departureMgo,
      vesselOutbnkQtyRedeliverymgo,
      vesselOutbnkRedeliveryQtyTolerancemgo,
      vesselOutbnkPriceRedeliverymgo,
      vesselOutbnkPriceSettlementmgo,
    );

    const shortageQuantityHsfo = calcShortage(
      departureHsfo,
      vesselOutbnkQtyRedeliveryhs,
      vesselOutbnkRedeliveryQtyTolerancehs,
    );
    const shortageQuantityLsfo = calcShortage(
      departureLsfo,
      vesselOutbnkQtyRedeliveryls,
      vesselOutbnkRedeliveryQtyTolerancels,
    );
    const shortageQuantityMgo = calcShortage(
      departureMgo,
      vesselOutbnkQtyRedeliverymgo,
      vesselOutbnkRedeliveryQtyTolerancemgo,
    );

    const shortagePenaltyHsfo =
      (vesselOutbnkPriceSettlemenths - vesselOutbnkPriceRedeliveryhs) *
      shortageQuantityHsfo;
    const shortagePenaltyLsfo =
      (vesselOutbnkPriceSettlementls - vesselOutbnkPriceRedeliveryls) *
      shortageQuantityLsfo;
    const shortagePenaltyMgo =
      (vesselOutbnkPriceSettlementmgo - vesselOutbnkPriceRedeliverymgo) *
      shortageQuantityMgo;

    const vesselOutExtraTable = {
      vesselOnDeliveryHsfo: bnkDelhs * vesselOutbnkPriceDeliveryhs,
      vesselOnDeliveryLsfo: bnkDells * vesselOutbnkPriceDeliveryls,
      vesselOnDeliveryMgo: bnkDelmgo * vesselOutbnkPriceDeliverymgo,
      actualRedelQuantityHsfo: departureHsfo,
      actualRedelQuantityLsfo: departureLsfo,
      actualRedelQuantityMgo: departureMgo,
      minRedelAcceptableHsfo:
        vesselOutbnkQtyRedeliveryhs - vesselOutbnkRedeliveryQtyTolerancehs,
      minRedelAcceptableLsfo:
        vesselOutbnkQtyRedeliveryls - vesselOutbnkRedeliveryQtyTolerancels,
      minRedelAcceptableMgo:
        vesselOutbnkQtyRedeliverymgo - vesselOutbnkRedeliveryQtyTolerancemgo,
      maxRedelAcceptableHsfo:
        vesselOutbnkQtyRedeliveryhs + vesselOutbnkRedeliveryQtyTolerancehs,
      maxRedelAcceptableLsfo:
        vesselOutbnkQtyRedeliveryls + vesselOutbnkRedeliveryQtyTolerancels,
      maxRedelAcceptableMgo:
        vesselOutbnkQtyRedeliverymgo + vesselOutbnkRedeliveryQtyTolerancemgo,
      shortageQuantityHsfo,
      shortageQuantityLsfo,
      shortageQuantityMgo,
      shortagePenaltyHsfo,
      shortagePenaltyLsfo,
      shortagePenaltyMgo,
      valueOnRedelHsfo,
      valueOnRedelLsfo,
      valueOnRedelMgo,
      effectiveRedelValueHsfo: valueOnRedelHsfo - shortagePenaltyHsfo,
      effectiveRedelValueLsfo: valueOnRedelLsfo - shortagePenaltyLsfo,
      effectiveRedelValueMgo: valueOnRedelMgo - shortagePenaltyMgo,
    };

    const valueOnRedelHsfoIn = calcValueOnRedel(
      redeliveryInHsfo,
      bnkQtyRedeliveryhs,
      bnkRedeliveryQtyTolerancehs,
      bnkPriceRedeliveryhs,
      bnkPriceSettlemenths,
    );
    const valueOnRedelLsfoIn = calcValueOnRedel(
      redeliveryInLsfo,
      bnkQtyRedeliveryls,
      bnkRedeliveryQtyTolerancels,
      bnkPriceRedeliveryls,
      bnkPriceSettlementls,
    );
    const valueOnRedelMgoIn = calcValueOnRedel(
      redeliveryInMgo,
      bnkQtyRedeliverymgo,
      bnkRedeliveryQtyTolerancemgo,
      bnkPriceRedeliverymgo,
      bnkPriceSettlementmgo,
    );

    const shortageQuantityHsfoIn = calcShortage(
      redeliveryInHsfo,
      bnkQtyRedeliveryhs,
      bnkRedeliveryQtyTolerancehs,
    );
    const shortageQuantityLsfoIn = calcShortage(
      redeliveryInLsfo,
      bnkQtyRedeliveryls,
      bnkRedeliveryQtyTolerancels,
    );
    const shortageQuantityMgoIn = calcShortage(
      redeliveryInMgo,
      bnkQtyRedeliverymgo,
      bnkRedeliveryQtyTolerancemgo,
    );

    const shortagePenaltyHsfoIn =
      (bnkPriceSettlemenths - bnkPriceRedeliveryhs) * shortageQuantityHsfoIn;
    const shortagePenaltyLsfoIn =
      (bnkPriceSettlementls - bnkPriceRedeliveryls) * shortageQuantityLsfoIn;
    const shortagePenaltyMgoIn =
      (bnkPriceSettlementmgo - bnkPriceRedeliverymgo) * shortageQuantityMgoIn;

    const vesselInExtraTable = {
      vesselOnDeliveryHsfo: vesselInDelhs * bnkPriceDeliveryhs,
      vesselOnDeliveryLsfo: vesselInDells * bnkPriceDeliveryls,
      vesselOnDeliveryMgo: vesselInDelmgo * bnkPriceDeliverymgo,
      actualRedelQuantityHsfo: redeliveryInHsfo,
      actualRedelQuantityLsfo: redeliveryInLsfo,
      actualRedelQuantityMgo: redeliveryInMgo,
      minRedelAcceptableHsfo: bnkQtyRedeliveryhs - bnkRedeliveryQtyTolerancehs,
      minRedelAcceptableLsfo: bnkQtyRedeliveryls - bnkRedeliveryQtyTolerancels,
      minRedelAcceptableMgo: bnkQtyRedeliverymgo - bnkRedeliveryQtyTolerancemgo,
      maxRedelAcceptableHsfo: bnkQtyRedeliveryhs + bnkRedeliveryQtyTolerancehs,
      maxRedelAcceptableLsfo: bnkQtyRedeliveryls + bnkRedeliveryQtyTolerancels,
      maxRedelAcceptableMgo: bnkQtyRedeliverymgo + bnkRedeliveryQtyTolerancemgo,
      shortageQuantityHsfo: shortageQuantityHsfoIn,
      shortageQuantityLsfo: shortageQuantityLsfoIn,
      shortageQuantityMgo: shortageQuantityMgoIn,
      shortagePenaltyHsfo: shortagePenaltyHsfoIn,
      shortagePenaltyLsfo: shortagePenaltyLsfoIn,
      shortagePenaltyMgo: shortagePenaltyMgoIn,
      valueOnRedelHsfo: valueOnRedelHsfoIn,
      valueOnRedelLsfo: valueOnRedelLsfoIn,
      valueOnRedelMgo: valueOnRedelMgoIn,
      effectiveRedelValueHsfo: valueOnRedelHsfoIn - shortagePenaltyHsfoIn,
      effectiveRedelValueLsfo: valueOnRedelLsfoIn - shortagePenaltyLsfoIn,
      effectiveRedelValueMgo: valueOnRedelMgoIn - shortagePenaltyMgoIn,
    };

    const costValue = {
      valueFuelDeliveryInHsfo: bnkPriceDeliveryhs * vesselInDelhs,
      valueFuelDeliveryInLsfo: bnkPriceDeliveryls * vesselInDells,
      valueFuelDeliveryInMgo: bnkPriceDeliverymgo * vesselInDelmgo,
      valueFuelDeliveryOutHsfo: vesselOutbnkPriceDeliveryhs * bnkDelhs,
      valueFuelDeliveryOutLsfo: vesselOutbnkPriceDeliveryls * bnkDells,
      valueFuelDeliveryOutMgo: vesselOutbnkPriceDeliverymgo * bnkDelmgo,
      valueFuelRedeliveryInHsfo: hasRedeliveryIn
        ? vesselInExtraTable.effectiveRedelValueHsfo
        : 0,
      valueFuelRedeliveryInLsfo: hasRedeliveryIn
        ? vesselInExtraTable.effectiveRedelValueLsfo
        : 0,
      valueFuelRedeliveryInMgo: hasRedeliveryIn
        ? vesselInExtraTable.effectiveRedelValueMgo
        : 0,
      valueFuelRedeliveryOutHsfo: vesselOutExtraTable.effectiveRedelValueHsfo,
      valueFuelRedeliveryOutLsfo: vesselOutExtraTable.effectiveRedelValueLsfo,
      valueFuelRedeliveryOutMgo: vesselOutExtraTable.effectiveRedelValueMgo,
      purchaseCostHsfo:
        vesselOutbnkPriceDeliveryhs * bnkDelhs -
        (vesselOutExtraTable.effectiveRedelValueHsfo || 0),
      purchaseCostLsfo:
        vesselOutbnkPriceDeliveryls * bnkDells -
        (vesselOutExtraTable.effectiveRedelValueLsfo || 0),
      purchaseCostMgo:
        vesselOutbnkPriceDeliverymgo * bnkDelmgo -
        (vesselOutExtraTable.effectiveRedelValueMgo || 0),
    };

    // Excel reconciliation ("our books"): Vessel-In side, once the operation has a Delivery-out
    // or Redelivery-in row; older operations keep the previous maths. A missing Delivery-out
    // falls back to the contract quantity; a missing Redelivery-in / Redelivery-out gives 0 (#N/A).
    const effectiveRedelValue = (actual, qty, tolerance, redelPrice, settlementPrice) =>
      calcValueOnRedel(actual, qty, tolerance, redelPrice, settlementPrice) -
      (settlementPrice - redelPrice) * calcShortage(actual, qty, tolerance);
    const allRows = cargoOperationTableData?.cargoList || [];
    const deliveryOutRow = allRows.find((item) => item.operation === "Delivery-out");
    const finalRedeliveryRow = allRows.find(
      (item) => item.operation === "Redelivery-out",
    );
    const inBooks =
      contractSide === "in" && (deliveryOutRow || hasRedeliveryIn)
        ? (() => {
          const outContract = {
            Hsfo: { del: bnkDelhs, delPrice: vesselOutbnkPriceDeliveryhs, qty: vesselOutbnkQtyRedeliveryhs, tol: vesselOutbnkRedeliveryQtyTolerancehs, redel: vesselOutbnkPriceRedeliveryhs, settle: vesselOutbnkPriceSettlemenths },
            Lsfo: { del: bnkDells, delPrice: vesselOutbnkPriceDeliveryls, qty: vesselOutbnkQtyRedeliveryls, tol: vesselOutbnkRedeliveryQtyTolerancels, redel: vesselOutbnkPriceRedeliveryls, settle: vesselOutbnkPriceSettlementls },
            Mgo: { del: bnkDelmgo, delPrice: vesselOutbnkPriceDeliverymgo, qty: vesselOutbnkQtyRedeliverymgo, tol: vesselOutbnkRedeliveryQtyTolerancemgo, redel: vesselOutbnkPriceRedeliverymgo, settle: vesselOutbnkPriceSettlementmgo },
          };
          const inContract = {
            Hsfo: { qty: bnkQtyRedeliveryhs, tol: bnkRedeliveryQtyTolerancehs, redel: bnkPriceRedeliveryhs, settle: bnkPriceSettlemenths },
            Lsfo: { qty: bnkQtyRedeliveryls, tol: bnkRedeliveryQtyTolerancels, redel: bnkPriceRedeliveryls, settle: bnkPriceSettlementls },
            Mgo: { qty: bnkQtyRedeliverymgo, tol: bnkRedeliveryQtyTolerancemgo, redel: bnkPriceRedeliverymgo, settle: bnkPriceSettlementmgo },
          };
          const books = {};
          fuelTypes.forEach((F) => {
            const o = outContract[F];
            const i = inContract[F];
            const delOutQty = deliveryOutRow
              ? safeNum(deliveryOutRow[`departure${F}`])
              : o.del;
            const redelInQty = safeNum(redeliveryIn[`departure${F}`]);
            const redelOutQty = finalRedeliveryRow
              ? safeNum(finalRedeliveryRow[`departure${F}`])
              : 0;
            books[F] = {
              delOutQty,
              delOutValue: delOutQty * o.delPrice,
              redelInQty,
              redelInValue: hasRedeliveryIn
                ? effectiveRedelValue(redelInQty, o.qty, o.tol, o.redel, o.settle)
                : 0,
              redelOutQty,
              redelOutValue: finalRedeliveryRow
                ? effectiveRedelValue(redelOutQty, i.qty, i.tol, i.redel, i.settle)
                : 0,
            };
          });
          return books;
        })()
        : null;

    const vesselOutFuelPurchaseHsfo = bnkDelhs - departureHsfo;
    const vesselOutFuelPurchaseLsfo = bnkDells - departureLsfo;
    const vesselOutFuelPurchaseMgo = bnkDelmgo - departureMgo;

    const selectedFuelPurchaseHsfo = inBooks
      ? inBooks.Hsfo.redelInQty - inBooks.Hsfo.delOutQty
      : contractSide === "in"
        ? vesselInDelhs - redeliveryInHsfo
        : vesselOutFuelPurchaseHsfo;
    const selectedFuelPurchaseLsfo = inBooks
      ? inBooks.Lsfo.redelInQty - inBooks.Lsfo.delOutQty
      : contractSide === "in"
        ? vesselInDells - redeliveryInLsfo
        : vesselOutFuelPurchaseLsfo;
    const selectedFuelPurchaseMgo = inBooks
      ? inBooks.Mgo.redelInQty - inBooks.Mgo.delOutQty
      : contractSide === "in"
        ? vesselInDelmgo - redeliveryInMgo
        : vesselOutFuelPurchaseMgo;
    const selectedPurchaseCostHsfo = inBooks
      ? inBooks.Hsfo.redelInValue - inBooks.Hsfo.delOutValue
      : contractSide === "in"
        ? costValue.valueFuelDeliveryInHsfo -
        costValue.valueFuelRedeliveryInHsfo
        : costValue.purchaseCostHsfo;
    const selectedPurchaseCostLsfo = inBooks
      ? inBooks.Lsfo.redelInValue - inBooks.Lsfo.delOutValue
      : contractSide === "in"
        ? costValue.valueFuelDeliveryInLsfo -
        costValue.valueFuelRedeliveryInLsfo
        : costValue.purchaseCostLsfo;
    const selectedPurchaseCostMgo = inBooks
      ? inBooks.Mgo.redelInValue - inBooks.Mgo.delOutValue
      : contractSide === "in"
        ? costValue.valueFuelDeliveryInMgo - costValue.valueFuelRedeliveryInMgo
        : costValue.purchaseCostMgo;

    const totalInternalQtyHsfo =
      reserveInternalQtyHsfo + tripSupplyQtyHsfo + selectedFuelPurchaseHsfo;
    const totalInternalQtyLsfo =
      reserveInternalQtyLsfo + tripSupplyQtyLsfo + selectedFuelPurchaseLsfo;
    const totalInternalQtyMgo =
      reserveInternalQtyMgo + tripSupplyQtyMgo + selectedFuelPurchaseMgo;

    const averagePricePmtHsfo = fuelData.averagePricePMTHsfo;
    const averagePricePmtLsfo = fuelData.averagePricePMTLsfo;
    const averagePricePmtMgo = fuelData.averagePricePMTMgo;

    const appliedQuantityHsfo = Math.min(
      safeNum(fuelData.voyageConsumptionQtyHsfo),
      tripSupplyQtyHsfo,
    );
    const appliedQuantityLsfo = Math.min(
      safeNum(fuelData.voyageConsumptionQtyLsfo),
      tripSupplyQtyLsfo,
    );
    const appliedQuantityMgo = Math.min(
      safeNum(fuelData.voyageConsumptionQtyMgo),
      tripSupplyQtyMgo,
    );

    const appliedCostAverageHsfo = averagePricePmtHsfo * appliedQuantityHsfo;
    const appliedCostAverageLsfo = averagePricePmtLsfo * appliedQuantityLsfo;
    const appliedCostAverageMgo = averagePricePmtMgo * appliedQuantityMgo;

    const appliedCostLIFOHsfo = sumProperty("lifoFuelCostHsfo");
    const appliedCostLIFOLsfo = sumProperty("lifoFuelCostLsfo");
    const appliedCostLIFOMgo = sumProperty("lifoFuelCostMgo");
    const appliedCostFIFOHsfo = sumProperty("fifoFuelCostHsfo");
    const appliedCostFIFOLsfo = sumProperty("fifoFuelCostLsfo");
    const appliedCostFIFOMgo = sumProperty("fifoFuelCostMgo");

    const appliedCostSelectedHsfo =
      fuelMethodHsfo === "LIFO"
        ? appliedCostLIFOHsfo
        : fuelMethodHsfo === "FIFO"
          ? appliedCostFIFOHsfo
          : appliedCostAverageHsfo;
    const appliedCostSelectedLsfo =
      fuelMethodLsfo === "LIFO"
        ? appliedCostLIFOLsfo
        : fuelMethodLsfo === "FIFO"
          ? appliedCostFIFOLsfo
          : appliedCostAverageLsfo;
    const appliedCostSelectedMgo =
      fuelMethodMgo === "LIFO"
        ? appliedCostLIFOMgo
        : fuelMethodMgo === "FIFO"
          ? appliedCostFIFOMgo
          : appliedCostAverageMgo;

    const voyageFuelCostHsfo =
      appliedCostSelectedHsfo +
      safeNum(fuelData.shortageQtyHsfo) * safeNum(shortagePriceHsfo);
    const voyageFuelCostLsfo =
      appliedCostSelectedLsfo +
      safeNum(fuelData.shortageQtyLsfo) * safeNum(shortagePriceLsfo);
    const voyageFuelCostMgo =
      appliedCostSelectedMgo +
      safeNum(fuelData.shortageQtyMgo) * safeNum(shortagePriceMgo);

    const revenueForHandOverHsfo =
      safeNum(fuelHandOverPriceHsfo) *
      (totalInternalQtyHsfo - safeNum(fuelData.voyageConsumptionQtyHsfo));
    const revenueForHandOverLsfo =
      safeNum(fuelHandOverPriceLsfo) *
      (totalInternalQtyLsfo - safeNum(fuelData.voyageConsumptionQtyLsfo));
    const revenueForHandOverMgo =
      safeNum(fuelHandOverPriceMgo) *
      (totalInternalQtyMgo - safeNum(fuelData.voyageConsumptionQtyMgo));

    const costForBalanceQtyHsfo =
      safeNum(reserves?.internal?.hsfo?.cost) +
      fuelData.tripSupplyCostHsfo +
      selectedPurchaseCostHsfo -
      voyageFuelCostHsfo;
    const costForBalanceQtyLsfo =
      safeNum(reserves?.internal?.lsfo?.cost) +
      fuelData.tripSupplyCostLsfo +
      selectedPurchaseCostLsfo -
      voyageFuelCostLsfo;
    const costForBalanceQtyMgo =
      safeNum(reserves?.internal?.mgo?.cost) +
      fuelData.tripSupplyCostMgo +
      selectedPurchaseCostMgo -
      voyageFuelCostMgo;
    const diff = tripSupplyQtyHsfo - fuelData[`voyageConsumptionQtyHsfo`];
    const diffLsfo = tripSupplyQtyLsfo - fuelData[`voyageConsumptionQtyLsfo`];
    const diffMgo = tripSupplyQtyMgo - fuelData[`voyageConsumptionQtyMgo`];

    const shortageQtyHsfo = fmt(diff < 0 ? Math.abs(diff) : 0);
    const shortageQtyLsfo = fmt(diffLsfo < 0 ? Math.abs(diffLsfo) : 0);
    const shortageQtyMgo = fmt(diffMgo < 0 ? Math.abs(diffMgo) : 0);
    const rawUpdateData = {
      reserveExternalQtyHsfo: safeNum(reserves?.external?.hsfo),
      reserveExternalQtyLsfo: safeNum(reserves?.external?.lsfo),
      reserveExternalQtyMgo: safeNum(reserves?.external?.mgo),
      reserveInternalCostHsfo: safeNum(reserves?.internal?.hsfo?.cost),
      reserveInternalCostLsfo: safeNum(reserves?.internal?.lsfo?.cost),
      reserveInternalCostMgo: safeNum(reserves?.internal?.mgo?.cost),
      fuelQtyActualRedeliveryOutHsfo: departureHsfo,
      fuelQtyActualRedeliveryOutLsfo: departureLsfo,
      fuelQtyActualRedeliveryOutMgo: departureMgo,
      fuelQtyDeliveryInHsfo: vesselInDelhs,
      fuelQtyDeliveryInLsfo: vesselInDells,
      fuelQtyDeliveryInMgo: vesselInDelmgo,
      fuelQtyRedeliveryInHsfo: redeliveryInHsfo,
      fuelQtyRedeliveryInLsfo: redeliveryInLsfo,
      fuelQtyRedeliveryInMgo: redeliveryInMgo,
      fuelQtyDeliveryOutHsfo: bnkDelhs,
      fuelQtyDeliveryOutLsfo: bnkDells,
      fuelQtyDeliveryOutMgo: bnkDelmgo,
      vesselOutFuelPurchaseHsfo,
      vesselOutFuelPurchaseLsfo,
      vesselOutFuelPurchaseMgo,
      vesselOutFuelPriceMgo: vesselOutFuelPurchaseMgo
        ? costValue.purchaseCostMgo / vesselOutFuelPurchaseMgo
        : 0,
      supplyInternalQtyHsfo: tripSupplyQtyHsfo,
      supplyInternalQtyLsfo: tripSupplyQtyLsfo,
      supplyInternalQtyMgo: tripSupplyQtyMgo,
      supplyInternalCostHsfo: fuelData.tripSupplyCostHsfo,
      supplyInternalCostLsfo: fuelData.tripSupplyCostLsfo,
      supplyInternalCostMgo: fuelData.tripSupplyCostMgo,
      supplyInternalPriceHsfo: averagePricePmtHsfo,
      supplyInternalPriceLsfo: averagePricePmtLsfo,
      supplyInternalPriceMgo: averagePricePmtMgo,
      appliedCostSelectedHsfo,
      appliedCostSelectedLsfo,
      appliedCostSelectedMgo,
      appliedQuantityHsfo,
      appliedQuantityLsfo,
      appliedQuantityMgo,
      reserveInternalQtyHsfo,
      reserveInternalQtyLsfo,
      reserveInternalQtyMgo,
      reserveInternalPriceHsfo: reserveInternalQtyHsfo
        ? safeNum(reserves?.internal?.hsfo?.cost) / reserveInternalQtyHsfo
        : 0,
      totalInternalCostHsfo:
        safeNum(reserves?.internal?.hsfo?.cost) +
        fuelData.tripSupplyCostHsfo +
        selectedPurchaseCostHsfo,
      totalInternalCostLsfo:
        safeNum(reserves?.internal?.lsfo?.cost) +
        fuelData.tripSupplyCostLsfo +
        selectedPurchaseCostLsfo,
      totalInternalCostMgo:
        safeNum(reserves?.internal?.mgo?.cost) +
        fuelData.tripSupplyCostMgo +
        selectedPurchaseCostMgo,
      totalInternalQtyHsfo,
      totalInternalQtyLsfo,
      totalInternalQtyMgo,
      totalInternalPriceHsfo: totalInternalQtyHsfo
        ? (safeNum(reserves?.internal?.hsfo?.cost) +
          fuelData.tripSupplyCostHsfo +
          selectedPurchaseCostHsfo) /
        totalInternalQtyHsfo
        : 0,
      totalInternalPriceLsfo: totalInternalQtyLsfo
        ? (safeNum(reserves?.internal?.lsfo?.cost) +
          fuelData.tripSupplyCostLsfo +
          selectedPurchaseCostLsfo) /
        totalInternalQtyLsfo
        : 0,
      totalInternalPriceMgo: totalInternalQtyMgo
        ? (safeNum(reserves?.internal?.mgo?.cost) +
          fuelData.tripSupplyCostMgo +
          selectedPurchaseCostMgo) /
        totalInternalQtyMgo
        : 0,
      balanceQtyInternalHsfo:
        totalInternalQtyHsfo - safeNum(fuelData.voyageConsumptionQtyHsfo),
      balanceQtyInternalLsfo:
        totalInternalQtyLsfo - safeNum(fuelData.voyageConsumptionQtyLsfo),
      balanceQtyInternalMgo:
        totalInternalQtyMgo - safeNum(fuelData.voyageConsumptionQtyMgo),
      fuelHandOverQtyHsfo:
        totalInternalQtyHsfo - safeNum(fuelData.voyageConsumptionQtyHsfo),
      fuelHandOverQtyLsfo:
        totalInternalQtyLsfo - safeNum(fuelData.voyageConsumptionQtyLsfo),
      fuelHandOverQtyMgo:
        totalInternalQtyMgo - safeNum(fuelData.voyageConsumptionQtyMgo),
      shortageCostHsfo:
        safeNum(fuelData.shortageQtyHsfo) * safeNum(shortagePriceHsfo),
      shortageCostLsfo:
        safeNum(fuelData.shortageQtyLsfo) * safeNum(shortagePriceLsfo),
      shortageCostMgo:
        safeNum(fuelData.shortageQtyMgo) * safeNum(shortagePriceMgo),
      shortageQtyHsfo,
      shortageQtyLsfo,
      shortageQtyMgo,
      voyageFuelCostHsfo,
      voyageFuelCostLsfo,
      voyageFuelCostMgo,
      costForBalanceQtyHsfo,
      costForBalanceQtyLsfo,
      costForBalanceQtyMgo,
      fuelMethodHsfo,
      fuelMethodLsfo,
      fuelMethodMgo,
      revenueForHandOverHsfo,
      revenueForHandOverLsfo,
      revenueForHandOverMgo,
      fuelHandOverProfitHsfo: revenueForHandOverHsfo - costForBalanceQtyHsfo,
      fuelHandOverProfitLsfo: revenueForHandOverLsfo - costForBalanceQtyLsfo,
      fuelHandOverProfitMgo: revenueForHandOverMgo - costForBalanceQtyMgo,
      totalFuelHandOverProfit:
        revenueForHandOverHsfo -
        costForBalanceQtyHsfo +
        (revenueForHandOverLsfo - costForBalanceQtyLsfo) +
        (revenueForHandOverMgo - costForBalanceQtyMgo),
      payableVendorHsfo: vesselInDelhs
        ? costValue.valueFuelDeliveryInHsfo / vesselInDelhs
        : 0,
      payableVendorLsfo: vesselInDells
        ? costValue.valueFuelDeliveryInLsfo / vesselInDells
        : 0,
      payableVendorMgo: vesselInDelmgo
        ? costValue.valueFuelDeliveryInMgo / vesselInDelmgo
        : 0,
      receivableCustomerHsfo: bnkDelhs
        ? costValue.valueFuelDeliveryOutHsfo / bnkDelhs
        : 0,
      receivableCustomerLsfo: bnkDells
        ? costValue.valueFuelDeliveryOutLsfo / bnkDells
        : 0,
      receivableCustomerMgo: bnkDelmgo
        ? costValue.valueFuelDeliveryOutMgo / bnkDelmgo
        : 0,
      payableCustomerHsfo: redeliveryInHsfo
        ? costValue.valueFuelRedeliveryInHsfo / redeliveryInHsfo
        : 0,
      payableCustomerLsfo: redeliveryInLsfo
        ? costValue.valueFuelRedeliveryInLsfo / redeliveryInLsfo
        : 0,
      payableCustomerMgo: redeliveryInMgo
        ? costValue.valueFuelRedeliveryInMgo / redeliveryInMgo
        : 0,
      receivableVendorHsfo: departureHsfo
        ? costValue.valueFuelRedeliveryOutHsfo / departureHsfo
        : 0,
      receivableVendorLsfo: departureLsfo
        ? costValue.valueFuelRedeliveryOutLsfo / departureLsfo
        : 0,
      receivableVendorMgo: departureMgo
        ? costValue.valueFuelRedeliveryOutMgo / departureMgo
        : 0,
      voyageEffectivePmtHsfo: safeNum(fuelData.voyageConsumptionQtyHsfo)
        ? voyageFuelCostHsfo / safeNum(fuelData.voyageConsumptionQtyHsfo)
        : 0,
      voyageEffectivePmtLsfo: safeNum(fuelData.voyageConsumptionQtyLsfo)
        ? voyageFuelCostLsfo / safeNum(fuelData.voyageConsumptionQtyLsfo)
        : 0,
      voyageEffectivePmtMgo: safeNum(fuelData.voyageConsumptionQtyMgo)
        ? voyageFuelCostMgo / safeNum(fuelData.voyageConsumptionQtyMgo)
        : 0,
      pricePerMtHsfo:
        totalInternalQtyHsfo !== safeNum(fuelData.voyageConsumptionQtyHsfo)
          ? costForBalanceQtyHsfo /
          (totalInternalQtyHsfo - safeNum(fuelData.voyageConsumptionQtyHsfo))
          : 0,
      pricePerMtLsfo:
        totalInternalQtyLsfo !== safeNum(fuelData.voyageConsumptionQtyLsfo)
          ? costForBalanceQtyLsfo /
          (totalInternalQtyLsfo - safeNum(fuelData.voyageConsumptionQtyLsfo))
          : 0,
      pricePerMtMgo:
        totalInternalQtyMgo !== safeNum(fuelData.voyageConsumptionQtyMgo)
          ? costForBalanceQtyMgo /
          (totalInternalQtyMgo - safeNum(fuelData.voyageConsumptionQtyMgo))
          : 0,
      ...costValue,
      extraCalc: {
        vesselInExtraTable,
        vesselOutExtraTable,
      },
    };

    const safeRatio = (value, qty) => (qty ? value / qty : 0);
    if (inBooks) {
      fuelTypes.forEach((F) => {
        const b = inBooks[F];
        const ratio = (value, qty) => (qty ? value / qty : 0);
        Object.assign(rawUpdateData, {
          [`fuelQtyDeliveryOut${F}`]: b.delOutQty,
          [`valueFuelDeliveryOut${F}`]: b.delOutValue,
          [`fuelQtyRedeliveryIn${F}`]: b.redelInQty,
          [`valueFuelRedeliveryIn${F}`]: b.redelInValue,
          [`fuelQtyActualRedeliveryOut${F}`]: b.redelOutQty,
          [`valueFuelRedeliveryOut${F}`]: b.redelOutValue,
          [`vesselOutFuelPurchase${F}`]: b.redelInQty - b.delOutQty,
          [`purchaseCost${F}`]: b.redelInValue - b.delOutValue,
          [`receivableCustomer${F}`]: ratio(b.delOutValue, b.delOutQty),
          [`payableCustomer${F}`]: ratio(b.redelInValue, b.redelInQty),
          [`receivableVendor${F}`]: ratio(b.redelOutValue, b.redelOutQty),
        });
        // Excel: Settlement Profit = value on Redelivery-Out - Balance value;
        // in relay mode the Hand-Over Profit applies instead.
        fuelData[`fuelSettlementProfit${F}`] =
          fuelSettlementMode === "relay"
            ? rawUpdateData[`fuelHandOverProfit${F}`]
            : finalRedeliveryRow
              ? b.redelOutValue - rawUpdateData[`costForBalanceQty${F}`]
              : 0;
      });
      rawUpdateData.vesselOutFuelPriceMgo = safeRatio(
        rawUpdateData.purchaseCostMgo,
        rawUpdateData.vesselOutFuelPurchaseMgo,
      );
    }

    // Excel: Main/ECA/MGO Fuel Expenses = applied cost + shortage x shortage price (both blocks)
    fuelTypes.forEach((F) => {
      fuelData[`voyageFuelExpenditure${F}`] = rawUpdateData[`voyageFuelCost${F}`];
    });

    const updateData = Object.entries(rawUpdateData).reduce(
      (acc, [key, val]) => {
        if (!isNaN(parseFloat(val)) && isFinite(val)) {
          acc[key] = fmt(val);
        } else {
          acc[key] = val;
        }
        return acc;
      },
      {},
    );

    const formattedFuelData = Object.entries(fuelData).reduce(
      (acc, [key, val]) => {
        if (!isNaN(parseFloat(val)) && isFinite(val)) {
          acc[key] = fmt(val);
        } else {
          acc[key] = val;
        }
        return acc;
      },
      {},
    );

    return {
      fuelData: formattedFuelData,
      updateData,
    };
  };

  const handleUpdateMainTable = async (
    calculatedExpenses,
    calculatedExpensesOut,
    calculatedRevenue,
    fuelDataIn,
    fuelDataOut,
    updateDataIn,
    updateDataOut,
  ) => {
    if (!calculatedRevenue?.length) return;

    const fmt = (num) => Number(Number(num || 0).toFixed(3));

    const keyMap = {
      grossFreight: "grossRevenue",
      charterersAdcom: "adcomAmt",
      brokerage: "brokerageAmt",
      taxesOthers: "otherAmt",
      netFreightRevenue: "netRevenue",
    };

    const revenueUpdates = {};
    for (const [targetKey, sourceKey] of Object.entries(keyMap)) {
      let total = 0;
      for (const item of calculatedRevenue) {
        total += Number(item?.[sourceKey]) || 0;
      }
      revenueUpdates[targetKey] = fmt(total);
    }

    const calculateActualBlock = (
      expenses,
      vesselDetails,
      totalOffHireDuration,
      category,
    ) => {
      const {
        adCom = 0,
        grossHireAmount = 0,
        deduction = 0,
        brokerageExp = 0,
        totalNetHireExpense = 0,
        totalTravelDays = 0,
        totalStay = 0,
        totalLaden = 0,
        totalBallast = 0,
        totalStayHSFO = 0,
        totalHSFO = 0,
        totalStayLSFO = 0,
        totalLSFO = 0,
        totalStayMGO = 0,
        totalMGO = 0,
      } = expenses || {};

      const extraExpense = Number(vesselDetails?.extraExpense || 0);

      const totalTripDuration = totalTravelDays + totalStay;

      const nonCveDailyExpIn =
        Number(subModelData?.expensesIn?.insurancePerDay || 0) ||
        Math.max(
          0,
          Number(subModelData?.expensesIn?.expensesPerDay || 0) -
          Number(subModelData?.expensesIn?.cvePerDay || 0),
        );
      const otherExp =
        Number(totalMiscExpenses || 0) +
        Number(nonCveDailyExpIn || 0) * Number(totalTripDuration || 0);

      const nonCveDailyExpOut =
        Number(subModelData?.expensesOut?.insurancePerDay || 0) ||
        Math.max(
          0,
          Number(subModelData?.expensesOut?.expensesPerDay || 0) -
          Number(subModelData?.expensesOut?.cvePerDay || 0),
        );
      const otherExpOut =
        Number(totalMiscExpensesOut || 0) +
        Number(nonCveDailyExpOut || 0) * Number(totalTripDuration || 0);

      const hireDuration =
        totalTripDuration - Number(totalOffHireDuration || 0);

      const isVesselOut = category === "ActualOut";
      const fuelData = isVesselOut ? fuelDataOut : fuelDataIn;
      const updateData = isVesselOut ? updateDataOut : updateDataIn;
      const totalPortExpt = contractCargoList().reduce(
        (sum, item) => sum + (Number(item.portExp) || 0),
        0,
      );
      const cve =
        (Number(
          isVesselOut
            ? subModelData?.expensesOut?.cvePerDay
            : subModelData?.expensesIn?.cvePerDay,
        ) || 0) * totalTripDuration;

      const totalFuelExpenses = fmt(
        (fuelData?.voyageFuelExpenditureHsfo || 0) +
        (fuelData?.voyageFuelExpenditureLsfo || 0) +
        (fuelData?.voyageFuelExpenditureMgo || 0),
      );
      const fuelHandOverProfitHsfo =
        fuelSettlementMode === "redelivery"
          ? updateData?.fuelHandOverProfitHsfo
          : fuelData?.fuelSettlementProfitHsfo;
      const fuelHandOverProfitLsfo =
        fuelSettlementMode === "redelivery"
          ? updateData?.fuelHandOverProfitLsfo
          : fuelData?.fuelSettlementProfitLsfo;
      const fuelHandOverProfitMgo =
        fuelSettlementMode === "redelivery"
          ? updateData?.fuelHandOverProfitMgo
          : fuelData?.fuelSettlementProfitMgo;
      const fuelSettlementProfitTotal = fmt(
        (fuelData?.fuelSettlementProfitHsfo || 0) +
        (fuelData?.fuelSettlementProfitLsfo || 0) +
        (fuelData?.fuelSettlementProfitMgo || 0),
      );

      const fuelHandOverProfitTotal = fmt(
        (fuelHandOverProfitHsfo || 0) +
        (fuelHandOverProfitLsfo || 0) +
        (fuelHandOverProfitMgo || 0),
      );

      let { netFreightRevenue, ...rest } = revenueUpdates;

      netFreightRevenue = fmt(
        (netFreightRevenue || 0) + Number(values?.[category]?.laytime || 0),
      );

      const totalNonHireExp =
        cve +
        totalPortExpt +
        (isVesselOut ? otherExpOut : otherExp) +
        totalFuelExpenses +
        extraExpense -
        fuelSettlementProfitTotal;

      const totalTripExp = fmt(totalNetHireExpense + totalNonHireExp);

      const netProfit = fmt(netFreightRevenue - totalTripExp);
      const revAvailableTC = fmt(netFreightRevenue - totalNonHireExp);
      const tceNet = fmt(revAvailableTC / (totalTripDuration || 1));

      return {
        ...Object.fromEntries(
          Object.entries(rest).map(([k, v]) => [k, fmt(v)]),
        ),
        netFreightRevenue: fmt(netFreightRevenue),
        additional: fmt(
          isVesselOut ? additionalRevenueOut : additionalRevenueIn,
        ),
        cve: fmt(cve),
        grossHireAmount: fmt(grossHireAmount),
        adCom: fmt(adCom),
        deduction: fmt(deduction),
        brokerageExp: fmt(brokerageExp),
        totalNetHireExpense: fmt(totalNetHireExpense),
        OffhireDuration: fmt(totalOffHireDuration),
        portExpenses: fmt(totalPortExpt),
        hireDuration: fmt(hireDuration),
        tceNet: fmt(tceNet),
        netProfit: fmt(netProfit),
        totalTripDuration: fmt(totalTripDuration),
        totalTripExp: fmt(totalTripExp),
        otherExp: fmt(isVesselOut ? otherExpOut : otherExp),
        otherExpOut: fmt(otherExpOut),
        totalFuelExpenses: fmt(totalFuelExpenses),
        fuelHandOverProfitTotal: fmt(fuelHandOverProfitTotal),
        fuelSettlementProfitTotal: fmt(fuelSettlementProfitTotal),
        ballastPassage: fmt(totalBallast),
        ladenPassage: fmt(totalLaden),
        portStayTotal: fmt(totalStay),
        hsfoQty: fmt(totalStayHSFO + totalHSFO),
        lsfoQty: fmt(totalStayLSFO + totalLSFO),
        mgoQty: fmt(totalStayMGO + totalMGO),
        MainFuelExpHSFO: fmt(fuelData?.voyageFuelExpenditureHsfo || 0),
        ECAFuelExpLSFO: fmt(fuelData?.voyageFuelExpenditureLsfo || 0),
        lightFuelExpMGO: fmt(fuelData?.voyageFuelExpenditureMgo || 0),
        fuelSettlementProfitHSFO: fmt(fuelData?.fuelSettlementProfitHsfo || 0),
        fuelSettlementProfitLSFO: fmt(fuelData?.fuelSettlementProfitLsfo || 0),
        fuelSettlementProfitMGO: fmt(fuelData?.fuelSettlementProfitMgo || 0),
      };
    };

    const actual = calculateActualBlock(
      calculatedExpenses,
      vesselContractDetails?.vesselDetails,
      totalOffHireDurationIn,
      "Actual",
    );

    const ActualOut = calculateActualBlock(
      calculatedExpensesOut,
      vesselContractDetails?.vesselOutContract?.vesselDetails,
      totalOffHireDurationOut,
      "ActualOut",
    );

    // Revenue rows are cargo only, as in the Excel: totals of the pricing table columns
    // (Gross Rev, Adcom Amt, Broker Amt, Other Amt); hire stays in the expense rows.
    [actual, ActualOut].forEach((block) => {
      block.netFreightRevenue = fmt(block.netFreightRevenue + block.additional);
    });
    // Excel: Net Profit = Net Revenue (revenue block) - Total Trip Exp (expenses block);
    // TCE = (Net Revenue - non-hire expenses) / total trip duration.
    const fullTripDuration = vesselOutRange()
      ? actual.totalTripDuration + ActualOut.totalTripDuration
      : actual.totalTripDuration;
    actual.netProfit = fmt(actual.netFreightRevenue - ActualOut.totalTripExp);
    actual.tceNet = fmt(
      (actual.netFreightRevenue -
        (ActualOut.totalTripExp - ActualOut.totalNetHireExpense)) /
      (fullTripDuration || 1),
    );
    ActualOut.netProfit = actual.netProfit;
    ActualOut.tceNet = actual.tceNet;

    const shallowEqual = (objA = {}, objB = {}) => {
      const keysA = Object.keys(objA);
      const keysB = Object.keys(objB);

      if (keysA.length !== keysB.length) return false;

      for (let key of keysA) {
        const valA = objA[key];
        const valB = objB[key];
        if (valA !== valB) {
          if (
            typeof valA === "object" &&
            valA !== null &&
            typeof valB === "object" &&
            valB !== null
          ) {
            if (JSON.stringify(valA) !== JSON.stringify(valB)) return false;
          } else {
            return false;
          }
        }
      }

      return true;
    };

    setSubModelData((prev) => {
      const nextFuelExpenseData = contractCargoList().some(
        (item) =>
          item.operation === "Delivery-out" || item.operation === "Redelivery-in",
      )
        ? { ...prev.fuelExpenseData, ...fuelDataIn, ...updateDataIn }
        : { ...prev.fuelExpenseData, ...fuelDataOut, ...updateDataOut };

      if (shallowEqual(prev.fuelExpenseData, nextFuelExpenseData)) {
        return prev;
      }

      return {
        ...prev,
        fuelExpenseData: nextFuelExpenseData,
      };
    });

    setValues((prev) => ({
      ...prev,
      Actual: {
        ...prev.Actual,
        ...actual,
      },
      ActualOut: {
        ...prev.ActualOut,
        ...ActualOut,
      },
    }));
  };

  const fuelInputSignature = JSON.stringify(
    ["Hsfo", "Lsfo", "Mgo"].flatMap((fuel) => [
      subModelData.fuelExpenseData?.[`fuelMethod${fuel}`],
      subModelData.fuelExpenseData?.[`shortagePrice${fuel}`],
      subModelData.fuelExpenseData?.[`fuelHandOverPrice${fuel}`],
      subModelData.fuelExpenseData?.[`${fuel.toLowerCase()}Shortfall`],
      subModelData.fuelExpenseData?.[`${fuel.toLowerCase()}Excess`],
    ]),
  );

  const calculateAll = useCallback(async () => {
    try {
      props.setLoader(true, "Calculating vessel data...");
      const expenseData = await calculateExpenses();
      const expenseDataOut = await calculateExpensesOut();
      const consumptionFor = (expenses) => ({
        hsfoQty:
          Number(expenses?.totalStayHSFO || 0) +
          Number(expenses?.totalHSFO || 0),
        lsfoQty:
          Number(expenses?.totalStayLSFO || 0) +
          Number(expenses?.totalLSFO || 0),
        mgoQty:
          Number(expenses?.totalStayMGO || 0) + Number(expenses?.totalMGO || 0),
      });
      const revenueDataIn = await calculateRevenue(
        consumptionFor(expenseData),
        "in",
      );
      const revenueDataOut = await calculateRevenue(
        consumptionFor(expenseDataOut),
        "out",
      );
      setCalculatedExpenses(expenseData);
      const { fuelData: fuelDataIn, updateData: updateDataIn } =
        await calculateFuelExpense(expenseData, revenueDataIn, "in");
      const { fuelData: fuelDataOut, updateData: updateDataOut } =
        await calculateFuelExpense(expenseDataOut, revenueDataOut, "out");
      handleUpdateMainTable(
        expenseData,
        expenseDataOut,
        revenueDataIn,
        fuelDataIn,
        fuelDataOut,
        updateDataIn,
        updateDataOut,
      );
    } catch (err) {
      console.error("Error calculating all:", err);
    } finally {
      props.setLoader(false);
    }
  }, [
    cargoOperationTableData,
    vesselContractDetails,
    offHireDeductionsOut,
    offHireDeductionsIn,
    totalFuelDeductionsIn,
    totalFuelDeductionsOut,
    totalMiscDeductionsIn,
    totalMiscDeductionsOut,
    fuelSettlementMode,
    additionalRevenueIn,
    additionalRevenueOut,
    fuelInputSignature,
    subModelData?.expensesIn,
    subModelData?.expensesOut,
    values.Actual.laytime,
    values.ActualOut.laytime,
    reCalculate,
  ]);

  useEffect(() => {
    if (
      JSON.stringify({
        vesselIn: values.Actual,
        vesselOut: values.ActualOut,
      }) !== JSON.stringify(unchangedValues.current)
    ) {
      isChanged.current = true;
    } else {
      isChanged.current = false;
    }
  }, [values.Actual]);

  const debouncedHandleUpdateMainTable = useMemo(
    () => debounce(calculateAll, 200),
    [calculateAll],
  );

  useEffect(() => {
    debouncedHandleUpdateMainTable();
    return () => debouncedHandleUpdateMainTable.cancel();
  }, [debouncedHandleUpdateMainTable]);

  useEffect(() => {
    fuelExpenseDataRef.current = subModelData.fuelExpenseData;
  }, [subModelData.fuelExpenseData]);

  const initHireExpenseOut = () => {
    const vessel = vesselContractDetails?.vesselOutContract?.vesselDetails;

    const offHire = recalculateOffHireRows(offHireDeductionsOut);

    const fuel = fuelDeductionsOut.map((item) => {
      const updated = { ...item };
      const rate = (updated.fuelDeducation || 0) / 100;

      if (updated.fuelGrade === "Main") {
        updated.price = +vessel?.bnkPriceDeliveryhs || 0;
      }
      if (updated.fuelGrade === "ECA") {
        updated.price = +vessel?.bnkPriceDeliveryls || 0;
      }
      if (updated.fuelGrade === "MGO") {
        updated.price = +vessel?.bnkPriceDeliverymgo || 0;
      }

      updated.amount = +(updated.quantity * updated.price * rate).toFixed(3);

      return updated;
    });

    setOffHireDeductionsOut(offHire);
    setFuelDeductionsOut(fuel);
  };

  const initHireExpenseIn = () => {
    const vessel = vesselContractDetails?.vesselDetails;

    const offHire = recalculateOffHireRows(offHireDeductionsIn);

    const fuel = fuelDeductionsIn?.map((item) => {
      const updated = { ...item };
      const rate = (updated.fuelDeducation || 0) / 100;

      if (updated.fuelGrade === "Main") {
        updated.price = +vessel?.bnkPriceDeliveryhs || 0;
      }
      if (updated.fuelGrade === "ECA") {
        updated.price = +vessel?.bnkPriceDeliveryls || 0;
      }
      if (updated.fuelGrade === "MGO") {
        updated.price = +vessel?.bnkPriceDeliverymgo || 0;
      }

      updated.amount = +(updated.quantity * updated.price * rate).toFixed(3);

      return updated;
    });

    setOffHireDeductionsIn(offHire);
    setFuelDeductionsIn(fuel);
  };

  useEffect(() => {
    if (
      vesselContractDetails?.vesselDetails &&
      offHireDeductionsIn?.length > 0 &&
      fuelDeductionsIn?.length > 0
    ) {
      initHireExpenseIn();
    }
  }, [
    vesselContractDetails?.vesselDetails,
    splitHireModal,
    offHireDeductionsIn?.length,
    fuelDeductionsIn?.length,
  ]);

  useEffect(() => {
    if (
      vesselContractDetails?.vesselOutContract?.vesselDetails &&
      offHireDeductionsOut?.length > 0 &&
      fuelDeductionsOut?.length > 0
    ) {
      initHireExpenseOut();
    }
  }, [
    vesselContractDetails?.vesselOutContract?.vesselDetails,
    splitHireModal,
    offHireDeductionsOut?.length,
    fuelDeductionsOut?.length,
  ]);

  const handleButtonClick = (action) => {
    switch (action) {
      case "vesselContractIn":
        {
          setPrevAttachments({
            attachments: vesselContractDetails?.attachments,
            textContract: vesselContractDetails?.textContract,
          });
          setEditVesselContractModelOpen({
            isOpen: true,
            key: action,
          });
        }
        break;
      case "vesselContractOut":
        {
          setPrevAttachments({
            attachments: vesselContractDetails?.vesselOutContract?.attachments,
            textContract:
              vesselContractDetails?.vesselOutContract?.textContract,
          });
          setEditVesselContractModelOpen({
            isOpen: true,
            key: action,
          });
        }
        break;
      case "cargoContract":
        setEditCargoContractModelOpen(true);
        break;
      case "offHire":
        setSplitHireModal(true);
        break;
      case "optimumSpeed":
        setOptimumSpeedModelOpen(true);
        break;
      case "laytimeSettlements":
        setIsLaytimeSettlementsModelOpen(true);
        break;
      case "invoices":
        setIsFinalInvoiceModelOpen(true);
        break;
      case "fuelSupplyRequest":
        setIsFuelSupplyRequestModelOpen(true);
        break;
      case "fuelExpensesReconciliation":
        setOpenFuelExpenseModel(true);
        break;
      case "sustainability":
        setOpenSustainabilityModel(true);
        break;
      default:
    }
  };

  const handleSetIntialStatementOfFactsDetails = (
    selectedOperation,
    selectedIndex,
  ) => {
    const vessel = JSON.parse(
      vesselContractDetails?.vesselDetails?.vessel || "{}",
    );

    setCargoOperationTableData((prev) => {
      const newState = { ...prev };
      const updatedCargoList = [...newState.cargoList];
      const currentItem = { ...updatedCargoList[selectedIndex] };

      if (!currentItem.statementOfFacts) {
        currentItem.statementOfFacts = {
          sof_authors: [
            {
              id: 1,
              name: "",
              designation: "",
              details: "",
            },
          ],
          userFields: [
            { label: "User Defined Fields 1", value: 0 },
            { label: "User Defined Fields 2", value: 1 },
          ],
          events: [{ day: "", time: "", event: "" }],
          remarks: {
            general: [""],
            shippers: [""],
            shipOwners: [""],
          },
        };
      }

      currentItem.vesselName =
        vessel?.vesselLabel || currentItem.vesselName || "";
      currentItem.grade = selectedOperation?.grade || currentItem.grade || "";
      currentItem.billOfLadingDate =
        selectedOperation?.billOfLadingDate ||
        currentItem.billOfLadingDate ||
        "";
      currentItem.port = selectedOperation?.port || currentItem.port || "";
      currentItem.operation =
        selectedOperation?.operation || currentItem.operation || "";
      currentItem.cargoQty =
        selectedOperation?.cargoQty || currentItem.cargoQty || "";
      currentItem.timeZone =
        selectedOperation?.timeZone || currentItem.timeZone || "";

      currentItem.voyageNo = currentItem.voyageNo || "";
      currentItem.charterer = currentItem.charterer || "";
      currentItem.shipmentId = currentItem.shipmentId || "";

      updatedCargoList[selectedIndex] = currentItem;

      return {
        ...newState,
        cargoList: updatedCargoList,
      };
    });

    setIsStatementOfFactsModelOpen(true);
  };

  const handleSetIntialLaytimeStatementDetails = (
    selectedOperation,
    selectedIndex,
  ) => {
    const vessel = JSON.parse(
      vesselContractDetails?.vesselDetails?.vessel || "{}",
    );

    setCargoOperationTableData((prev) => {
      const newState = { ...prev };
      const updatedCargoList = [...newState.cargoList];
      const currentItem = { ...updatedCargoList[selectedIndex] };

      if (!currentItem.laytimeStatement) {
        currentItem.laytimeStatement = {
          cargoDesc: "",
          dischargingRate: 0,
          dischargingTerms: 0,
          turnTimeAllowed: 0,
          turnTimeClause: 0,
          demurrageRate: 0,
          despatchRate: 0,
          totalTimeAllowed: 0,
          norTendered: "",
          norTerms: "Discharging",
          laytimeStarts: "",
          commencedDischarging: "",
          completedDischarging: "",
          laytimeStops: "",
          vesselOnDemurrage: "",
          eventTable: [
            {
              id: 1,
              fromTime: null,
              fromDay: "",
              toTime: null,
              toDay: "",
              event: "",
              timeDuration: 0,
              percentTimeToCount: 0,
              timeUsed: 0,
              cumulatedTimeUsed: 0,
              comments: "",
            },
          ],
        };
      }

      currentItem.vesselName =
        vessel?.vesselLabel || currentItem.vesselName || "";
      currentItem.grade = selectedOperation?.grade || currentItem.grade || "";
      currentItem.billOfLadingDate =
        selectedOperation?.billOfLadingDate ||
        currentItem.billOfLadingDate ||
        "";
      currentItem.port = selectedOperation?.port || currentItem.port || "";
      currentItem.operation =
        selectedOperation?.operation || currentItem.operation || "";
      currentItem.cargoQty =
        selectedOperation?.cargoQty || currentItem.cargoQty || "";
      currentItem.arrivalTime =
        selectedOperation?.arrivalTime || currentItem.arrivalTime || "";
      currentItem.departureTime =
        selectedOperation?.departureTime || currentItem.departureTime || "";

      currentItem.voyageNo = currentItem.voyageNo || "";
      currentItem.charterer = currentItem.charterer || "";
      currentItem.shipmentId = currentItem.shipmentId || "";

      updatedCargoList[selectedIndex] = currentItem;
      return { ...newState, cargoList: updatedCargoList };
    });

    setIsLaytimesModelOpen(true);
  };

  const getCurrentStatementOfFactsRowData = (cargoItem) => {
    const { _id, id, ...cleanSof } = cargoItem?.statementOfFacts || {};
    return {
      vesselData: {
        vesselName: cargoItem?.vesselName || "",
        grade: cargoItem?.grade || "",
        billOfLadingDate: cargoItem?.billOfLadingDate || "",
        voyageNo: vesselContractDetails?.voyageNumber || "",
        charterer: cargoItem?.charterer || "",
        port: cargoItem?.port || "",
        operation: cargoItem?.operation || "",
        cargoQty: cargoItem?.cargoQty || "",
        shipmentId: cargoContractDetails?.shipmentNumber || "",
        timeZone: cargoItem?.timeZone || "",
      },
      ...cleanSof,
    };
  };

  const getCurrentLaytimeStatementRowData = (cargoItem) => {
    const { _id, id, ...cleanLaytime } = cargoItem?.laytimeStatement || {};
    return {
      vesselName: cargoItem?.vesselName || "",
      grade: cargoItem?.grade || "",
      billOfLadingDate: cargoItem?.billOfLadingDate || "",
      voyageNo: vesselContractDetails?.voyageNumber || "",
      charterer: cargoItem?.charterer || "",
      port: cargoItem?.port || "",
      operation: cargoItem?.operation || "",
      cargoQty: cargoItem?.cargoQty || "",
      shipmentId: cargoContractDetails?.shipmentNumber || "",
      arrivalTime: cargoItem?.arrivalTime || "",
      departureTime: cargoItem?.departureTime || "",
      ...cleanLaytime,
    };
  };

  const handleCargoOperationSubModels = (index, row, key) => {
    setSelectedOperationIndex(index);
    switch (key) {
      case "laytimes":
        handleSetIntialLaytimeStatementDetails(row, index);
        break;
      case "statementOfFacts":
        handleSetIntialStatementOfFactsDetails(row, index);
        break;
      case "issueInvoice":
        handleSetIntialIssueInvoiceDetails(row, index);
        break;
      default:
    }
  };

  const handleSetIntialIssueInvoiceDetails = (row, index) => {
    if (freightMode?.toLowerCase() !== "single freight") {
      setSelectedCargoIndex(index);
    } else {
      const cargoDetails = cargoOperationTableData?.cargoList?.filter(
        (item) => item.operation?.toLowerCase() === "loading",
      );
      setSelectedCargoIndex(index);
    }
    setIsIssueInvoiceModelOpen(true);
  };

  const handleSubmit = async (
    updatedCargoOperationTableData,
    updatedVesselDetails,
  ) => {
    try {
      props.setLoader(true, "Saving vessel details..");
      isSaved.current = true;
      reCalculate.current = true;
      const { actualCargoContract, operationCargoContract, ...rest } =
        cargoContractDetails;
      const {
        actualVesselContract,
        operationVesselContract,
        vesselOutContract,
        ...restVessel
      } = vesselContractDetails;
      const {
        type: _vesselType,
        isOps: _isOps,
        ...vesselFields
      } = updatedVesselDetails || {};
      const isUpdatingVesselOut = _vesselType === "vesselContractOut";
      const payload = {
        vesselContractDetails: {
          ...restVessel,
          vesselInvoiceDetails: expenseInvoicesIn,
          vesselInvoiceDetailsOut: expenseInvoicesOut,
          vesselDetails: {
            ...vesselContractDetails?.vesselDetails,
            ...(!isUpdatingVesselOut ? vesselFields : {}),
            clientQuery:
              updatedCargoOperationTableData || cargoOperationTableData,
          },
          vesselOutContract: isUpdatingVesselOut
            ? {
              ...vesselOutContract,
              vesselDetails: {
                ...vesselOutContract?.vesselDetails,
                ...vesselFields,
              },
            }
            : vesselOutContract,
        },
        calculatedExpenses,
        subModelCalculation: subModelData,
        offHire: {
          offHireDeductionsIn,
          offHireDeductionsOut,
          fuelDeductionsIn,
          fuelDeductionsOut,
          miscDeductionsIn,
          miscDeductionsOut,
        },
        fuelSettlementMode,
        operationStatus,
        sustainabilityData,
        actualCalculation: {
          vesselIn: values.Actual,
          vesselOut: values.ActualOut,
        },
        vesselCargoDetailsTableData,
      };
      await calculateOperation({ id }, payload);
      await fetchOperation();
      setWarning({});
      reCalculate.current = false;
      !updatedCargoOperationTableData &&
        toastSuccess("Operation calculation saved successfully");
      return true;
    } catch (error) {
      toastError("Failed to calculate operation");
      return false;
    } finally {
      props.setLoader(false);
    }
  };

  const expensesKeyForSection = (section) =>
    section === "revenue" ? "expensesIn" : "expensesOut";
  const revenueKeyForSection = (section) =>
    section === "expenses" ? "revenueOut" : "revenue";

  const handleChangeSubMenu = (section, field, value) => {
    const expensesKey = expensesKeyForSection(section);

    setSubModelData((prev) => ({
      ...prev,
      [expensesKey]: {
        ...prev[expensesKey],
        [field]: value,
      },
    }));
  };

  const handleSubModels = (action, key) => {
    switch (action) {
      case "additional":
        setOpenAdditionalRevenueModel(key);
        break;
      case "otherExp":
        setOpenOtherExpModel({
          isOpen: true,
          key: key,
        });
        break;
      case "otherExpOut":
        setOpenOtherExpModel({
          isOpen: true,
          key: key,
        });
        break;
      case "totalFuelExpenses":
        setOpenFuelExpenseModel(true);
        break;
      default:
    }
  };

  const renderCompactInput = (
    value,
    onChange,
    endAdornment = null,
    forceDisabled = false,
  ) => (
    <div className="flex items-center overflow-hidden">
      <TextField
        type="number"
        size="small"
        className="text-xs w-full border bg-[#f6f8ff] border-gray-300 rounded"
        variant="outlined"
        margin="none"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        fullWidth
        disabled={isReadOnly || forceDisabled}
        InputProps={{
          style: { fontSize: "0.775rem", height: "25px" },
          classes: {
            root: "px-1",
            input: "px-[0.5px]",
          },
        }}
      />
      {endAdornment && <span className="text-xs p-1">{endAdornment}</span>}
    </div>
  );

  useEffect(() => {
    return () => {
      if (transitionTimeoutRef.current) {
        clearTimeout(transitionTimeoutRef.current);
      }
    };
  }, []);

  const initializSettledAmt = async () => {
    try {
      props.setLoader(true, "Fetching settled amounts...");
      const { data } = await getPostedInvoiceByUserId(id);
      if (!data || !Array.isArray(data)) return;

      const labelKeyMap = {
        "Base Rate": "grossFreight",
        Demurrage: "laytime",
        Despatch: "laytime",
        "Lumpsum Charges": "additional",
        "Adcom (%)": "charterersAdcom",
        "Others (%)": "taxesOthers",
        "Brokerage (%)": "brokerage",
      };

      const allInvoiceDetails = data.flatMap((inv) =>
        (inv.invoiceDetail || []).map((detail) => ({
          ...detail,
          postedAt: inv.postedAt || null,
        })),
      );

      const vesselInItems = allInvoiceDetails.filter(
        (item) => item.vesselType !== "vesselOut",
      );

      const vesselOutItems = allInvoiceDetails.filter(
        (item) => item.vesselType === "vesselOut",
      );

      const buildSettledData = (items) => {
        const grouped = items.reduce((acc, item) => {
          const label = item?.invoiceDetail?.label || item.label;
          const itemKey = item?.key || label;

          if (!label && !itemKey) return acc;

          if (!acc[itemKey]) {
            acc[itemKey] = {
              label: label || "",
              key: itemKey || null,
              paymentEntries: [],
              totalSettledAmt: 0,
            };
          }

          if (Array.isArray(item.paymentEntries)) {
            const counterParty =
              item?.invoiceDetail?.counterParty ||
              item?.invoiceDetail?.name ||
              null;

            const counterPartyId = item?.invoiceDetail?.counterPartyId || null;

            const postedAt = item?.postedAt || null;

            const enrichedEntries = item.paymentEntries.map((entry) => ({
              ...entry,
              counterParty,
              counterPartyId,
              postedAt,
            }));

            acc[itemKey].paymentEntries.push(...enrichedEntries);
          }

          acc[itemKey].totalSettledAmt = Number(
            (
              acc[itemKey].totalSettledAmt + Number(item.totalSettledAmt || 0)
            ).toFixed(3),
          );

          return acc;
        }, {});

        const groupedResult = Object.values(grouped);
        const fieldMappings = [...financialFields, ...profitFileds];
        const settledData = {};

        fieldMappings.forEach((field) => {
          let found =
            groupedResult.find(
              (item) => labelKeyMap[item.label] === field.key,
            ) ||
            groupedResult.find((item) =>
              item.label?.toLowerCase().includes(field.label.toLowerCase()),
            ) ||
            groupedResult.find((item) => item.key === field.key);

          settledData[field.key] = found
            ? {
              paymentEntries: found.paymentEntries,
              totalSettledAmt: Number(
                Number(found.totalSettledAmt || 0).toFixed(3),
              ),
              key: found.key,
            }
            : {
              paymentEntries: [],
              totalSettledAmt: 0,
              key: null,
            };
        });

        return settledData;
      };

      const settledIn = buildSettledData(vesselInItems);
      const settledOut = buildSettledData(vesselOutItems);

      setValues((prev) => ({
        ...prev,
        Settled: settledIn,
        SettledOut: settledOut,
      }));
    } catch (error) {
      console.error("Error fetching settled amounts:", error);
    } finally {
      props.setLoader(false);
    }
  };

  useEffect(() => {
    initializSettledAmt();
  }, []);

  const getNextMiscExpLabel = (list) => {
    const usedNums = new Set();
    (list || []).forEach((item) => {
      if (item && typeof item.label === "string") {
        const matches = item.label.match(/\d+/g);
        if (matches) {
          matches.forEach((m) => {
            const val = parseInt(m, 10);
            if (!isNaN(val) && val > 0 && val < 1000) {
              usedNums.add(val);
            }
          });
        }
      }
    });
    let n = 1;
    while (usedNums.has(n)) {
      n++;
    }
    return `Misc Exp-${n}`;
  };

  const getNextMiscRevLabel = (list) => {
    const usedNums = new Set();
    (list || []).forEach((item) => {
      if (item && typeof item.label === "string") {
        const matches = item.label.match(/\d+/g);
        if (matches) {
          matches.forEach((m) => {
            const val = parseInt(m, 10);
            if (!isNaN(val) && val > 0 && val < 1000) {
              usedNums.add(val);
            }
          });
        }
      }
    });
    let n = 1;
    while (usedNums.has(n)) {
      n++;
    }
    return `Misc ${n}`;
  };

  const handleAddMiscExpense = (key) => {
    setSubModelData((prev) => {
      const expensesKey = expensesKeyForSection(key);
      const list = prev[expensesKey]?.miscExp || [];
      const newLabel = getNextMiscExpLabel(list);
      const newExpense = {
        id: `misc_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
        label: newLabel,
        amount: 0,
      };

      return {
        ...prev,
        [expensesKey]: {
          ...prev[expensesKey],
          miscExp: [...list, newExpense],
        },
      };
    });
  };

  const handleRenameMiscExpense = (index, newLabel, key) => {
    const expensesKey = expensesKeyForSection(key);
    setSubModelData((prev) => ({
      ...prev,
      [expensesKey]: {
        ...prev[expensesKey],
        miscExp: (prev[expensesKey]?.miscExp || []).map((exp, idx) =>
          idx === index ? { ...exp, label: newLabel } : exp,
        ),
      },
    }));
  };

  const handleRemoveMiscExpense = (index, key) => {
    const expensesKey = expensesKeyForSection(key);
    setSubModelData((prev) => {
      const list = prev[expensesKey]?.miscExp || [];
      const updated = list.filter((_, idx) => idx !== index);

      return {
        ...prev,
        [expensesKey]: {
          ...prev[expensesKey],
          miscExp:
            updated.length > 0
              ? updated
              : [{ id: `misc_${Date.now()}`, label: "Misc Exp-1", amount: 0 }],
        },
      };
    });
  };

  const handleChangeMiscExpenseAmount = (index, newAmount, key) => {
    const expensesKey = expensesKeyForSection(key);
    setSubModelData((prev) => ({
      ...prev,
      [expensesKey]: {
        ...prev[expensesKey],
        miscExp: (prev[expensesKey]?.miscExp || []).map((exp, idx) =>
          idx === index ? { ...exp, amount: newAmount } : exp,
        ),
      },
    }));
  };

  const handleAddMiscRevenue = (section) => {
    setSubModelData((prev) => {
      const revenueKey = revenueKeyForSection(section);
      const list = prev[revenueKey]?.misc || [];
      const newLabel = getNextMiscRevLabel(list);
      const newItem = {
        id: `rev_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
        label: newLabel,
        amount: 0,
      };

      return {
        ...prev,
        [revenueKey]: {
          ...prev[revenueKey],
          misc: [...list, newItem],
        },
      };
    });
  };

  const handleUpdateMiscRevenueAmount = (index, newAmount, section) => {
    const revenueKey = revenueKeyForSection(section);
    setSubModelData((prev) => ({
      ...prev,
      [revenueKey]: {
        ...prev[revenueKey],
        misc: (prev[revenueKey]?.misc || []).map((m, idx) =>
          idx === index ? { ...m, amount: newAmount } : m,
        ),
      },
    }));
  };

  const handleRenameMiscRevenue = (index, newLabel, section) => {
    const revenueKey = revenueKeyForSection(section);
    setSubModelData((prev) => ({
      ...prev,
      [revenueKey]: {
        ...prev[revenueKey],
        misc: (prev[revenueKey]?.misc || []).map((m, idx) =>
          idx === index ? { ...m, label: newLabel } : m,
        ),
      },
    }));
  };

  const handleRemoveMiscRevenue = (index, section) => {
    setSubModelData((prev) => {
      const revenueKey = revenueKeyForSection(section);
      const list = prev[revenueKey]?.misc || [];
      const updated = list.filter((_, idx) => idx !== index);

      return {
        ...prev,
        [revenueKey]: {
          ...prev[revenueKey],
          misc:
            updated.length > 0
              ? updated
              : [{ id: `rev_${Date.now()}`, label: "Misc 1", amount: 0 }],
        },
      };
    });
  };

  const handleAddRow = (index) => {
    setOpenAddOperation({
      isOpen: true,
      index,
    });
  };

  const handleUserRefChange = (index, value) => {
    const updated = [...vesselCargoDetailsTableData.userRef];
    updated[index].value = value;
    setVesselCargoDetailsTableData((prev) => ({ ...prev, userRef: updated }));
  };

  const handleAddUserRef = (index) => {
    if (vesselCargoDetailsTableData.userRef.length >= 6) {
      toastWarning("Maximum 6 user references allowed");
      return;
    }
    const currentRefs = vesselCargoDetailsTableData.userRef;

    const existingNums = new Set(
      currentRefs
        .map((item) => {
          const match = item.label?.match(/\d+/);
          return match ? parseInt(match[0], 10) : null;
        })
        .filter((n) => n !== null),
    );

    let nextNum = 1;
    if (existingNums.size > 0) {
      const maxNum = Math.max(...existingNums);
      if (maxNum < 6) {
        nextNum = maxNum + 1;
      } else {
        for (let i = 1; i <= 6; i++) {
          if (!existingNums.has(i)) {
            nextNum = i;
            break;
          }
        }
      }
    }

    const updated = [...currentRefs];
    const newId = Date.now();
    updated.splice(index + 1, 0, {
      id: newId,
      label: `User Ref-${nextNum}`,
      value: "",
    });
    setVesselCargoDetailsTableData((prev) => ({ ...prev, userRef: updated }));

    setTimeout(() => {
      if (vesselCargoContainerRef.current) {
        vesselCargoContainerRef.current.scrollTo({
          top: vesselCargoContainerRef.current.scrollHeight,
          behavior: "smooth",
        });
      }
    }, 50);
  };

  const handleRemoveUserRef = (index) => {
    if (vesselCargoDetailsTableData.userRef.length === 1) return;
    const updated = [...vesselCargoDetailsTableData.userRef];
    updated.splice(index, 1);
    setVesselCargoDetailsTableData((prev) => ({ ...prev, userRef: updated }));
  };

  const handleVesselCargoDetailsTableInputChange = (e, key) => {
    setVesselCargoDetailsTableData({
      ...vesselCargoDetailsTableData,
      [key]: e.target.value,
    });
  };

  const delayPortSearch = useCallback(
    _.debounce(
      (selectedOperation, search, isInitialData = false) =>
        onSearchPortList(selectedOperation, search, isInitialData),
      500,
    ),
    [],
  );
  const onSearchPortList = (selectedOperation, value) => {
    setInitialAddOperation({ ...selectedOperation, port: value });
    if (value) {
      setPortLoading(true);
      portsByChar(value)
        .then((res) => {
          setPortLoading(false);
          setPortOptions(
            (Array.isArray(res.data.list) &&
              res.data.list.map((item) => ({
                label: `${item.name} ${item.country ? "(" + item.country + ")" : ""
                  }`,
                value: item.name,
                lat: item?.lat,
                lng: item?.lng,
              }))) ||
            [],
          );
        })
        .catch((err) => {
          setPortLoading(false);
        });
    }
  };

  const onAddOperationByModal = async () => {
    const {
      operation = "",
      port = "",
      portExp = 0,
      portStay = 0,
    } = initialAddOperation;

    if (!operation || !port) {
      return toastError("Port is required");
    }

    const prevCargoList = cargoOperationTableData?.cargoList || [];
    if (isSingleOperationTaken(prevCargoList, operation)) {
      return toastError(
        `Only one ${operation} operation is allowed per contract.`,
      );
    }
    const insertIndex = openAddOperation.index + 1;

    const prevRowDepartureTime =
      openAddOperation.index >= 0 && prevCargoList[openAddOperation.index]
        ? prevCargoList[openAddOperation.index].departureTime
        : "N/A";

    const newRow = {
      _id: uuidv4(),
      operation,
      port,
      cargoTx: 0,
      cargoDescription: "",
      remarks: null,
      statementOfFacts: null,
      laytimes: null,
      timeZone: "+0",
      arrivalTime: prevRowDepartureTime,
      arrivalTimeZ: convertToUTC(prevRowDepartureTime, "+0"),
      arrivalHsfo: 0,
      arrivalLsfo: 0,
      arrivalMgo: 0,
      cargoDetails: {
        distNormal: 728,
        distEca: 0,
        bnkrFactor: 0,
        speedReduce: 0,
        speedFactor: 0,
        arrivalBunkersHSFO: 0,
        arrivalBunkersLSFO: 0,
        arrivalBunkersMGO: 0,
        bunkerSuppliedHSFO: 0,
        bunkerSuppliedLSFO: 0,
        bunkerSuppliedMGO: 0,
        bunkerNonScrubberHSFO: 0,
        bunkerNonScrubberLSFO: 0,
        bunkerNonScrubberMGO: 0,
        bunkerScrubberHSFO: 0,
        bunkerScrubberLSFO: 0,
        bunkerScrubberMGO: 0,
        fuelPriceType0Hsfo: 0,
        fuelPriceType0Lsfo: 0,
        fuelPriceType0Mgo: 0,
        fuelPriceType1Hsfo: 0,
        fuelPriceType1Lsfo: 0,
        fuelPriceType1Mgo: 0,
        fuelPriceType2Hsfo: 0,
        fuelPriceType2Lsfo: 0,
        fuelPriceType2Mgo: 0,
        fuelPriceType3Hsfo: 0,
        fuelPriceType3Lsfo: 0,
        fuelPriceType3Mgo: 0,
        fuelPriceType4Hsfo: 0,
        fuelPriceType4Lsfo: 0,
        fuelPriceType4Mgo: 0,
        reserve: "Supply",
        paidBy: "Owner",
      },
      priceHsfo: 0,
      priceLsfo: 0,
      priceMgo: 0,
      departureTime:
        insertIndex < prevCargoList.length
          ? prevCargoList[insertIndex].arrivalTime
          : null,
      departureTimeZ:
        insertIndex < prevCargoList.length
          ? convertToUTC(prevCargoList[insertIndex].arrivalTime, "+0")
          : "N/A",
      departureHsfo: 0,
      departureLsfo: 0,
      departureMgo: 0,
      billOfLadingDate: null,
      blNumber: null,
      remarks2: null,
      portExp,
      portStay,
    };

    const updatedList = [...prevCargoList];
    updatedList.splice(insertIndex, 0, newRow);

    if (insertIndex + 1 < updatedList.length) {
      updatedList[insertIndex + 1].arrivalTime = newRow.departureTime;
    }

    const newCargoOperationTableData = {
      ...cargoOperationTableData,
      cargoList: updatedList,
    };

    setCargoOperationTableData(newCargoOperationTableData);
    setIntialCargoInvoiceDetails({
      ...newCargoOperationTableData,
      cargoDetails: newCargoOperationTableData,
    });

    setInitialAddOperation({
      operation: "Passing",
      port: "",
      portExp: 0,
      portStay: 0,
    });
    setOpenAddOperation({
      isOpen: false,
      index: null,
    });
    setPortOptions([]);
    setPortLoading(false);
  };

  const handleDeleteRow = (index) => {
    if (cargoOperationTableData?.cargoList?.length <= 1) return;

    const newCargoData = [...cargoOperationTableData?.cargoList];

    newCargoData.splice(index, 1);
    setCargoOperationTableData((prev) => ({
      ...prev,
      cargoList: newCargoData,
    }));
  };

  const handleDownloadPdf = async () => {
    try {
      props.setLoader(true, "Creating download...");
      let response;
      if (isLaytimesModelOpen) {
        response = await createElectricLaytimePdf(
          getCurrentLaytimeStatementRowData(
            cargoOperationTableData?.cargoList?.[selectedOperationIndex],
          ),
        );
      } else if (isStatementOfFactsModelOpen) {
        response = await createElectricSofPdf(
          getCurrentStatementOfFactsRowData(
            cargoOperationTableData?.cargoList?.[selectedOperationIndex],
          ),
        );
      }
      if (!response) {
        toastError(`Failed to create data`);
      }
      return response.data || response;
    } catch (err) {
      toastError(err.message);
    } finally {
      props.setLoader(false);
    }
  };

  const fetchDataForPdf = async () => {
    const vessel = vesselContractDetails?.vesselDetails;
    const cargo =
      contractCargoList().find((item) => item.operation === "Redelivery-in") ||
      contractCargoList().slice(-1)[0];
    const {
      _id = "",
      bnkPriceDeliveryhs = 0,
      bnkPriceDeliveryls = 0,
      bnkPriceDeliverymgo = 0,
      bnkPriceRedeliveryhs = 0,
      bnkPriceRedeliveryls = 0,
      bnkPriceRedeliverymgo = 0,
      bnkPriceSettlemenths = 0,
      bnkPriceSettlementls = 0,
      bnkPriceSettlementmgo = 0,
      bnkQtyRedeliveryhs = 0,
      bnkQtyRedeliveryls = 0,
      bnkQtyRedeliverymgo = 0,
      bnkRedeliveryQtyTolerancehs = 0,
      bnkRedeliveryQtyTolerancels = 0,
      bnkRedeliveryQtyTolerancemgo = 0,
      constants = 0,
      extraExpense = 0,
    } = vessel || {};
    const vesselData = vessel?.vessel ? JSON.parse(vessel?.vessel || "{}") : {};
    const {
      vesselName = "",
      vesselDwt = 0,
      vesselIMO = 0,
      vesselDraftS = 0,
      vesselTPC,
      vesselGrainCap,
      vesselBuilt,
      vesselLOA,
      vesselBeam,
      vesselGRT,
      vesselNRT,
      vesselLightShip,
      vesselBaleCap,
      vesselGears,
      vesselGrabs,
      vesselFlag,
      vesselMMSI,
      vesselClass,
      vesselBalSpd1 = 0,
      vesselBalConsmHS1 = 0,
      vesselBalConsmLS1 = 0,
      vesselBalConsmMGO1 = 0,
      vesselBalSpd2 = 0,
      vesselBalConsmHS2 = 0,
      vesselBalConsmLS2 = 0,
      vesselBalConsmMGO2 = 0,
      vesselBalSpd3 = 0,
      vesselBalConsmHS3 = 0,
      vesselBalConsmLS3 = 0,
      vesselBalConsmMGO3 = 0,
      vesselBalSpd4 = 0,
      vesselBalConsmHS4 = 0,
      vesselBalConsmLS4 = 0,
      vesselBalConsmMGO4 = 0,
      vesselLdnSpd1 = 0,
      vesselLdnConsmHS1 = 0,
      vesselLdnConsmLS1 = 0,
      vesselLdnConsmMGO1 = 0,
      vesselLdnSpd2 = 0,
      vesselLdnConsmHS2 = 0,
      vesselLdnConsmLS2 = 0,
      vesselLdnConsmMGO2 = 0,
      vesselLdnSpd3 = 0,
      vesselLdnConsmHS3 = 0,
      vesselLdnConsmLS3 = 0,
      vesselLdnConsmMGO3 = 0,
      vesselLdnSpd4 = 0,
      vesselLdnConsmHS4 = 0,
      vesselLdnConsmLS4 = 0,
      vesselLdnConsmMGO4 = 0,
      vesselPIdleConsmHS = 0,
      vesselPIdleConsmLS = 0,
      vesselPIdleConsmMGO = 0,
      vesselPLdgConsmHS = 0,
      vesselPLdgConsmLS = 0,
      vesselPLdgConsmMGO = 0,
      vesselPDischConsmHS = 0,
      vesselPDischConsmLS = 0,
      vesselPDischConsmMGO = 0,
      vesselVExtraConsmHS = 0,
      vesselVExtraConsmLS = 0,
      vesselVExtraConsmMGO = 0,
      vesselBalComb1Selected = false,
      vesselBalComb2Selected = false,
      vesselBalComb3Selected = false,
      vesselBalComb4Selected = false,
      vesselLadComb1Selected = false,
      vesselLadComb2Selected = false,
      vesselLadComb3Selected = false,
      vesselLadComb4Selected = false,
    } = vesselData || {};
    const splitData = JSON.parse(vessel?.splitHire || "[]") || [];

    const data = {
      first_section: {
        _id,
        vesselName,
        port: cargo?.port,
        remark: vessel?.remark,
        DWT: vesselDwt,
        Draft: vesselDraftS,
        TPC: vesselTPC,
        grain: vesselGrainCap,
        built: vesselBuilt,
        LOA: vesselLOA,
        Beam: vesselBeam,
        GRT: vesselGRT,
        NRT: vesselNRT,
        light_ship: vesselLightShip,
        bale_capacity: vesselBaleCap,
        Gears: vesselGears,
        Grabs: vesselGrabs,
        Flag: vesselFlag,
        MMSI: vesselMMSI,
        Class: vesselClass,
      },
      second_section: {
        DWT: vesselDwt,
        IMO: vesselIMO,
        BalComb1: vesselBalComb1Selected,
        BalComb2: vesselBalComb2Selected,
        BalComb3: vesselBalComb3Selected,
        BalComb4: vesselBalComb4Selected,
        LadComb1: vesselLadComb1Selected,
        LadComb2: vesselLadComb2Selected,
        LadComb3: vesselLadComb3Selected,
        LadComb4: vesselLadComb4Selected,
        Ballast_Sea_Consumptions: {
          col_1: {
            Speed: vesselBalSpd1,
            HS: vesselBalConsmHS1,
            LS: vesselBalConsmLS1,
            MGO: vesselBalConsmMGO1,
          },
          col_2: {
            Speed: vesselBalSpd2,
            HS: vesselBalConsmHS2,
            LS: vesselBalConsmLS2,
            MGO: vesselBalConsmMGO2,
          },
          col_3: {
            Speed: vesselBalSpd3,
            HS: vesselBalConsmHS3,
            LS: vesselBalConsmLS3,
            MGO: vesselBalConsmMGO3,
          },
          col_4: {
            Speed: vesselBalSpd4,
            HS: vesselBalConsmHS4,
            LS: vesselBalConsmLS4,
            MGO: vesselBalConsmMGO4,
          },
        },
        Laden_Sea_Consumptions: {
          col_1: {
            Speed: vesselLdnSpd1,
            HS: vesselLdnConsmHS1,
            LS: vesselLdnConsmLS1,
            MGO: vesselLdnConsmMGO1,
          },
          col_2: {
            Speed: vesselLdnSpd2,
            HS: vesselLdnConsmHS2,
            LS: vesselLdnConsmLS2,
            MGO: vesselLdnConsmMGO2,
          },
          col_3: {
            Speed: vesselLdnSpd3,
            HS: vesselLdnConsmHS3,
            LS: vesselLdnConsmLS3,
            MGO: vesselLdnConsmMGO3,
          },
          col_4: {
            Speed: vesselLdnSpd4,
            HS: vesselLdnConsmHS4,
            LS: vesselLdnConsmLS4,
            MGO: vesselLdnConsmMGO4,
          },
        },
        Port_Consumptions: {
          Idle: {
            HS: vesselPIdleConsmHS,
            LS: vesselPIdleConsmLS,
            MGO: vesselPIdleConsmMGO,
          },
          Loading: {
            HS: vesselPLdgConsmHS,
            LS: vesselPLdgConsmLS,
            MGO: vesselPLdgConsmMGO,
          },
          Disch: {
            HS: vesselPDischConsmHS,
            LS: vesselPDischConsmLS,
            MGO: vesselPDischConsmMGO,
          },
          Extra: {
            HS: vesselVExtraConsmHS,
            LS: vesselVExtraConsmLS,
            MGO: vesselVExtraConsmMGO,
          },
        },
      },
      third_section: {
        split_hire: splitData || [],
        bunker_qty: {
          bnkDelhs: Number(cargo?.departureHsfo)?.toFixed(2) || 0,
          bnkDells: Number(cargo?.departureLsfo)?.toFixed(2) || 0,
          bnkDelmgo: Number(cargo?.departureMgo)?.toFixed(2) || 0,
          bnkQtyRedeliveryhs:
            Number(cargo?.redeliveryHsfo)?.toFixed(2) ||
            bnkQtyRedeliveryhs ||
            0,
          bnkQtyRedeliveryls:
            Number(cargo?.redeliveryLsfo)?.toFixed(2) ||
            bnkQtyRedeliveryls ||
            0,
          bnkQtyRedeliverymgo:
            Number(cargo?.redeliveryMgo)?.toFixed(2) ||
            bnkQtyRedeliverymgo ||
            0,
          bnkRedeliveryQtyTolerancehs,
          bnkRedeliveryQtyTolerancels,
          bnkRedeliveryQtyTolerancemgo,
        },
        bunker_price: {
          bnkPriceDeliveryhs,
          bnkPriceDeliveryls,
          bnkPriceDeliverymgo,
          bnkPriceRedeliveryhs,
          bnkPriceRedeliveryls,
          bnkPriceRedeliverymgo,
          bnkPriceSettlemenths,
          bnkPriceSettlementls,
          bnkPriceSettlementmgo,
        },
      },
      fourth_section: {
        extraExpense,
        constants,
        dateOpen: cargo?.departureTime || vessel?.dateOpen,
        Gbb: vessel?.gbb,
        hirePerDay: vessel?.hirePerDay,
        hireGbb: vessel?.hireGbb,
      },
    };

    return data;
  };
  const pdfTemplate = async () => {
    try {
      const generatedData = await fetchDataForPdf();
      setPdfData(generatedData);
    } catch (err) {
      console.error("Error in PDF generation", err);
    }
  };

  useEffect(() => {
    pdfTemplate();
  }, [vesselContractDetails, cargoOperationTableData, cargoContractDetails]);

  const onDownloadPdf = async () => {
    try {
      props.setLoader(true, "Downloading PDF...");
      const data = await addDownloadPdf(pdfData);
      if (data) {
        return data;
      }
      return null;
    } catch (err) {
      console.error("Error downloading PDF", err);
      return null;
    } finally {
      props.setLoader(false);
    }
  };

  const tabs = [
    {
      key: 0,
      label: "First Estimate",
      tooltip: isRevised
        ? "Contracts that were created but are now locked after revision."
        : "New contracts that are active and editable until revised.",
    },
    isRevised && {
      key: 1,
      label: "Revised Estimate",
      tooltip: "Edited contracts that have been revised.",
    },
    {
      key: 2,
      label: "Actual Operations",
      tooltip: "View and manage operations assigned to you.",
    },
  ];

  const handleSetRows = useCallback(
    (newRowsOrUpdater) => {
      setCargoOperationTableData((prev) => {
        const updated = { ...prev };
        const currentRows =
          updated.cargoList[selectedCargoIndex]?.invoiceDetails || [];

        const newRows =
          typeof newRowsOrUpdater === "function"
            ? newRowsOrUpdater(currentRows)
            : newRowsOrUpdater;

        updated.cargoList[selectedCargoIndex] = {
          ...updated.cargoList[selectedCargoIndex],
          invoiceDetails: newRows,
        };

        const selectedCargo = updated.cargoList[selectedCargoIndex];

        setCargoInvoices((prevInvoices) => {
          const existingIndex = prevInvoices.findIndex(
            (invoice) =>
              invoice.cargoId === selectedCargo?._id &&
              invoice.port === selectedCargo?.port,
          );

          if (existingIndex !== -1) {
            const updatedInvoices = [...prevInvoices];
            updatedInvoices[existingIndex] = {
              ...updatedInvoices[existingIndex],
              cargoName: updated?.name,
              invoiceDetails: newRows,
              totalAmt: newRows.reduce(
                (sum, row) => sum + (row.amount || 0),
                0,
              ),
            };
            return updatedInvoices;
          } else {
            return [
              ...prevInvoices,
              {
                cargoId: selectedCargo?._id,
                port: selectedCargo?.port,
                operation: selectedCargo?.operation,
                cargoName: updated?.name,
                totalAmt: newRows.reduce(
                  (sum, row) => sum + (row.amount || 0),
                  0,
                ),
                invoiceDetails: newRows,
              },
            ];
          }
        });

        handleSubmit(updated);
        return updated;
      });
    },
    [selectedCargoIndex],
  );

  const isContractIn = editVesselContractModelOpen.key === "vesselContractIn";

  const vesselDetailsViewTabs = [
    {
      key: 0,
      label: "Vessel Contract",
    },
    {
      key: 1,
      label: "Vessel Details",
    },
    {
      key: 2,
      label: "View Off-Hire",
    },
  ];

  const cargoDetailsViewTabs = [
    {
      key: 0,
      label: "Cargo Contract",
    },
    {
      key: 1,
      label: "Cargo Details",
    },
  ];

  const activeData = subModelData[expensesKeyForSection(openOtherExpModel.key)];

  const getOffHireProps = () => {
    if (isContractIn) {
      return {
        fuelDeductions: fuelDeductionsIn,
        vesselData: vesselContractDetails?.vesselDetails,
        setFuelDeductions: setFuelDeductionsIn,
        offHireDeductions: offHireDeductionsIn,
        miscDeductions: miscDeductionsIn,
        setOffHireDeductions: setOffHireDeductionsIn,
        setMiscDeductions: setMiscDeductionsIn,
        totalDeductions: totalDeductionsIn,
        totalOffHireDuration: totalOffHireDurationIn,
        totalMiscDeductions: totalMiscDeductionsIn,
        totalFuelDeductions: totalFuelDeductionsIn,
        cveDeduction: cveDeductionIn,
        hirePerDayDeduction: hirePerDayDeductionIn,
      };
    }

    return {
      fuelDeductions: fuelDeductionsOut,
      setFuelDeductions: setFuelDeductionsOut,
      offHireDeductions: offHireDeductionsOut,
      vesselData: vesselContractDetails?.vesselOutContract?.vesselDetails,
      miscDeductions: miscDeductionsOut,
      setOffHireDeductions: setOffHireDeductionsOut,
      setMiscDeductions: setMiscDeductionsOut,
      totalDeductions: totalDeductionsOut,
      totalOffHireDuration: totalOffHireDurationOut,
      totalMiscDeductions: totalMiscDeductionsOut,
      totalFuelDeductions: totalFuelDeductionsOut,
      cveDeduction: cveDeductionOut,
      hirePerDayDeduction: hirePerDayDeductionOut,
    };
  };

  const getCurrentVesselContractDetails = (contractKey, details) => {
    if (contractKey === "vesselContractOut") {
      return {
        ...details?.vesselOutContract?.vesselDetails,
        contractId: details?.vesselContractId,
      };
    }

    return {
      ...details?.vesselDetails,
      contractId: details?.vesselContractId,
    };
  };

  const currentVesselContractDetails = useMemo(
    () =>
      getCurrentVesselContractDetails(
        editVesselContractModelOpen.key,
        vesselContractDetails,
      ),
    [editVesselContractModelOpen.key, vesselContractDetails],
  );

  const getCurrentVesselContract = (contractKey, details) => {
    if (contractKey === "vesselContractOut") {
      return {
        ...details?.vesselOutContract,
      };
    }
    return {
      ...details,
    };
  };

  const handleUpdateVesselContract = (key) => {
    const { vesselContractId, ...payload } =
      key === "vesselContractIn"
        ? vesselContractDetails
        : {
          ...vesselContractDetails.vesselOutContract,
          vesselContractId: vesselContractDetails?.vesselContractId,
        };
    const formPayload = new FormData();

    if (vesselTextContract) {
      const fileType = vesselTextContract.type || "application/octet-stream";
      const fileName = vesselTextContract.name || "contract";

      const textBlob = new Blob([vesselTextContract], { type: fileType });
      const textFile = new File([textBlob], fileName, { type: fileType });

      formPayload.append("files", textFile);
      formPayload.append(
        "textContractMetadata",
        JSON.stringify({ fileType: "textContract" }),
      );
    }

    if (vesselAttachments?.length) {
      vesselAttachments.forEach((file) => {
        formPayload.append("files", file);
      });
    }

    formPayload.append(
      "data",
      JSON.stringify({
        ...payload,
        type: key === "vesselContractIn" ? "vesselIn" : "vesselOut",
        isOpsContract: true,
        attachments: prevAttachments.attachments,
        textContract: prevAttachments.textContract,
      }),
    );

    updateVesselContract({ _id: vesselContractId }, formPayload)
      .then(() => {
        setEditVesselContractModelOpen(false);
        setVesselAttachments([]);
        setVesselTextContract("");
        setPrevAttachments({
          attachments: [],
          textContract: "",
        });
        fetchOperation();
        toastSuccess("Vessel Contract Updated successfully!!");
      })
      .catch((err) => {
        toastError(err.message);
      });
  };

  const handleUpdateCargoContract = () => {
    const {
      cargoContractId,
      actualCargoContract,
      operationCargoContract,
      ...payload
    } = cargoContractDetails;
    const cpDate =
      payload.cpDate ||
      payload.contractDate ||
      payload.cp_date ||
      moment().format("YYYY-MM-DD");
    const formPayload = new FormData();

    if (cargoTextContract) {
      const fileType = cargoTextContract.type || "application/octet-stream";
      const fileName = cargoTextContract.name || "contract";

      const textBlob = new Blob([cargoTextContract], { type: fileType });
      const textFile = new File([textBlob], fileName, { type: fileType });

      formPayload.append("files", textFile);
      formPayload.append(
        "textContractMetadata",
        JSON.stringify({ fileType: "textContract" }),
      );
    }

    if (cargoAttachments?.length) {
      cargoAttachments.forEach((file) => {
        formPayload.append("files", file);
      });
    }

    formPayload.append(
      "data",
      JSON.stringify({
        ...payload,
        cpDate,
        isOpsContract: true,
        attachments: prevAttachments.attachments,
        textContract: prevAttachments.textContract,
      }),
    );

    updateCargoContract({ _id: cargoContractId || id }, formPayload)
      .then(() => {
        setEditCargoContractModelOpen(false);
        setCargoAttachments([]);
        setCargoTextContract("");
        setPrevAttachments({
          attachments: [],
          textContract: "",
        });
        fetchOperation();
        toastSuccess("Cargo Contract Updated successfully!!");
      })
      .catch((err) => {
        toastError(err.message);
      });
  };

  const handleOnChangeVesselContract = (e) => {
    setVesselContractDetails((prev) => {
      const resolvedUpdate = typeof e === "function" ? e(prev) : e;

      if (editVesselContractModelOpen.key === "vesselContractIn") {
        return resolvedUpdate;
      }

      return {
        ...prev,
        vesselOutContract: {
          ...(prev?.vesselOutContract || {}),
          ...resolvedUpdate,
        },
      };
    });
  };

  const handleViewNextTrip = () => {
    if (!nextOperationId) return;
    window.open(`/saved-contracts/${nextOperationId}`, "_blank");
  };

  const handleCreateNextTrip = async () => {
    if (vesselContractDetails?.tripEnded) {
      setTripEndWarningReason(
        vesselContractDetails.tripEndReason || "trip_end",
      );
      setTripEndWarningMode("blocked");
      setTripEndWarningOpen(true);
      return;
    }
    if (nextOperationId) {
      handleViewNextTrip();
      return;
    }

    try {
      if (typeof props.setLoader === "function") {
        props.setLoader(true, "Checking trip eligibility...");
      }
      const res = await checkTripEligibility(id);
      if (typeof props.setLoader === "function") {
        props.setLoader(false);
      }

      const { eligible, reason } = normalizeTripEligibilityResponse(res);
      if (eligible) {
        setNextTripModalOpen(true);
      } else {
        setTripEndWarningReason(reason);
        setTripEndWarningMode("blocked");
        setTripEndWarningOpen(true);
      }
    } catch (err) {
      if (typeof props.setLoader === "function") {
        props.setLoader(false);
      }
      console.error(err);
      toastError(err?.message || "Failed to check trip eligibility");
    }
  };

  const handleConfirmEndTrip = async () => {
    if (!id) {
      toastError("Operation ID not found");
      return;
    }
    try {
      const res = await endTrip({ operationId: id });
      if (res?.success) {
        toastSuccess("Trip ended successfully");
        if (typeof fetchOperation === "function") {
          fetchOperation();
        }
      } else {
        toastError(res?.message || "Failed to end trip");
      }
    } catch (err) {
      toastError(err?.message || "Failed to end trip");
    }
  };

  const { setIsFullScreen } = props;

  useEffect(() => {
    if (typeof setIsFullScreen === "function") {
      setIsFullScreen(true);
      return () => setIsFullScreen(false);
    }
  }, [setIsFullScreen]);

  return (
    <Grid alignContent="flex-start" className="min-h-[calc(100vh-106px)]">
      {IsLoading && (
        <div className="inset-0 flex items-center justify-center bg-black/70 absolute z-[999]">
          <div className="gap-2 mb-2 text-white flex flex-col items-center">
            <CircularProgress size={30} color="inherit" />
            <span className="text-base tracking-wider font-semibold">
              Loading...
            </span>
          </div>
        </div>
      )}
      {mainTab === 2 && (
        <div className="actual-ops bg-[#c8dcf6] w-full max-w-full min-w-0 p-2 flex flex-col">
          <div className="mb-3 overflow-x-auto overflow-y-hidden scroll-thin pb-2.5 w-full max-w-full min-w-0 shrink-0">
            <div className="min-w-[1080px]">
              <div className="flex justify-between gap-10 items-center mb-2">
                <div className="flex items-center gap-3">
                  <Box display="flex" alignItems="center" className="shadow-lg">
                    <CustomTabs
                      tabs={tabs}
                      active={mainTab}
                      onChange={handleMainTabChange}
                      isCompact
                    />
                  </Box>
                  <div className="inline-flex items-center border border-[#cbd5e1] rounded-none overflow-hidden h-[24px] text-[11px]">
                    <span className="bg-[#f8fafc] text-[#1e293b] font-medium px-2 h-full flex items-center border-r border-[#cbd5e1] whitespace-nowrap">
                      Fuel Settlement Mode :
                    </span>
                    <div className="bg-[#ffffd6] h-full flex items-center px-1">
                      <select
                        value={fuelSettlementMode}
                        disabled={isReadOnly}
                        onChange={handleFuelSettlementModeChange}
                        name="fuelSettlementMode"
                        className="bg-transparent text-[#1e293b] text-[11px] font-medium border-none outline-none cursor-pointer capitalize h-full pr-1 disabled:cursor-not-allowed"
                      >
                        {fuelSettlementList.map((item, i) => (
                          <option
                            value={item.value}
                            key={i}
                            className="text-[#1e293b] capitalize bg-white"
                          >
                            {item.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="inline-flex items-center border border-[#cbd5e1] rounded-none overflow-hidden h-[24px] text-[11px]">
                    <span className="bg-[#f8fafc] text-[#1e293b] font-medium px-2 h-full flex items-center border-r border-[#cbd5e1] whitespace-nowrap">
                      Operation Status :
                    </span>
                    <div className="bg-[#ffffd6] h-full flex items-center px-1">
                      <select
                        value={operationStatus}
                        disabled={isReadOnly}
                        onChange={handleOperationStatusChange}
                        name="operationStatus"
                        className="bg-transparent text-[#1e293b] text-[11px] font-medium border-none outline-none cursor-pointer capitalize h-full pr-1 disabled:cursor-not-allowed"
                      >
                        {operationStatusList.map((item, i) => (
                          <option
                            value={item.value}
                            key={i}
                            className="text-[#1e293b] capitalize bg-white"
                          >
                            {item.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {!isReadOnly && (
                    <div className="flex items-center gap-2">
                      {fuelSettlementMode?.toLowerCase() === "relay" &&
                        (nextOperationId ? (
                          <Button
                            onClick={handleViewNextTrip}
                            variant="contained"
                            style={{
                              backgroundColor: "#313C4C",
                              borderColor: "#313C4C",
                              color: "#ffffff",
                              borderRadius: 0,
                            }}
                            className="px-2 py-1 text-xs whitespace-nowrap text-white capitalize flex items-center gap-1 hover:opacity-90"
                            title="A Next Trip has already been created for this voyage. Click to view it."
                          >
                            <span>View Next Trip</span>
                            <svg
                              className="w-3 h-3"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                              />
                            </svg>
                          </Button>
                        ) : (
                          <Button
                            onClick={handleCreateNextTrip}
                            disabled={vesselContractDetails?.tripEnded}
                            variant="contained"
                            style={{ borderRadius: 0 }}
                            className="px-2 py-1 text-xs whitespace-nowrap bg-[#313C4C] hover:bg-[#1e2632] text-white capitalize"
                          >
                            Create Next Trip
                          </Button>
                        ))}
                      <Button
                        onClick={() => {
                          setTripEndWarningMode("confirm_end");
                          setTripEndWarningOpen(true);
                        }}
                        disabled={vesselContractDetails?.tripEnded}
                        variant="contained"
                        style={{ backgroundColor: "#313C4C", color: "#fff" }}
                        className="px-2 py-1 text-xs whitespace-nowrap rounded-none text-white capitalize"
                        title="End this trip and block future Next Trip creation"
                      >
                        {vesselContractDetails?.tripEnded
                          ? "Trip Ended"
                          : "Trip End"}
                      </Button>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  {/* Save Button Section */}
                  <div className="flex items-center justify-end">
                    {!isReadOnly && isChanged.current && isSaved.current && (
                      <span className="mr-2">
                        <CustomTooltip
                          position="left"
                          text="Unsaved changes detected"
                        >
                          <div className="relative">
                            <div className="w-8 h-8 flex items-center justify-center rounded-full bg-rose-50 border border-rose-200">
                              <div className="w-2 h-2 rounded-full bg-rose-500 animate-pulse"></div>
                            </div>
                          </div>
                        </CustomTooltip>
                      </span>
                    )}
                    {!isReadOnly && (
                      <Button
                        onClick={() => {
                          handleSubmit();
                        }}
                        variant="outlined"
                        className={`px-2 py-1 text-sm whitespace-nowrap rounded-none   ${!isChanged.current || !isSaved.current ? "border border-gray-300 text-white bg-[#313C4C]" : "border border-rose-300 text-white bg-rose-500"}`}
                        autoFocus={false}
                      >
                        Calculate and Save
                      </Button>
                    )}
                  </div>

                  {/* Download PDF Section */}
                  {/* <div className="flex justify-end">
                    <DownloadPdf
                      pdfData={pdfData}
                      onDownloadPdf={onDownloadPdf}
                      isOperationTab
                    />
                  </div> */}
                </div>
              </div>
              <div className="flex gap-2 py-1 h-[62vh] max-h-[700px] min-h-0 overflow-hidden">
                <div className="flex flex-row gap-2 flex-1 min-h-0 overflow-hidden">
                  <div className="flex-[1.75] min-w-[420px] max-w-[800px] rounded-none h-full max-h-full overflow-y-auto scroll-thin bg-white border border-[#cbd5e1]">
                    <table className="w-full border-collapse text-[11px]">
                      {/* Table Header */}
                      <thead className="sticky top-0 z-[99]">
                        <tr className="bg-[#313C4C] text-white h-[24px]">
                          <th className="px-2 py-1 text-left w-[32%] font-semibold text-[11px] align-middle border-r border-b border-[#5b6779]">
                            Label
                          </th>
                          <th className="px-2 py-1 text-left w-[17%] font-semibold text-[11px] align-middle border-r border-b border-[#5b6779]">
                            First Estimate
                          </th>
                          <th className="px-2 py-1 text-left w-[17%] font-semibold text-[11px] align-middle border-r border-b border-[#5b6779]">
                            Revised
                          </th>
                          <th className="px-2 py-1 text-left w-[17%] font-semibold text-[11px] align-middle border-r border-b border-[#5b6779]">
                            Actual
                          </th>
                          <th className="px-2 py-1 text-left w-[17%] font-semibold text-[11px] align-middle border-b border-[#5b6779]">
                            Settled
                          </th>
                        </tr>
                      </thead>

                      <tbody className="divide-y-0">
                        {/* Revenue Section */}
                        <tr>
                          <td
                            colSpan={5}
                            className="bg-[#dbe4ef] px-2 py-1 text-[11px] font-semibold text-black border-y border-[#b4bbc4] shadow-[inset_0_1px_0_#b4bbc4] h-[24px] align-middle"
                          >
                            <div className="flex gap-5 items-center">
                              <span className="section-title relative z-10 font-semibold text-black">
                                Revenue
                              </span>
                              <Tooltip
                                arrow
                                title={
                                  <span className="text-[11px]">
                                    Generate Invoice
                                  </span>
                                }
                              >
                                <button
                                  className="text-[11px] bg-white border border-[#94a3b8] text-slate-800 shadow-sm font-medium px-2 py-0 rounded-none hover:bg-[#313C4C] hover:text-white transition-all cursor-pointer"
                                  onClick={() =>
                                    setOpenExpensesInvoiceModel({
                                      isOpen: true,
                                      vessel: "In",
                                    })
                                  }
                                >
                                  Generate Invoice
                                </button>
                              </Tooltip>
                            </div>
                          </td>
                        </tr>

                        {financialFields.map((field, i) => (
                          <tr
                            key={`rev-${i}`}
                            className="h-[19px] hover:bg-amber-50/60 transition-colors"
                          >
                            <td
                              className={`px-2 py-0 border-r border-b border-[#cbd5e1] bg-white h-[19px] align-middle text-[11px]
                                ${field.link
                                  ? "cursor-pointer font-medium text-black underline"
                                  : "text-black font-medium"
                                }`}
                              onClick={() =>
                                field.link &&
                                handleSubModels(field.key, "revenue")
                              }
                            >
                              {field.label}
                            </td>

                            <td className="bg-[#f1f5f9] border-r border-b border-[#cbd5e1] p-0 h-[19px] align-middle">
                              <input
                                className="w-full h-full text-[11px] px-1.5 bg-[#f1f5f9] disabled:bg-[#f1f5f9] font-medium text-black border-none outline-none tabular-nums"
                                value={values.Revenue[field.key] ?? "-"}
                                disabled
                                type="text"
                              />
                            </td>

                            <td className="bg-[#f8fafc] border-r border-b border-[#cbd5e1] p-0 h-[19px] align-middle">
                              <input
                                className="w-full h-full text-[11px] px-1.5 bg-[#f8fafc] disabled:bg-[#f8fafc] font-medium text-black border-none outline-none tabular-nums"
                                value={values.Revised[field.key] ?? "-"}
                                disabled
                                type="text"
                              />
                            </td>

                            <td
                              className={`border-r border-b border-[#cbd5e1] p-0 h-[19px] align-middle ${field.key !== "laytime"
                                ? "bg-white"
                                : "bg-[#ffffd6]"
                                }`}
                            >
                              <input
                                className={`w-full h-full text-[11px] px-1.5 tabular-nums font-medium text-black border-none outline-none bg-transparent
                                  ${field.key === "laytime"
                                    ? "focus:ring-1 focus:ring-inset focus:ring-amber-500 focus:bg-white cursor-text"
                                    : "cursor-not-allowed text-black"
                                  }`}
                                value={values.Actual[field.key] ?? ""}
                                onChange={handleChange("Actual", field.key)}
                                disabled={field.key !== "laytime"}
                                type="number"
                              />
                            </td>

                            <td className="bg-white border-b border-[#cbd5e1] p-0 h-[19px] align-middle">
                              <button
                                className={`w-full h-full text-left text-[11px] px-1.5 font-medium flex items-center bg-transparent border-none outline-none
                                  ${values?.Settled[field.key]
                                    ?.totalSettledAmt > 0
                                    ? "text-black font-medium underline"
                                    : "text-black cursor-default"
                                  }`}
                                onClick={() =>
                                  setSelectedSettledAmt(
                                    values?.Settled[field.key],
                                  )
                                }
                                disabled={
                                  values?.Settled[field.key]
                                    ?.totalSettledAmt === 0
                                }
                              >
                                {values?.Settled[field.key]?.totalSettledAmt
                                  ? Number(
                                    Number(
                                      values?.Settled[field.key]
                                        ?.totalSettledAmt,
                                    ).toFixed(3),
                                  )
                                  : 0}
                              </button>
                            </td>
                          </tr>
                        ))}

                        {/* Expenses Section */}
                        <tr>
                          <td
                            colSpan={5}
                            className="bg-[#dbe4ef] px-2 py-1 text-[11px] font-semibold text-black border-y border-[#b4bbc4] shadow-[inset_0_1px_0_#b4bbc4] h-[24px] align-middle relative"
                          >
                            <div className="flex gap-5 items-center">
                              <span className="section-title relative z-10 font-semibold text-black">
                                Expenses
                              </span>
                              <Tooltip
                                arrow
                                title={
                                  <span className="text-[11px]">
                                    Generate Payment Instruction
                                  </span>
                                }
                              >
                                <button
                                  className="text-[11px] bg-white border border-[#94a3b8] text-black shadow-sm font-medium px-2 py-0 rounded-none hover:bg-[#313C4C] hover:text-white transition-all cursor-pointer"
                                  onClick={() =>
                                    setOpenExpensesInvoiceModel({
                                      isOpen: true,
                                      vessel: "Out",
                                    })
                                  }
                                >
                                  Generate Payment Instruction
                                </button>
                              </Tooltip>
                            </div>
                          </td>
                        </tr>

                        {financialFields.map((field, i) => (
                          <tr
                            key={`exp-${i}`}
                            className="h-[19px] hover:bg-amber-50/60 transition-colors"
                          >
                            <td
                              className={`px-2 py-0 border-r border-b border-[#cbd5e1] bg-white h-[19px] align-middle text-[11px]
                                ${field.link
                                  ? "cursor-pointer font-medium text-black underline"
                                  : "text-black font-medium"
                                }`}
                              onClick={() =>
                                field.link &&
                                handleSubModels(field.key, "expenses")
                              }
                            >
                              {field.label}
                            </td>

                            <td className="bg-[#f1f5f9] border-r border-b border-[#cbd5e1] p-0 h-[19px] align-middle">
                              <input
                                className="w-full h-full text-[11px] px-1.5 bg-[#f1f5f9] disabled:bg-[#f1f5f9] font-medium text-black border-none outline-none tabular-nums"
                                value={values.RevenueOut[field.key] ?? "-"}
                                disabled
                                type="text"
                              />
                            </td>

                            <td className="bg-[#f8fafc] border-r border-b border-[#cbd5e1] p-0 h-[19px] align-middle">
                              <input
                                className="w-full h-full text-[11px] px-1.5 bg-[#f8fafc] disabled:bg-[#f8fafc] font-medium text-black border-none outline-none tabular-nums"
                                value={values.RevisedOut[field.key] ?? "-"}
                                disabled
                                type="text"
                              />
                            </td>

                            <td
                              className={`border-r border-b border-[#cbd5e1] p-0 h-[19px] align-middle ${field.key !== "laytime"
                                ? "bg-white"
                                : "bg-[#ffffd6]"
                                }`}
                            >
                              <input
                                className={`w-full h-full text-[11px] px-1.5 tabular-nums font-medium text-black border-none outline-none bg-transparent
                                  ${field.key === "laytime"
                                    ? "focus:ring-1 focus:ring-inset focus:ring-amber-500 focus:bg-white cursor-text"
                                    : "cursor-not-allowed text-black"
                                  }`}
                                value={values.ActualOut[field.key] ?? ""}
                                onChange={handleChange("ActualOut", field.key)}
                                disabled={field.key !== "laytime"}
                                type="number"
                              />
                            </td>

                            <td className="bg-white border-b border-[#cbd5e1] p-0 h-[19px] align-middle">
                              <button
                                className={`w-full h-full text-left text-[11px] px-1.5 font-medium flex items-center bg-transparent border-none outline-none
                                  ${values?.SettledOut[field.key]
                                    ?.totalSettledAmt > 0
                                    ? "text-black font-medium underline"
                                    : "text-black cursor-default"
                                  }`}
                                onClick={() =>
                                  setSelectedSettledAmt(
                                    values?.SettledOut[field.key],
                                  )
                                }
                                disabled={
                                  values?.SettledOut[field.key]
                                    ?.totalSettledAmt === 0
                                }
                              >
                                {values?.SettledOut[field.key]?.totalSettledAmt
                                  ? Number(
                                    Number(
                                      values?.SettledOut[field.key]
                                        ?.totalSettledAmt,
                                    ).toFixed(3),
                                  )
                                  : 0}
                              </button>
                            </td>
                          </tr>
                        ))}

                        {/* Profit Section */}
                        <tr>
                          <td
                            colSpan={5}
                            className="bg-[#dbe4ef] px-2 py-1 text-[11px] font-semibold text-black border-y border-[#b4bbc4] shadow-[inset_0_1px_0_#b4bbc4] h-[24px] align-middle relative"
                          >
                            <span className="section-title relative z-10 font-semibold text-black">
                              Profit
                            </span>
                          </td>
                        </tr>

                        {profitFileds.map((field, i) => (
                          <tr
                            key={`pro-${i}`}
                            className="h-[19px] hover:bg-amber-50/50 transition-colors"
                          >
                            <td
                              className={`px-2 py-0 border-r border-b border-[#cbd5e1] bg-white h-[19px] align-middle text-[11px]
                                ${field.link
                                  ? "cursor-pointer font-medium text-black underline"
                                  : "text-black font-medium"
                                }`}
                              onClick={() =>
                                field.link && handleSubModels(field.key)
                              }
                            >
                              <span>{field.label}</span>
                            </td>

                            <td className="bg-[#f1f5f9] border-r border-b border-[#cbd5e1] p-0 h-[19px] align-middle">
                              <input
                                className="w-full h-full text-[11px] px-1.5 bg-[#f1f5f9] disabled:bg-[#f1f5f9] font-medium text-black border-none outline-none tabular-nums"
                                value={values.Revenue[field.key] ?? "-"}
                                disabled
                                type="text"
                              />
                            </td>

                            <td className="bg-[#f8fafc] border-r border-b border-[#cbd5e1] p-0 h-[19px] align-middle">
                              <input
                                className="w-full h-full text-[11px] px-1.5 bg-[#f8fafc] disabled:bg-[#f8fafc] font-medium text-black border-none outline-none tabular-nums"
                                value={values.Revised[field.key] ?? "-"}
                                disabled
                                type="text"
                              />
                            </td>

                            <td
                              className={`border-r border-b border-[#cbd5e1] p-0 h-[19px] align-middle ${field.key !== "laytime"
                                ? "bg-white"
                                : "bg-[#ffffd6]"
                                }`}
                            >
                              <input
                                className={`w-full h-full text-[11px] px-1.5 tabular-nums font-medium text-black border-none outline-none bg-transparent
                                  ${field.key === "laytime"
                                    ? "focus:ring-1 focus:ring-inset focus:ring-amber-500 focus:bg-white cursor-text"
                                    : "cursor-not-allowed text-black"
                                  }`}
                                value={values.Actual[field.key] || ""}
                                onChange={handleChange("Actual", field.key)}
                                disabled={field.key !== "laytime"}
                                type="number"
                              />
                            </td>

                            <td className="bg-white border-b border-[#cbd5e1] p-0 h-[19px] align-middle">
                              <button
                                className={`w-full h-full text-left text-[11px] px-1.5 font-medium flex items-center bg-transparent border-none outline-none
                                  ${values?.Settled[field.key]
                                    ?.totalSettledAmt > 0
                                    ? "text-black font-medium underline"
                                    : "text-black cursor-default"
                                  }`}
                                onClick={() =>
                                  setSelectedSettledAmt(
                                    values?.Settled[field.key],
                                  )
                                }
                                disabled={
                                  values?.Settled[field.key]
                                    ?.totalSettledAmt === 0
                                }
                              >
                                {values?.Settled[field.key]?.totalSettledAmt
                                  ? Number(
                                    Number(
                                      values?.Settled[field.key]
                                        ?.totalSettledAmt,
                                    ).toFixed(3),
                                  )
                                  : 0}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div
                    ref={vesselCargoContainerRef}
                    className="w-fit h-full max-h-full overflow-y-auto overflow-x-hidden border border-[#cbd5e1] rounded-none scroll-thin bg-white"
                  >
                    <div className="w-full">
                      <table className="w-full border-collapse text-[11px]">
                        <tbody>
                          <tr>
                            <td
                              colSpan={2}
                              className="bg-[#313C4C] text-white px-2 py-1 text-[11px] text-left font-semibold border-b border-[#1e293b] h-[24px] align-middle"
                            >
                              <span className="font-semibold text-white">
                                Vessel Information
                              </span>
                            </td>
                          </tr>

                          {expenseVesselFields.map((item) => {
                            const getFieldValue = (key) =>
                              vesselCargoDetailsTableData[key] || "";

                            return (
                              <tr
                                key={item.key}
                                className="h-[19px] hover:bg-amber-50/60 transition-colors"
                              >
                                <td className="w-[1%] whitespace-nowrap px-2 py-0 bg-white text-black font-medium border-r border-b border-[#cbd5e1] text-[11px] align-middle h-[19px]">
                                  {item.label}
                                </td>

                                <td
                                  className={`p-0 border-b border-[#cbd5e1] min-w-[100px] h-[19px] align-middle ${item.disabled ? "bg-white" : "bg-[#ffffd6]"}`}
                                >
                                  <input
                                    type={item.type}
                                    disabled={item.disabled}
                                    className="w-full h-full text-[11px] border-none outline-none px-1.5 py-0 font-medium bg-transparent text-black disabled:text-black disabled:cursor-not-allowed focus:ring-1 focus:ring-inset focus:ring-amber-500"
                                    value={
                                      item.key === "contractType"
                                        ? "vessel-vessel"
                                        : getFieldValue(item.key)
                                    }
                                    onChange={(e) =>
                                      handleVesselCargoDetailsTableInputChange(
                                        e,
                                        item.key,
                                      )
                                    }
                                  />
                                </td>
                              </tr>
                            );
                          })}

                          <tr>
                            <td
                              colSpan={2}
                              className="bg-[#313C4C] text-white px-2 py-1 text-[11px] text-left font-semibold border-t border-b border-[#1e293b] h-[24px] align-middle"
                            >
                              <span className="font-semibold text-white">
                                Shipment Information
                              </span>
                            </td>
                          </tr>

                          {shipmentsFileds.map((item) => {
                            const getFieldValue = (key) =>
                              vesselCargoDetailsTableData[key] || "";

                            return (
                              <tr
                                key={item.key}
                                className="h-[19px] hover:bg-amber-50/60 transition-colors"
                              >
                                <td className="w-[1%] whitespace-nowrap px-2 py-0 bg-white text-black font-medium border-r border-b border-[#cbd5e1] text-[11px] align-middle h-[19px]">
                                  {item.label}
                                </td>

                                <td
                                  className={`p-0 border-b border-[#cbd5e1] min-w-[100px] h-[19px] align-middle ${item.disabled ? "bg-white" : "bg-[#ffffd6]"}`}
                                >
                                  <input
                                    type="text"
                                    disabled={item.disabled}
                                    className="w-full h-full text-[11px] border-none outline-none px-1.5 py-0 font-medium bg-transparent text-black disabled:text-black disabled:cursor-not-allowed focus:ring-1 focus:ring-inset focus:ring-amber-500"
                                    value={
                                      item.key === "opsId"
                                        ? uniqIds["opsId"]
                                        : typeof getFieldValue(item.key) ===
                                          "number"
                                          ? getFieldValue(item.key)?.toFixed(3)
                                          : getFieldValue(item.key)
                                    }
                                    onChange={(e) =>
                                      handleVesselCargoDetailsTableInputChange(
                                        e,
                                        item.key,
                                      )
                                    }
                                    readOnly={item.disabled}
                                  />
                                </td>
                              </tr>
                            );
                          })}

                          <tr>
                            <td
                              colSpan={2}
                              className="bg-[#313C4C] text-white px-2 py-1 text-[11px] text-left font-semibold border-t border-b border-[#1e293b] h-[24px] align-middle"
                            >
                              <span className="font-semibold text-white">
                                Revenue Information
                              </span>
                            </td>
                          </tr>

                          {revenueCargoFields.map((item) => {
                            if (item.key === "userRef") {
                              return vesselCargoDetailsTableData.userRef.map(
                                (refItem, index) => (
                                  <tr
                                    key={refItem.id}
                                    className="h-[22px] hover:bg-amber-50/60 transition-colors"
                                  >
                                    <td className="w-[1%] whitespace-nowrap px-2 py-0 bg-white text-black font-medium border-r border-b border-[#cbd5e1] text-[11px] align-middle h-[22px]">
                                      {refItem.label || `User Ref-${index + 1}`}
                                    </td>
                                    <td
                                      className={`p-0 border-b border-[#cbd5e1] min-w-[100px] h-[22px] align-middle ${item.disabled ? "bg-white" : "bg-[#ffffd6]"}`}
                                    >
                                      <div className="flex gap-1 items-center pr-1 h-full">
                                        <input
                                          type={item.type}
                                          disabled={item.disabled}
                                          className="w-full h-full text-[11px] border-none outline-none px-1.5 py-0 font-medium bg-transparent text-black disabled:text-black disabled:cursor-not-allowed focus:ring-1 focus:ring-inset focus:ring-amber-500"
                                          value={refItem.value || ""}
                                          onChange={(e) =>
                                            handleUserRefChange(
                                              index,
                                              e.target.value,
                                            )
                                          }
                                        />

                                        <button
                                          type="button"
                                          className="text-emerald-700 bg-emerald-50 hover:bg-emerald-600 hover:text-white border border-emerald-400 hover:border-emerald-600 w-[18px] h-[18px] rounded flex items-center justify-center transition-colors active:scale-95 flex-shrink-0 cursor-pointer"
                                          onClick={() =>
                                            handleAddUserRef(index)
                                          }
                                          title="Add User Ref"
                                        >
                                          <svg
                                            className="w-3 h-3"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                          >
                                            <line
                                              x1="12"
                                              y1="5"
                                              x2="12"
                                              y2="19"
                                            />
                                            <line
                                              x1="5"
                                              y1="12"
                                              x2="19"
                                              y2="12"
                                            />
                                          </svg>
                                        </button>

                                        <button
                                          type="button"
                                          className={`${vesselCargoDetailsTableData.userRef
                                            .length === 1
                                            ? "bg-gray-100 text-gray-300 border-gray-200 cursor-not-allowed"
                                            : "text-rose-600 bg-rose-50 hover:bg-rose-600 hover:text-white border-rose-300 hover:border-rose-600 active:scale-95 cursor-pointer"
                                            } border w-[18px] h-[18px] rounded flex items-center justify-center transition-all flex-shrink-0`}
                                          onClick={() =>
                                            handleRemoveUserRef(index)
                                          }
                                          disabled={
                                            vesselCargoDetailsTableData.userRef
                                              .length === 1
                                          }
                                          title="Remove User Ref"
                                        >
                                          <svg
                                            className="w-3 h-3"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                          >
                                            <line
                                              x1="5"
                                              y1="12"
                                              x2="19"
                                              y2="12"
                                            />
                                          </svg>
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                ),
                              );
                            }

                            const getFieldValue = (key) =>
                              vesselCargoDetailsTableData[key] || "";

                            return (
                              <tr
                                key={item.key}
                                className="h-[22px] hover:bg-amber-50/60 transition-colors"
                              >
                                <td className="w-[1%] whitespace-nowrap px-2 py-0 bg-white text-black font-medium border-r border-b border-[#cbd5e1] text-[11px] align-middle h-[22px]">
                                  {item.label}
                                </td>

                                <td
                                  className={`p-0 border-b border-[#cbd5e1] min-w-[100px] h-[22px] align-middle ${item.disabled ? "bg-white" : "bg-[#ffffd6]"}`}
                                >
                                  <input
                                    type="text"
                                    disabled={item.disabled}
                                    className="w-full h-full text-[11px] border-none outline-none px-1.5 py-0 font-medium bg-transparent text-black disabled:text-black disabled:cursor-not-allowed focus:ring-1 focus:ring-inset focus:ring-amber-500"
                                    value={getFieldValue(item.key)}
                                    onChange={(e) =>
                                      handleVesselCargoDetailsTableInputChange(
                                        e,
                                        item.key,
                                      )
                                    }
                                  />
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
                <div className="w-[13%] min-w-fit flex flex-col gap-1 mt-0 h-full overflow-y-auto scroll-thin">
                  <div className="flex flex-col h-fit my-auto w-full bg-slate-50/50 rounded-lg border border-gray-200 p-1.5 shadow-xs">
                    <div className="flex flex-col gap-1">
                      {[
                        {
                          label: "View Vessel out Contract",
                          key: "vesselContractOut",
                        },
                        {
                          label: "View Vessel in Contract",
                          key: "vesselContractIn",
                        },
                        {
                          label: "Laytime Settlements",
                          key: "laytimeSettlements",
                        },
                        { label: "View Invoices", key: "invoices" },
                        { label: "Sustainability", key: "sustainability" },
                        { label: "Optimum Speed", key: "optimumSpeed" },
                        {
                          label: "Fuel Supply Request",
                          key: "fuelSupplyRequest",
                        },
                        {
                          label: "Fuel Expenses Reconciliation",
                          key: "fuelExpensesReconciliation",
                        },
                      ].map(({ label, key }) => (
                        <button
                          key={key}
                          onClick={() => handleButtonClick(key)}
                          className="
            group relative w-full flex items-center justify-between
            px-2 py-1 h-[24px]
            bg-white hover:bg-sky-50/80
            border border-slate-200 hover:border-[#2a3990]/50
            rounded
            shadow-2xs
            transition-all duration-150 ease-out
            active:scale-[0.98]
            cursor-pointer
          "
                        >
                          {/* Label */}
                          <span className="text-[11px] font-medium text-slate-700 group-hover:text-[#2a3990] transition-colors duration-150 text-left truncate pr-1">
                            {label}
                          </span>

                          {/* Right Icon */}
                          <svg
                            className="w-3 h-3 text-slate-400 group-hover:text-[#2a3990] transition-all duration-150 transform group-hover:translate-x-0.5 shrink-0"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth="2.5"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M9 5l7 7-7 7"
                            />
                          </svg>

                          {/* Left Active Indicator */}
                          <div className="absolute left-0 top-1/2 -translate-y-1/2 h-3 w-[2.5px] bg-[#2a3990] rounded-r-full opacity-0 group-hover:opacity-100 transition-opacity duration-150"></div>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="w-full max-w-full min-w-0 mt-1 shrink-0 overflow-hidden max-h-[260px] flex flex-col">
            <div className="flex items-end border-b border-[#cbd5e1] bg-transparent px-4 pl-16 gap-1.5 overflow-x-auto overflow-y-hidden max-w-full min-w-0 scroll-thin">
              {[
                "Cargo Operations",
                "Vessel Out Ops",
                "Itinerary & Fuel Supply",
                "Pricing",
                "Sea Table",
                "Insights",
              ].map((tabLabel, tabIndex) => {
                const isActive = activeTab === tabIndex;
                return (
                  <button
                    key={tabIndex}
                    type="button"
                    onClick={() => handleTabChange(tabIndex)}
                    disabled={isAnimating}
                    className={`px-3 py-1.5 text-[11px] transition-all duration-100 rounded-none cursor-pointer relative -mb-[1px] ${isActive
                      ? "bg-white text-[#103f6b] font-semibold border-t-2 border-t-[#103f6b] border-l border-r border-b-0 border-l-[#cbd5e1] border-r-[#cbd5e1] z-10"
                      : "bg-[#f1f5f9] text-slate-600 font-medium border border-[#cbd5e1] hover:bg-[#e2e8f0] hover:text-slate-900"
                      }`}
                  >
                    {tabLabel}
                  </button>
                );
              })}
            </div>

            <div
              key={`tab-${activeTab}`}
              className="bg-white border border-t-0 border-[#cbd5e1] rounded-none p-0 shadow-none overflow-hidden w-full max-w-full min-w-0"
            >
              <div className="bg-white rounded-none overflow-hidden w-full max-w-full min-w-0">
                {activeTab === 0 ? (
                  <>
                    <div
                      key={`tab-${activeTab}`}
                      className="w-full max-w-full min-w-0 overflow-x-auto"
                      style={{
                        animation: isAnimating
                          ? `slideInLeft 200ms ease-in-out forwards`
                          : "",
                      }}
                    >
                      <CargoOperationTable
                        isReadOnly={isReadOnly}
                        handleDateChange={
                          handleDateChangeForCargoOperationTable
                        }
                        columns={columns}
                        handleInputChanges={
                          handleInputChangeForCargoOperationTable
                        }
                        data={cargoOperationTableData?.cargoList}
                        onAddRow={handleAddRow}
                        onPortChange={handlePortChange}
                        onDeleteRow={handleDeleteRow}
                        onButtonClick={handleCargoOperationSubModels}
                        freightMode={freightMode}
                      />
                    </div>
                  </>
                ) : activeTab === 2 ? (
                  <div
                    key={`tab-${activeTab}`}
                    className="w-full max-w-full min-w-0 overflow-x-auto"
                    style={{
                      animation: isAnimating
                        ? `slideInRight 200ms ease-in-out forwards`
                        : "",
                    }}
                  >
                    <FualSupplyTable
                      lockFirstRow
                      isReadOnly={isReadOnly}
                      handleDateChange={handleDateChangeForFualSupplyTable}
                      handleInputChanges={
                        handleInputChangeForCargoOperationTable
                      }
                      data={cargoOperationTableData}
                      onAddRow={handleAddRow}
                      onDeleteRow={handleDeleteRow}
                      onPortChange={handlePortChange}
                    />
                  </div>
                ) : activeTab === 3 ? (
                  <div
                    key={`tab-${activeTab}`}
                    className="w-full max-w-full min-w-0 overflow-x-auto"
                    style={{
                      animation: isAnimating
                        ? `slideInRight 200ms ease-in-out forwards`
                        : "",
                    }}
                  >
                    <PricingTable
                      isReadOnly={isReadOnly}
                      columns={pricingColumns}
                      handleInputChanges={
                        handleInputChangeForCargoOperationTable
                      }
                      handleDateChange={handleDateChangeForCargoOperationTable}
                      data={cargoOperationTableData}
                      onAddRow={handleAddRow}
                      onDeleteRow={handleDeleteRow}
                      onPortChange={handlePortChange}
                    />
                  </div>
                ) : activeTab === 4 ? (
                  <div
                    key={`tab-${activeTab}`}
                    className="w-full max-w-full min-w-0 overflow-x-auto"
                    style={{
                      animation: isAnimating
                        ? `slideInRight 200ms ease-in-out forwards`
                        : "",
                    }}
                  >
                    <SeaTable
                      isReadOnly={isReadOnly}
                      columns={seaTableColumns}
                      handleInputChanges={
                        handleInputChangeForCargoOperationTable
                      }
                      handleDateChange={handleDateChangeForCargoOperationTable}
                      data={cargoOperationTableData}
                      onAddRow={handleAddRow}
                      onDeleteRow={handleDeleteRow}
                      onPortChange={handlePortChange}
                    />
                  </div>
                ) : activeTab === 1 ? (
                  <div
                    key={`tab-${activeTab}`}
                    className="w-full max-w-full min-w-0 overflow-x-auto"
                    style={{
                      animation: isAnimating
                        ? `slideInRight 200ms ease-in-out forwards`
                        : "",
                    }}
                  >
                    {/* read-only: Excel's "Dummy for Convenience" block, values come from the main operation table */}
                    <table className="w-full min-w-full border-collapse text-[11px]">
                      <thead>
                        <tr className="bg-[#313C4C] text-white h-[24px]">
                          {[
                            "Operation",
                            "Port",
                            "Departure Time (Z)",
                            "Main Fuel",
                            "ECA Fuel",
                            "MGO Fuel",
                          ].map((h) => (
                            <th
                              key={h}
                              className="px-1.5 py-0.5 font-medium text-left whitespace-nowrap border-r border-b border-[#5b6779]"
                            >
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {contractCargoListOut().map((row, i) => {
                          // Delivery-out / Redelivery-in fuel comes from Vessel Out > Vessel Details > Bunker Qty
                          const vo =
                            vesselContractDetails?.vesselOutContract
                              ?.vesselDetails || {};
                          const op = String(row.operation).toLowerCase();
                          const fuel =
                            i === 0 || op === "delivery-out"
                              ? [vo.bnkDelhs, vo.bnkDells, vo.bnkDelmgo]
                              : op === "redelivery-in"
                                ? [
                                  vo.bnkQtyRedeliveryhs,
                                  vo.bnkQtyRedeliveryls,
                                  vo.bnkQtyRedeliverymgo,
                                ]
                                : [
                                  row.departureHsfo,
                                  row.departureLsfo,
                                  row.departureMgo,
                                ];
                          return (
                          <tr key={row._id || i}>
                            {[
                              row.operation,
                              row.port,
                              formatToLT(row.departureTimeZ) || "",
                              ...fuel,
                            ].map((v, c) => (
                              <td
                                key={c}
                                className="px-1.5 h-[19px] border-r border-b border-[#cbd5e1] bg-[#f1f5f9] whitespace-nowrap"
                              >
                                {v ?? ""}
                              </td>
                            ))}
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div
                    key={`tab-${activeTab}`}
                    className="w-full max-w-full min-w-0 overflow-x-auto"
                    style={{
                      animation: isAnimating
                        ? `slideInRight 200ms ease-in-out forwards`
                        : "",
                    }}
                  >
                    <InsightsTable
                      isReadOnly={isReadOnly}
                      columns={insightsColumns}
                      data={{
                        ...calculatedExpenses,
                        cargoList: [...cargoOperationTableData?.cargoList],
                      }}
                      onAddRow={handleAddRow}
                      onDeleteRow={handleDeleteRow}
                      onPortChange={handlePortChange}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          <CustomShowModal
            open={isLaytimeSettlementsModelOpen}
            handleClose={() => setIsLaytimeSettlementsModelOpen(false)}
            maxWidth="lg"
            title="Laytime Settlements"
          >
            <LaytimeSettlementModel
              isReadOnly={isReadOnly}
              finalState={finalState}
              calculateStats={calculateStats}
              updateFinalState={updateFinalState}
            />
          </CustomShowModal>

          <CustomShowModal
            open={isLaytimesModelOpen}
            handleClose={() => setIsLaytimesModelOpen(false)}
            maxWidth="lg"
            title="Laytime Statement"
          >
            <LaytimeStatement
              isReadOnly={isReadOnly}
              laytimeStatementDetails={getCurrentLaytimeStatementRowData(
                cargoOperationTableData?.cargoList?.[selectedOperationIndex],
              )}
              selectedOperationIndex={selectedOperationIndex}
              setCargoOperationTableData={setCargoOperationTableData}
              warning={warning}
              setWarning={setWarning}
              currentItemId={
                cargoOperationTableData?.cargoList?.[selectedOperationIndex]
                  ?._id
              }
              onDownloadPdf={handleDownloadPdf}
            />
          </CustomShowModal>

          <CustomShowModal
            open={isStatementOfFactsModelOpen}
            handleClose={() => setIsStatementOfFactsModelOpen(false)}
            maxWidth="lg"
            title="Statement of Facts"
          >
            <StatementOfFactsModel
              isReadOnly={isReadOnly}
              state={getCurrentStatementOfFactsRowData(
                cargoOperationTableData?.cargoList?.[selectedOperationIndex],
              )}
              selectedOperationIndex={selectedOperationIndex}
              setCargoOperationTableData={setCargoOperationTableData}
              onDownloadPdf={handleDownloadPdf}
            />
          </CustomShowModal>

          <CustomShowModal
            open={isFinalInvoiceModelOpen}
            handleClose={() => setIsFinalInvoiceModelOpen(false)}
            maxWidth="lg"
            title="Final Invoice"
          >
            <FinalInvoiceModel
              opsId={id}
              isReadOnly={isReadOnly}
              finalInvoiceDetails={[
                ...cargoInvoices,
                ...expenseInvoicesIn,
                ...expenseInvoicesOut,
              ]}
              refetch={() => fetchOperation()}
            />
          </CustomShowModal>

          <CustomShowModal
            open={isFuelSupplyRequestModelOpen}
            handleClose={() => setIsFuelSupplyRequestModelOpen(false)}
            maxWidth="lg"
            title="Fuel Supply Request"
          >
            <FuelSupplyReqModel
              uniqIds={uniqIds}
              isReadOnly={isReadOnly}
              data={cargoOperationTableData}
              vesselContractDetails={vesselContractDetails}
              refetch={() => fetchOperation()}
              operationId={id}
            />
          </CustomShowModal>

          <CustomShowModal
            open={isIssueInvoiceModelOpen}
            handleClose={() => setIsIssueInvoiceModelOpen(false)}
            maxWidth="none"
            title={
              cargoOperationTableData?.cargoList?.[selectedCargoIndex]?.port
                ? `Issue Invoice — ${cargoOperationTableData?.cargoList?.[selectedCargoIndex]?.port}`
                : "Issue Invoice"
            }
          >
            <IssueInvoiceModel
              opsId={id}
              isReadOnly={isReadOnly}
              setInitialRows={handleSetRows}
              initialRows={
                cargoOperationTableData?.cargoList?.[selectedCargoIndex]
                  ?.invoiceDetails || []
              }
              cargoDetails={cargoOperationTableData}
              selectedPort={
                cargoOperationTableData?.cargoList?.[selectedCargoIndex]
              }
            />
          </CustomShowModal>

          <CustomAddModal
            open={openExpensesInvoiceModel.isOpen}
            handleClose={() => setOpenExpensesInvoiceModel({ isOpen: false })}
            onSubmitConfirm={() =>
              setOpenExpensesInvoiceModel({ isOpen: false })
            }
            saveLabel="Save"
            maxWidth="lg"
            title={
              openExpensesInvoiceModel.vessel === "In"
                ? "Revenue Invoice"
                : "Expenses Invoice"
            }
          >
            <ExpensesInvoiceModel
              isReadOnly={isReadOnly}
              isVesselVesselOps
              invoiceType={
                openExpensesInvoiceModel.vessel === "In" ? "Revenue" : "Expense"
              }
              vesselType={
                openExpensesInvoiceModel.vessel === "In"
                  ? "vesselIn"
                  : "vesselOut"
              }
              expensesInvoiceDetails={
                openExpensesInvoiceModel.vessel === "In"
                  ? values.Actual
                  : values.ActualOut
              }
              vesselDetails={
                openExpensesInvoiceModel.vessel === "In"
                  ? vesselContractDetails
                  : {
                    ...vesselContractDetails?.vesselOutContract,
                    vesselContractId: vesselContractDetails?.vesselContractId,
                  }
              }
              usedCounterparties={
                openExpensesInvoiceModel.vessel === "In"
                  ? usedCounterpartiesIn
                  : usedCounterpartiesOut
              }
              setUsedCounterparties={
                openExpensesInvoiceModel.vessel === "In"
                  ? setUsedCounterpartiesIn
                  : setUsedCounterpartiesOut
              }
              expenseAllocations={
                openExpensesInvoiceModel.vessel === "In"
                  ? expenseInvoicesIn
                  : expenseInvoicesOut
              }
              setExpenseAllocations={
                openExpensesInvoiceModel.vessel === "In"
                  ? setExpenseInvoicesIn
                  : setExpenseInvoicesOut
              }
            />
          </CustomAddModal>

          <CustomShowModal
            open={openAddOperation.isOpen}
            handleClose={() =>
              setOpenAddOperation({
                isOpen: false,
                index: null,
              })
            }
            maxWidth="xs"
            title="Add Operation"
          >
            <AddOperationsModal
              delayPortsSearch={delayPortSearch}
              portOptions={portOptions}
              portLoading={portLoading}
              initialAddOperation={initialAddOperation}
              setInitialAddOperation={setInitialAddOperation}
              onAddOperationByModal={onAddOperationByModal}
              isOperationTab
              vesselVesselOperationList={cargoOperationTableData?.cargoList}
            />
          </CustomShowModal>
          {/* <CustomShowModal
            open={splitHireModal}
            handleClose={() => setSplitHireModal(false)}
            maxWidth="xl"
            title="Hire Expense Analyzer"
          >
            <HireExpenseCalculations
              isReadOnly={isReadOnly}
              fuelDeductions={fuelDeductions}
              setFuelDeductions={setFuelDeductions}
              offHireDeductions={offHireDeductions}
              miscDeductions={miscDeductions}
              setOffHireDeductions={setOffHireDeductions}
              setMiscDeductions={setMiscDeductions}
              totalDeductions={totalDeductions}
              totalOffHireDuration={totalOffHireDuration}
              totalMiscDeductions={totalMiscDeductions}
              totalFuelDeductions={totalFuelDeductions}
              vesselData={vesselContractDetails?.vesselDetails}
            />
          </CustomShowModal> */}
          <CustomShowModal
            open={editCargoContractModelOpen}
            title={"Cargo Details"}
            maxWidth="md"
            saveLabel="Update"
            handleClose={() => setEditCargoContractModelOpen(false)}
          >
            <div className="flex justify-between items-center mb-4">
              <CustomTabs
                tabs={cargoDetailsViewTabs}
                active={activeCargoModelTab}
                setActive={setActiveCargoModelTab}
              />
              <button
                type="button"
                onClick={() => {
                  if (activeCargoModelTab === 1) {
                    addQueryModalRef.current?.onSubmitQuery();
                  } else {
                    handleUpdateCargoContract();
                  }
                }}
                className="h-[26px] px-3.5 text-[11px] font-medium text-white bg-[#313C4C] hover:bg-[#1e2632] border border-[#313C4C] rounded shadow-2xs transition-all duration-150 active:scale-95 cursor-pointer whitespace-nowrap"
              >
                Update
              </button>
            </div>

            {activeCargoModelTab === 1 && (
              <CargoViewModel
                ref={addQueryModalRef}
                setLoader={setLoader}
                cargoAnalysisData={cargoOperationTableData}
                setSelectedPortsCommence={setSelectedPortsCommence}
                selectedQuery={{
                  ...cargoOperationTableData,
                  cargoContractId:
                    cargoContractDetails?.cargoContractId ||
                    cargoContractDetails?._id ||
                    id ||
                    cargoOperationTableData?.cargoContractId,
                  _id:
                    cargoContractDetails?.cargoContractId ||
                    cargoContractDetails?._id ||
                    id ||
                    cargoOperationTableData?._id,
                  contractId:
                    cargoContractDetails?.cargoContractId ||
                    cargoContractDetails?._id ||
                    id ||
                    cargoOperationTableData?.contractId,
                }}
                cargoContractId={
                  cargoContractDetails?.cargoContractId ||
                  cargoContractDetails?._id ||
                  id
                }
                selectedPorts={selectedPorts}
                refetch={() => fetchOperation()}
              />
            )}

            {activeCargoModelTab === 0 && (
              <CreateCargoContractForm
                isReadOnly={isReadOnly}
                formData={cargoContractDetails}
                setFormData={setCargoContractDetails}
                handleChange={handleCargoContractInputChange}
                removeAttachment={removeCargoAttachment}
                textContract={cargoTextContract}
                handleTextContractUpload={handleCargoTextContractUpload}
                handleAttachmentUpload={handleCargoContractAttachmentUpload}
                attachments={cargoAttachments}
                setTextContract={setCargoTextContract}
                prevAttachments={prevAttachments}
                setPrevAttachments={setPrevAttachments}
              />
            )}
          </CustomShowModal>
          <CustomShowModal
            open={!!selectedSettledAmt}
            handleClose={() => setSelectedSettledAmt(null)}
            title="Settled Amount Details"
            maxWidth="sm"
          >
            {selectedSettledAmt && (
              <div className="space-y-2 py-1">
                {/* Header Info Card */}
                <div className="bg-[#f8fafc] rounded-none p-2 border border-slate-200">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-[11px] text-slate-500 font-medium uppercase tracking-wider mb-0.5">
                        Reference Key
                      </p>
                      <p className="text-[11px] font-medium text-slate-900">
                        {[
                          ...revenueFields,
                          ...expensesFields,
                          ...profitFileds,
                        ].find((f) => f.key === selectedSettledAmt.key)
                          ?.label || selectedSettledAmt.key}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-[11px] text-slate-500 font-medium uppercase tracking-wider mb-0.5">
                        Total Settled
                      </p>
                      <p className="text-[11px] font-medium text-slate-900">
                        {Number(
                          Number(
                            selectedSettledAmt.totalSettledAmt || 0,
                          ).toFixed(3),
                        )}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Compact Table */}
                <div className="border border-slate-200 rounded-none overflow-hidden">
                  <table className="w-full text-[11px] font-medium border-collapse">
                    <thead>
                      <tr className="bg-[#f8fafc] border-b border-slate-200 h-6">
                        <th className="px-2 py-0.5 w-[15%] text-left font-medium text-[11px] text-slate-700 border-r border-slate-200">
                          Amount
                        </th>
                        <th className="px-2 py-0.5 w-[20%] text-left font-medium text-[11px] text-slate-700 border-r border-slate-200">
                          Counter Party
                        </th>
                        <th className="px-2 py-0.5 w-[35%] text-left font-medium text-[11px] text-slate-700 border-r border-slate-200">
                          Remarks
                        </th>
                        <th className="px-2 py-0.5 w-[15%] text-left font-medium text-[11px] text-slate-700 border-r border-slate-200">
                          Received At
                        </th>
                        <th className="px-2 py-0.5 w-[15%] text-left font-medium text-[11px] text-slate-700">
                          Posted At
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {selectedSettledAmt.paymentEntries.map((entry, idx) => (
                        <tr
                          key={idx}
                          className="h-6 hover:bg-slate-50 transition-colors"
                        >
                          <td className="px-2 py-0.5 text-left font-medium text-[11px] text-slate-900 border-r border-slate-200">
                            {entry.amount}
                          </td>
                          <td className="px-2 py-0.5 text-slate-700 font-medium text-[11px] border-r border-slate-200">
                            {entry.counterParty}
                          </td>
                          <td className="px-2 py-0.5 text-slate-700 font-medium text-[11px] border-r border-slate-200">
                            {entry.remarks || "—"}
                          </td>
                          <td className="px-2 py-0.5 text-slate-700 font-medium text-[11px] border-r border-slate-200 whitespace-nowrap">
                            {moment(entry.date).isValid()
                              ? moment(entry.date).format("DD-MM-YYYY")
                              : "-"}
                          </td>
                          <td className="px-2 py-0.5 text-left text-slate-700 font-medium text-[11px] whitespace-nowrap">
                            {moment(entry.postedAt).isValid()
                              ? moment(entry.postedAt).format("DD-MM-YYYY")
                              : "-"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </CustomShowModal>

          <CustomShowModal
            open={editVesselContractModelOpen.isOpen}
            handleClose={() => {
              setEditVesselContractModelOpen({ isOpen: false, key: "" });
              setPrevAttachments({ attachments: [], textContract: undefined });
              setVesselAttachments([]);
              setVesselTextContract(undefined);
            }}
            title={`Vessel Contract ${isContractIn ? "In" : "Out"}`}
            maxWidth="lg"
          >
            <div className="flex justify-between mb-3">
              <CustomTabs
                tabs={vesselDetailsViewTabs}
                active={vesselActiveTab}
                setActive={setVesselActiveTab}
              />
              {vesselActiveTab !== 2 && (
                <button
                  type="button"
                  onClick={async () => {
                    if (vesselActiveTab === 1) {
                      handleUpdateVesselDetails();
                    } else {
                      handleUpdateVesselContract(
                        editVesselContractModelOpen.key,
                      );
                    }
                  }}
                  className="h-[26px] px-3.5 text-[11px] font-medium text-white bg-[#313C4C] hover:bg-[#1e2632] border border-[#313C4C] rounded shadow-2xs transition-all duration-150 active:scale-95 cursor-pointer whitespace-nowrap"
                >
                  Update
                </button>
              )}
            </div>

            {vesselActiveTab === 0 && (
              <VesselContractForm
                isReadOnly={isReadOnly}
                formData={getCurrentVesselContract(
                  editVesselContractModelOpen.key,
                  vesselContractDetails,
                )}
                setFormData={handleOnChangeVesselContract}
                handleChange={handleVesselContractInputChange}
                attachments={vesselAttachments}
                textContract={vesselTextContract}
                isEditing
                isVesselVesselOps={true}
                removeAttachment={removeVesselAttachment}
                handleTextContractUpload={handleVesselTextContractUpload}
                handleAttachmentUpload={handleVesselContractAttachmentUpload}
                setTextContract={setVesselTextContract}
                prevAttachments={prevAttachments}
                setPrevAttachments={setPrevAttachments}
              />
            )}

            {vesselActiveTab === 1 && (
              <VesselViewModel
                ref={vesselRef}
                currentContract={currentVesselContractDetails}
                extraDetails={
                  isContractIn
                    ? subModelData.expensesIn
                    : subModelData.expensesOut
                }
                setExtraDetails={(newDetails) => {
                  setSubModelData((prevData) => ({
                    ...prevData,
                    [isContractIn ? "expensesIn" : "expensesOut"]: newDetails,
                  }));
                }}
                setCurrentContract={setVesselContractDetails}
                handleClose={() => setEditVesselContractModelOpen(false)}
                refetch={() => fetchOperation()}
                isFromOperation
                type={editVesselContractModelOpen.key}
                showCommissionFields
                onSaveOverride={async (updatedVal) => {
                  const saved = await handleSubmit(undefined, updatedVal);
                  if (saved)
                    setEditVesselContractModelOpen({ isOpen: false, key: "" });
                }}
                isViewOnly={isReadOnly}
              />
            )}

            {vesselActiveTab === 2 && (
              <HireExpenseCalculations
                showOffHireAmounts
                extraDetails={
                  isContractIn
                    ? subModelData.expensesIn
                    : subModelData.expensesOut
                }
                setExtraDetails={(newDetails) => {
                  setSubModelData((prevData) => ({
                    ...prevData,
                    [isContractIn ? "expensesIn" : "expensesOut"]: newDetails,
                  }));
                }}
                isReadOnly={isReadOnly}
                {...getOffHireProps()}
              />
            )}
          </CustomShowModal>
          <CustomShowModal
            open={openFuelExpenseModel}
            handleClose={() => setOpenFuelExpenseModel(false)}
            title="View Fuel Expense"
            maxWidth="md"
          >
            <VesselFuelExpenseTable
              isReadOnly={isReadOnly}
              fuelSettlementMode={fuelSettlementMode}
              fuelExpenseData={subModelData.fuelExpenseData}
              setfuelExpenseData={setSubModelData}
            />
          </CustomShowModal>
          <CustomShowModal
            open={openSustainabilityModel}
            handleClose={() => setOpenSustainabilityModel(false)}
            title="Add Sustainability"
            maxWidth="lg"
          >
            <SustainabilityModel
              formData={sustainabilityData}
              setFormData={setSustainabilityData}
              cargoDetails={cargoOperationTableData}
              calculatedExpenses={calculatedExpenses}
            />
          </CustomShowModal>
          <CustomShowModal
            open={optimumSpeedModelOpen}
            handleClose={() => setOptimumSpeedModelOpen(false)}
            title="View Optimum Speeds"
            maxWidth="sm"
          >
            <div className="rounded-none border border-[#cbd5e1] overflow-hidden my-1">
              <div className="overflow-x-auto scroll-thin w-full">
                <table className="w-full border-collapse text-[11px] font-medium">
                  <thead className="bg-[#e2e8f0] text-[#1e293b] h-[24px]">
                    <tr>
                      <Tooltip title="Ballast or laden" placement="top" arrow>
                        <th className="px-2.5 py-1 text-center font-medium capitalize tracking-wider text-[11px] text-[#1e293b] border-r border-b border-[#cbd5e1]">
                          B/L
                        </th>
                      </Tooltip>
                      <Tooltip title="Operation" placement="top" arrow>
                        <th className="px-2.5 py-1 text-left font-medium capitalize tracking-wider text-[11px] text-[#1e293b] border-r border-b border-[#cbd5e1]">
                          Operation
                        </th>
                      </Tooltip>
                      <Tooltip title="Port" placement="top" arrow>
                        <th className="px-2.5 py-1 text-left font-medium capitalize tracking-wider text-[11px] text-[#1e293b] border-r border-b border-[#cbd5e1]">
                          Port
                        </th>
                      </Tooltip>
                      <Tooltip title="Speed (Normal)" placement="top" arrow>
                        <th className="px-2.5 py-1 text-left font-medium capitalize tracking-wider text-[11px] text-[#1e293b] border-r border-b border-[#cbd5e1]">
                          Speed (Normal)
                        </th>
                      </Tooltip>
                      <Tooltip title="Speed (ECA)" placement="top" arrow>
                        <th className="px-2.5 py-1 text-left font-medium capitalize tracking-wider text-[11px] text-[#1e293b] border-b border-[#cbd5e1]">
                          Speed (ECA)
                        </th>
                      </Tooltip>
                    </tr>
                  </thead>
                  <tbody className="bg-white">
                    {cargoOperationTableData?.cargoList?.length > 0 ? (
                      cargoOperationTableData.cargoList.map((item, i) => (
                        <tr
                          key={item?.id || i}
                          className="h-[20px] hover:bg-amber-50/50 transition-colors"
                        >
                          <td
                            className={`px-2.5 py-0.5 whitespace-nowrap border-r border-b border-[#cbd5e1] align-middle text-center text-[11px] font-medium ${item?.lbmarker?.toLowerCase() === "laden"
                              ? "bg-emerald-50 text-emerald-800"
                              : "bg-amber-50 text-amber-800"
                              }`}
                          >
                            {item?.lbmarker?.toLowerCase() === "laden"
                              ? "Laden"
                              : "Ballast"}
                          </td>
                          <td className="px-2.5 py-0.5 text-slate-800 font-medium text-[11px] border-r border-b border-[#cbd5e1] align-middle">
                            {item?.operation || "-"}
                          </td>
                          <td className="px-2.5 py-0.5 text-slate-800 font-medium text-[11px] border-r border-b border-[#cbd5e1] align-middle">
                            {item?.port || "-"}
                          </td>
                          <td className="px-2.5 py-0.5 text-slate-900 font-medium text-[11px] border-r border-b border-[#cbd5e1] bg-[#f0f4f9] align-middle">
                            {item?.speedNormal || "-"}
                          </td>
                          <td className="px-2.5 py-0.5 text-slate-900 font-medium text-[11px] border-b border-[#cbd5e1] bg-[#f0f4f9] align-middle">
                            {item?.speedECA || "-"}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan="5"
                          className="px-2.5 py-4 text-center text-slate-400 italic text-[11px] font-medium border-b border-[#cbd5e1]"
                        >
                          No optimum speeds data available
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </CustomShowModal>
          <AdditionalRevenueModel
            open={Boolean(openAdditionalRevenueModel)}
            onClose={() => setOpenAdditionalRevenueModel(null)}
            isReadOnly={isReadOnly}
            titleSuffix={
              openAdditionalRevenueModel === "expenses"
                ? "Vessel Out"
                : "Vessel In"
            }
            items={
              subModelData[revenueKeyForSection(openAdditionalRevenueModel)]
                ?.misc || []
            }
            onAdd={() => handleAddMiscRevenue(openAdditionalRevenueModel)}
            onRemove={(item, index) =>
              handleRemoveMiscRevenue(index, openAdditionalRevenueModel)
            }
            onRename={(item, index, value) =>
              handleRenameMiscRevenue(index, value, openAdditionalRevenueModel)
            }
            onUpdateAmount={(item, index, value) =>
              handleUpdateMiscRevenueAmount(
                index,
                value === "" ? 0 : Number(value),
                openAdditionalRevenueModel,
              )
            }
          />

          <OtherExpensesModal
            open={Boolean(openOtherExpModel?.isOpen)}
            onClose={() => setOpenOtherExpModel({ isOpen: false })}
            isReadOnly={isReadOnly}
            titleSuffix={
              openOtherExpModel?.key === "revenue" ? "Vessel In" : "Vessel Out"
            }
            expensesData={activeData || {}}
            onOpenBreakdown={({ label, key, itemsKey }) => {
              const activeProp = expensesKeyForSection(openOtherExpModel.key);
              const existing = subModelData[activeProp]?.[itemsKey];
              if (!existing || !Array.isArray(existing)) {
                const currentVal = Number(subModelData[activeProp]?.[key]) || 0;
                setSubModelData((prev) => ({
                  ...prev,
                  [activeProp]: {
                    ...prev[activeProp],
                    [itemsKey]: [{ label, value: currentVal }],
                  },
                }));
              }
              setExpenseModalField({ label, value: key, itemsKey, activeProp });
            }}
            onAddMisc={() => handleAddMiscExpense(openOtherExpModel.key)}
            onRemoveMisc={(item, index) =>
              handleRemoveMiscExpense(index, openOtherExpModel.key)
            }
            onRenameMisc={(item, index, value) =>
              handleRenameMiscExpense(index, value, openOtherExpModel.key)
            }
            onUpdateMiscAmount={(item, index, value) =>
              handleChangeMiscExpenseAmount(index, value, openOtherExpModel.key)
            }
            totalAmount={
              (openOtherExpModel.key === "revenue"
                ? totalMiscExpenses
                : totalMiscExpensesOut) +
              Number(activeData?.expensesPerDay || 0)
            }
          />
          <div>
            <DateChangeModal
              open={showDateChangeModal}
              onClose={() => setShowDateChangeModal(false)}
              onConfirm={confirmDateChange}
            />
          </div>

          {/* Expense Breakdown Modal (rendered on top of Other Expenses modal) */}
          {expenseModalField && (
            <CustomShowModal
              open={Boolean(expenseModalField)}
              handleClose={() => setExpenseModalField(null)}
              maxWidth="sm"
              title={expenseModalField.label}
            >
              <ExpenseBreakdownModal
                expenseLabel={expenseModalField.label}
                items={
                  subModelData[expenseModalField.activeProp][
                  expenseModalField.itemsKey
                  ] || [
                    {
                      label: expenseModalField.label,
                      value:
                        Number(
                          subModelData[expenseModalField.activeProp][
                          expenseModalField.value
                          ],
                        ) || 0,
                    },
                  ]
                }
                onChange={(updatedItems, sum) => {
                  setSubModelData((prev) => {
                    const newExpenses = {
                      ...prev[expenseModalField.activeProp],
                      [expenseModalField.itemsKey]: updatedItems,
                      [expenseModalField.value]: sum,
                    };
                    if (expenseModalField.itemsKey === "dailyExpenses") {
                      newExpenses.cvePerDay = updatedItems
                        .filter((i) => i.label?.toLowerCase().includes("cve"))
                        .reduce((acc, i) => acc + (Number(i.value) || 0), 0);
                      newExpenses.insurancePerDay = updatedItems
                        .filter((i) =>
                          i.label?.toLowerCase().includes("insurance"),
                        )
                        .reduce((acc, i) => acc + (Number(i.value) || 0), 0);
                    }
                    return {
                      ...prev,
                      [expenseModalField.activeProp]: newExpenses,
                    };
                  });
                }}
              />
            </CustomShowModal>
          )}
          <style jsx>{`
            @keyframes slideInRight {
              from {
                transform: translateX(100%);
              }
              to {
                transform: translateX(0);
              }
            }

            @keyframes slideInLeft {
              from {
                transform: translateX(-100%);
              }
              to {
                transform: translateX(0);
              }
            }

            @keyframes slideOutRight {
              from {
                transform: translateX(0);
              }
              to {
                transform: translateX(100%);
              }
            }

            @keyframes slideOutLeft {
              from {
                transform: translateX(0);
              }
              to {
                transform: translateX(-100%);
              }
            }
          `}</style>
        </div>
      )}
      {(mainTab === 0 || (mainTab === 1 && isRevised)) && (
        <VesselVesselContract
          key={`${mainTab}-${isRevised}`}
          setLoader={setLoader}
          loader={loader}
          vesselId={id}
          tabs={tabs}
          active={mainTab}
          handleMainTabChange={handleMainTabChange}
          isViewOnly={mainTab === 0 && isRevised}
          setIsRevised={setIsRevised}
        />
      )}
      <NextTripCargoSelectionModal
        open={nextTripModalOpen}
        onClose={() => setNextTripModalOpen(false)}
        previousOperationId={id}
        onSuccess={async (createdOp) => {
          const newOpId = createdOp?._id;
          if (newOpId) {
            setNextOperationId(newOpId);
            await fetchOperation();
            window.open(
              createdOp?.contractType === "vessel-vessel"
                ? `/vessel-vessel-contract/${newOpId}`
                : `/saved-contracts/${newOpId}`,
              "_blank",
            );
          }
        }}
      />
      <TripEndWarningModal
        open={tripEndWarningOpen}
        onClose={() => setTripEndWarningOpen(false)}
        mode={tripEndWarningMode}
        reason={tripEndWarningReason}
        onConfirmEndTrip={handleConfirmEndTrip}
      />
    </Grid>
  );
};

export default VesselVesselCalc;
